import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, RotateCcw, Upload, Download, X, Paperclip, MessageSquare, ListChecks, History, Send, Eye, Plus, Trash2, Users, Maximize2, Minimize2, GripVertical, ChevronUp, ChevronDown, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  admin: "Administrator",
  npd_manager: "NPD Manager",
  npd_engineer: "Project Leader",
  md: "MD",
  director: "Director",
};

// Designations available in each task's Responsibility box.
const DESIGNATIONS = ["npd_engineer", "npd_manager", "admin"];

// Development categories — the 8 APQP Matrix (ENGG-F-07) columns A–H.
const DEV_CATEGORIES = [
  ["A", "New Product Development (NPD)"],
  ["B", "Similar Product Development (SPD)"],
  ["C", "ECN / RM Change"],
  ["D", "PCN"],
  ["E", "Proto Parts"],
  ["F", "Plant Location Change / Floor Layout Change"],
  ["G", "Supplier Change"],
  ["H", "Contract Manufacturing / Tool off Load / Tool Life Over"],
];

// Rows in the top Team panel. "user" = pick a system user (maps a designation to a person);
// "owner" = a free-text name (MD / Director aren't app users).
const TEAM_ROWS = [
  { kind: "user", role: "npd_engineer" },
  { kind: "user", role: "npd_manager" },
  { kind: "owner", key: "md", label: "MD" },
  { kind: "owner", key: "director", label: "Director" },
];

const STATUS_META = {
  complete: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  incomplete: { label: "Incomplete", cls: "bg-white text-slate-600 border-slate-300" },
  reject: { label: "Reject", cls: "bg-red-50 text-red-700 border-red-300" },
};

// Standard milestone template (editable; the user can add more below the Download button).
const DEFAULT_MILESTONES = [
  { event: "T0", aampl_date: "", customer_date: "" },
  { event: "T1 Fot", aampl_date: "", customer_date: "" },
  { event: "T2 (C1)", aampl_date: "", customer_date: "" },
  { event: "T3 (C2)", aampl_date: "", customer_date: "" },
  { event: "RUN RATE & PPAP AUDIT", aampl_date: "", customer_date: "" },
  { event: "PPAP SAMPLE SUBMISSION", aampl_date: "", customer_date: "" },
  { event: "PSW SIGN-OFF", aampl_date: "", customer_date: "" },
  { event: "Customer SOP", aampl_date: "", customer_date: "" },
];

// Strip a trailing "sir"/"sr" and prefix "Mr." for a team member's display name.
function mr(name) {
  const n = String(name || "").replace(/\s*\b(sir|sr\.?)\b\.?\s*$/i, "").trim();
  return n ? `Mr. ${n}` : "—";
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  const d = Math.round((new Date(b) - new Date(a)) / 86400000);
  return isNaN(d) ? null : d;
}

// Add N days to an ISO date (YYYY-MM-DD); returns "" if unparseable.
function addDays(dateStr, n) {
  const d = new Date(String(dateStr).slice(0, 10));
  if (isNaN(d)) return "";
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function ApqpProjectDetail() {
  const { apqpId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("apqp.edit");

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [msSaved, setMsSaved] = useState(false);  // milestone "✓ Saved" flash
  const [docsModal, setDocsModal] = useState(false);   // all-documents shortcut popup
  const [allDocs, setAllDocs] = useState([]);
  const [allDocsLoading, setAllDocsLoading] = useState(false);
  const [docsAutoOnly, setDocsAutoOnly] = useState(false);   // modal filter: show only auto-filled docs
  const tasksEditedRef = useRef(false);  // true once the user edits a task (gates milestone auto-persist)
  const [counts, setCounts] = useState({ total: 0, completed: 0, pending: 0, overdue: 0, progress_pct: 0 });
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);   // all roles → Responsibility dropdown options
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [popupTaskId, setPopupTaskId] = useState(null);
  const [excelSynced, setExcelSynced] = useState(false); // true after Save rebuilds the Excel
  const [actualOffsets, setActualOffsets] = useState({}); // {taskId: "+days"} for the Actual auto-fill box

  // Auto-fill Actual dates = Plan dates + N days (still editable afterwards).
  function applyActualOffset(id, val) {
    setActualOffsets((m) => ({ ...m, [id]: val }));
    const n = parseInt(val, 10);
    if (isNaN(n)) return;
    const t = phases.flatMap((p) => p.tasks).find((x) => x.id === id) || {};
    const patch = {};
    if (t.start_date) patch.actual_start_date = addDays(t.start_date, n) || null;
    if (t.completion_date) patch.actual_completion_date = addDays(t.completion_date, n) || null;
    if (Object.keys(patch).length) patchTaskAndSave(id, patch);
  }

  const load = useCallback(async () => {
    const [r, u, rl] = await Promise.all([
      api.get(`/apqp/${apqpId}`),
      api.get("/apqp/users").catch(() => ({ data: [] })),
      api.get("/roles").catch(() => ({ data: [] })),
    ]);
    setProject(r.data.project);
    setPhases(r.data.phases);
    setCounts(r.data.counts);
    setUsers(u.data || []);
    setRoles(Array.isArray(rl.data) ? rl.data : (rl.data?.roles || []));
    setDirty(new Set());
  }, [apqpId]);

  useEffect(() => { load(); }, [load]);

  const allTasks = phases.flatMap((p) => p.tasks);
  const popupTask = allTasks.find((t) => t.id === popupTaskId) || null;

  function patchTask(id, patch) {
    setPhases((prev) => prev.map((ph) => ({
      ...ph,
      tasks: ph.tasks.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...patch };
        if ("start_date" in patch || "completion_date" in patch) {
          next.completion_days = daysBetween(next.start_date, next.completion_date);
        }
        return next;
      }),
    })));
    setDirty((prev) => new Set(prev).add(id));
    setExcelSynced(false);
  }

  // Plan/Actual date edits persist immediately (no need to click Save) so a picked
  // date never reverts on navigation. Optimistically updates the row, then saves just
  // that task; on failure the row stays "dirty" so the manual Save can retry.
  async function patchTaskAndSave(id, patch) {
    patchTask(id, patch);
    tasksEditedRef.current = true;  // enable milestone auto-persist after real task edits
    const cur = phases.flatMap((p) => p.tasks).find((x) => x.id === id) || {};
    const t = { ...cur, ...patch };
    try {
      await api.put(`/apqp/${apqpId}/tasks/${id}`, {
        responsibility_role: t.responsibility_role || null,
        secondary_responsibility_role: t.secondary_responsibility_role || null,
        start_date: t.start_date || null,
        completion_date: t.completion_date || null,
        status: t.status,
        applicability: t.applicability || "Yes",
        actual_start_date: t.actual_start_date || null,
        actual_completion_date: t.actual_completion_date || null,
        remarks: t.remarks ?? null,
      });
      setDirty((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      // leave the row dirty so the sticky Save button can retry it
    }
  }

  // Mark every task Applicable / Not Applicable at once; then override individual rows.
  function setAllApplicability(value) {
    const ids = [];
    setPhases((prev) => prev.map((ph) => ({
      ...ph,
      tasks: ph.tasks.map((t) => { ids.push(t.id); return { ...t, applicability: value }; }),
    })));
    setDirty((prev) => { const n = new Set(prev); ids.forEach((i) => n.add(i)); return n; });
    setExcelSynced(false);
  }

  // Rebuild the Timing-Plan Excel from the current data so the attached file / View
  // reflect edits immediately — no download+upload round-trip. Returns a promise.
  function regenerateExcelSilently() {
    return api.post(`/apqp/${apqpId}/timing-plan/regenerate`).catch(() => {});
  }

  async function saveAll() {
    if (dirty.size === 0) return;
    // Warn (but don't block) when mandatory header fields aren't filled.
    const missing = [];
    if (!project.category) missing.push("Category (2W/4W)");
    if (missing.length) {
      const ok = window.confirm(`These mandatory field(s) are not filled: ${missing.join(", ")}.\n\nSave anyway?`);
      if (!ok) return;
    }
    setSaving(true);
    setExcelSynced(false);
    try {
      for (const id of dirty) {
        const t = phases.flatMap((p) => p.tasks).find((x) => x.id === id);
        if (!t) continue;
        await api.put(`/apqp/${apqpId}/tasks/${id}`, {
          responsibility_role: t.responsibility_role || null,
          secondary_responsibility_role: t.secondary_responsibility_role || null,
          start_date: t.start_date || null,
          completion_date: t.completion_date || null,
          status: t.status,
          applicability: t.applicability || "Yes",
          actual_start_date: t.actual_start_date || null,
          actual_completion_date: t.actual_completion_date || null,
          remarks: t.remarks ?? null,
        });
      }
      await regenerateExcelSilently(); // wait so View/Download are guaranteed current
      setExcelSynced(true);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveCategory(cat) {
    setProject((p) => ({ ...p, category: cat }));
    try {
      await api.put(`/apqp/${apqpId}`, { category: cat });
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // Dev. Category drives per-task Applicability from the APQP Matrix (backend). Reload
  // immediately for every category so the Ap/NA changes show at once; the Timing-Plan
  // Excel is rebuilt in the background (non-blocking).
  async function saveDevCategory(value) {
    setProject((p) => ({ ...p, dev_category: value }));
    try {
      await api.put(`/apqp/${apqpId}`, { dev_category: value });
      await load();                 // always reflect the new Applicable/NA state
      regenerateExcelSilently();    // background — don't block the UI
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // Team = designation(role) → person. The name shows only here (top); task boxes show the designation.
  function teamName(role) {
    const uid = (project.team || {})[role];
    const u = users.find((x) => x.user_id === uid);
    return u ? u.name : "—";
  }
  async function setTeamMember(role, userId) {
    const team = { ...(project.team || {}), [role]: userId || null };
    setProject((p) => ({ ...p, team }));
    try {
      await api.put(`/apqp/${apqpId}`, { team });
      regenerateExcelSilently();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }
  // MD / Director are free-text names (stored in owners; also feed the Excel header).
  function setOwnerLocal(key, value) {
    setProject((p) => ({ ...p, owners: { ...(p.owners || {}), [key]: value } }));
  }
  async function saveOwners() {
    try {
      await api.put(`/apqp/${apqpId}`, { owners: project.owners || {} });
      regenerateExcelSilently();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  async function toggleHold() {
    const next = !project.on_hold;
    const today = new Date().toISOString().slice(0, 10);
    // Stamp the hold date when putting on hold, and the resume date when starting again.
    const patch = next ? { on_hold: true, hold_date: today } : { on_hold: false, resume_date: today };
    setProject((p) => ({ ...p, ...patch }));
    try {
      await api.put(`/apqp/${apqpId}`, patch);
    } catch (e) {
      setProject((p) => ({ ...p, on_hold: !next }));
      alert(e.response?.data?.detail || e.message);
    }
  }

  async function saveProjectField(field, value) {
    setProject((p) => ({ ...p, [field]: value }));
    try {
      await api.put(`/apqp/${apqpId}`, { [field]: value });
      regenerateExcelSilently();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // Won date re-anchors Start Date + shifts the whole plan on the backend — reload to show it.
  async function saveWonDate(value) {
    setProject((p) => ({ ...p, won_date: value }));
    try {
      await api.put(`/apqp/${apqpId}`, { won_date: value });
      await regenerateExcelSilently();
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // Milestones — compact list. Dates auto-fill from the matching plan task (like earlier);
  // users can still Add / remove events.
  const milestones = (project?.milestones && project.milestones.length) ? project.milestones : DEFAULT_MILESTONES;
  // Bake each milestone's live AAMPL date (auto-derived from its plan task) + the effective
  // Customer date into the stored record, so a Save persists exactly what's on screen and the
  // Excel export carries real dates (not blanks). Task edits flow in via deriveMilestoneDate.
  function bakedMilestones(arr) {
    const src = arr || milestones;
    return src.map((m) => {
      // AAMPL: once the user edits it, it's PINNED and saved exactly as typed. Only
      // milestones never touched keep auto-deriving from their linked plan task.
      const aampl = m.aampl_manual ? (m.aampl_date || "") : (deriveMilestoneDate(m.event) || m.aampl_date || "");
      // Customer auto-follows AAMPL + 1 week UNLESS the user manually picked a date.
      const customer_date = (m.customer_manual && m.customer_date) ? m.customer_date : addDays(aampl, 7);
      return { ...m, aampl_date: aampl, customer_date, customer_manual: !!m.customer_manual, aampl_manual: !!m.aampl_manual };
    });
  }
  function commitMilestones(arr) {
    const baked = bakedMilestones(arr);
    setProject((p) => ({ ...p, milestones: baked }));
    api.put(`/apqp/${apqpId}`, { milestones: baked }).then(() => regenerateExcelSilently()).catch((e) => alert(e.response?.data?.detail || e.message));
  }
  function addMilestone() {
    const name = window.prompt("New milestone event name:");
    if (!name || !name.trim()) return;
    commitMilestones([...milestones.map((m) => ({ ...m })), { event: name.trim(), aampl_date: "", customer_date: "" }]);
  }
  function removeMilestone(idx) {
    commitMilestones(milestones.filter((_, i) => i !== idx));
  }
  // Edit a milestone's Customer date (manual) — flag it so it stops auto-following AAMPL+7.
  function updMilestoneLocal(idx, key, value) {
    setProject((p) => {
      const arr = ((p.milestones && p.milestones.length) ? p.milestones : DEFAULT_MILESTONES).map((m) => ({ ...m }));
      arr[idx] = { ...arr[idx], [key]: value };
      if (key === "customer_date") arr[idx].customer_manual = true;
      if (key === "aampl_date") arr[idx].aampl_manual = true;  // pin it — stop auto-deriving from the task
      return { ...p, milestones: arr };
    });
  }
  async function recomputeDates() {
    if (!window.confirm("Recompute all task dates from the plan Start Date, durations and dependencies?\n\nThis restores a clean sequence. Your actual dates are not touched.")) return;
    try {
      await api.post(`/apqp/${apqpId}/recompute-dates`);
      await load();
      regenerateExcelSilently();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }
  // Build the anchor list the backend re-flows around: every milestone that has an AAMPL date
  // pins its matching plan task's seq to that exact date. In-between tasks are interpolated.
  function buildMilestoneAnchors(ms) {
    const anchors = [];
    (ms || []).forEach((m) => {
      const d = (m.aampl_date || "").slice(0, 10);
      if (!d) return;
      const t = milestoneTask(m.event);
      if (t && t.seq != null) anchors.push({ seq: t.seq, date: d });
    });
    return anchors;
  }
  async function saveMilestones() {
    const baked = bakedMilestones();
    setProject((p) => ({ ...p, milestones: baked }));
    try {
      await api.put(`/apqp/${apqpId}`, { milestones: baked });
      // Re-flow all task PLAN dates around the milestone anchors so milestone tasks land on
      // their exact dates and the other tasks re-sequence between them — then reload to show it.
      const anchors = buildMilestoneAnchors(baked);
      if (anchors.length) {
        await api.post(`/apqp/${apqpId}/apply-milestones`, { anchors });
        await load();
      }
      regenerateExcelSilently();
      setMsSaved(true); setTimeout(() => setMsSaved(false), 2000);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }
  // Auto-update the milestone box when a mapped task's date is saved: re-derive the AAMPL
  // dates and persist them silently (only after the user has actually edited a task, so we
  // never write on plain page loads). The on-screen box already reflects it live.
  useEffect(() => {
    if (!tasksEditedRef.current || !project || !phases.length) return;
    const baked = bakedMilestones();
    const cur = (project.milestones && project.milestones.length) ? project.milestones : DEFAULT_MILESTONES;
    const changed = baked.length !== cur.length || baked.some((b, i) => b.aampl_date !== (cur[i]?.aampl_date || ""));
    if (!changed) return;
    setProject((p) => ({ ...p, milestones: baked }));
    api.put(`/apqp/${apqpId}`, { milestones: baked }).then(() => regenerateExcelSilently()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);
  // The plan task a milestone maps to (best-effort by event name).
  function milestoneTask(event) {
    const ev = (event || "").toLowerCase();
    const tasks = phases.flatMap((p) => p.tasks || []);
    const find = (re) => tasks.find((t) => re.test(t.task_name || ""));
    const findAll = (re) => tasks.filter((t) => re.test(t.task_name || ""));
    if (/customer sop|\bsop\b/.test(ev)) return find(/sop/i);
    if (/psw/.test(ev)) return find(/psw/i);  // PSW SIGN-OFF → the "PSW sign-off" task (not PPAP)
    // "PPAP SAMPLE SUBMISSION" → the SECOND "Sample Submission" (after Prepare Sample T2, seq 36).
    if (/sample submission|ppap sample/.test(ev)) { const s = findAll(/sample submission/i); return s[1] || s[0] || null; }
    if (/ppap audit|run rate|\bppap\b/.test(ev)) return find(/^ppap$/i) || find(/ppap/i);
    // Each sample milestone links to its OWN task (T2 ≠ T3):
    if (/t3|c2/.test(ev)) return find(/prepare sample \(t2\)/i);                           // T3 → "Prepare Sample (T2)" (seq 35)
    if (/t2|c1/.test(ev)) { const s = findAll(/sample submission/i); return s[0] || null; } // T2 → 1st "Sample Submission" (seq 32)
    if (/t1|fot/.test(ev)) return find(/prepare sample \(t1\)/i);                          // T1 Fot → "Prepare Sample (T1)" (seq 31)
    if (/t0|\bto\b/.test(ev)) return find(/prepare sample \(to\)/i);                       // T0 → "Prepare Sample (TO) Internal" (seq 28)
    // Fallback — match a task by name so ANY milestone (incl. custom ones you Add) colours its
    // task in the list. Exact normalized name first, then either-contains-the-other.
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const evn = norm(event);
    if (evn) {
      const exact = tasks.find((t) => norm(t.task_name) === evn);
      if (exact) return exact;
      const partial = tasks.find((t) => {
        const tn = norm(t.task_name);
        return tn && tn.length >= 4 && (tn.includes(evn) || evn.includes(tn));
      });
      if (partial) return partial;
    }
    return null;
  }
  function deriveMilestoneDate(event) {
    const t = milestoneTask(event);
    return t ? String(t.completion_date || t.start_date || "").slice(0, 10) : "";
  }
  // Milestone status → color: complete=green, in-progress=yellow, pending=red.
  function milestoneStatus(event) {
    const t = milestoneTask(event);
    if (!t) return "pending";
    if (t.status === "complete") return "complete";
    const today = new Date();
    const ps = t.start_date ? new Date(t.start_date) : null;
    if (t.actual_start_date || (ps && today >= ps)) return "progress";
    return "pending";
  }
  const MS_COLOR = { complete: "text-emerald-600", progress: "text-amber-600", pending: "text-red-600" };
  const MS_DOT = { complete: "bg-emerald-500", progress: "bg-amber-500", pending: "bg-red-500" };

  async function downloadTimingPlan() {
    try {
      const r = await api.get(`/apqp/projects/${apqpId}/timing-plan.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `APQP_Timing_Plan_${project.part_number || project.apqp_number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // All auto-generated / uploaded documents across every task (shortcut popup).
  function openDocsModal() {
    setDocsModal(true);
    setAllDocsLoading(true);
    api.get(`/apqp/${apqpId}/documents`)
      .then((r) => setAllDocs(r.data.documents || []))
      .catch(() => setAllDocs([]))
      .finally(() => setAllDocsLoading(false));
  }
  function viewDocFile(doc) {
    const target = doc.is_office ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.url)}` : doc.url;
    window.open(target, "_blank");
  }
  function downloadAllDocsZip() {
    window.open(`${api.defaults.baseURL}/apqp/${apqpId}/documents.zip`, "_blank");
  }

  if (!project) return <div className="p-8 text-sm text-slate-400">Loading APQP project…</div>;

  const totalDays = daysBetween(project.start_date, project.completion_date);

  // Flag tasks that are milestones so they show in the milestone colour in the list
  // (pending=red · progress=amber · complete=green — same as the milestone strip).
  const milestoneByTaskId = {};
  milestones.forEach((m) => {
    const t = milestoneTask(m.event);
    if (t) milestoneByTaskId[t.id] = { event: m.event, status: milestoneStatus(m.event) };
  });

  // Responsibility dropdown options — every role (so a task can be assigned to any role like
  // "Quality Head"), falling back to the fixed designations when roles haven't loaded.
  const respRoleOptions = (roles && roles.length)
    ? roles.map((r) => ({ id: r.role_id, label: r.name || r.role_id }))
    : DESIGNATIONS.map((r) => ({ id: r, label: ROLE_LABELS[r] || r }));
  const roleLabelMap = { ...ROLE_LABELS };
  (roles || []).forEach((r) => { roleLabelMap[r.role_id] = r.name || r.role_id; });

  // Continuous task numbering across phases (1,2,3,… not restarting each phase).
  let _acc = 0;
  const phaseTaskOffset = phases.map((ph) => { const o = _acc; _acc += (ph.tasks || []).length; return o; });
  // Map each task id → the continuous task number shown in the table (1,2,3,… across phases),
  // so the All-Documents popup can label every document with its task no.
  const taskNoById = {};
  phases.forEach((ph, p) => (ph.tasks || []).forEach((t, j) => { taskNoById[t.id] = phaseTaskOffset[p] + j + 1; }));

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="apqp-detail-page">
      {/* All auto-generated documents — shortcut popup */}
      {docsModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4" data-testid="apqp-docs-modal" onClick={() => setDocsModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const autoCount = allDocs.filter((d) => d.auto_generated).length;
              const shownDocs = docsAutoOnly ? allDocs.filter((d) => d.auto_generated) : allDocs;
              return (
                <>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Paperclip size={16} className="text-indigo-600" />
                <h3 className="text-sm font-semibold text-slate-800">All Documents <span className="text-slate-400 font-normal">({allDocs.length})</span></h3>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadAllDocsZip} disabled={allDocs.length === 0}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-800 text-white rounded hover:bg-slate-900 disabled:opacity-40" data-testid="apqp-docs-zip">
                  <Download size={12} /> Download All (ZIP)
                </button>
                <button onClick={() => setDocsModal(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
              </div>
            </div>
            {/* Quick filter: All vs auto-filled (system-generated) documents */}
            <div className="flex items-center gap-2 px-5 py-2 border-b border-slate-100 bg-slate-50/60">
              <button onClick={() => setDocsAutoOnly(false)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full border ${!docsAutoOnly ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}
                data-testid="apqp-docs-filter-all">
                All ({allDocs.length})
              </button>
              <button onClick={() => setDocsAutoOnly(true)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full border ${docsAutoOnly ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}
                data-testid="apqp-docs-filter-auto">
                <Sparkles size={11} /> Auto-filled ({autoCount})
              </button>
              <span className="ml-auto text-[10px] text-slate-400">Sorted by task no.</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {allDocsLoading ? (
                <div className="p-8 text-center text-xs text-slate-400">Loading documents…</div>
              ) : shownDocs.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400">
                  {docsAutoOnly ? "No auto-filled documents yet. Save any auto-form (Timing Plan, CFT, PFMEA, Control Plan…) and it’ll appear here." : "No documents yet. Save the auto-forms or upload files in the tasks and they’ll appear here."}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {shownDocs.map((doc, i) => {
                    const taskNo = taskNoById[doc.task_id] || doc.seq || null;
                    return (
                    <li key={i} className={`flex items-center gap-2 px-5 py-2.5 text-xs hover:bg-slate-50 ${doc.auto_generated ? "bg-emerald-50/40" : ""}`} data-testid={`apqp-doc-${i}`}>
                      {taskNo != null && (
                        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded bg-slate-200 text-slate-700 font-mono text-[11px] font-semibold" title={`Task #${taskNo}`} data-testid={`apqp-doc-taskno-${i}`}>#{taskNo}</span>
                      )}
                      <Paperclip size={13} className="text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-slate-800 flex items-center gap-1.5" title={doc.file_name}>
                          <span className="truncate">{doc.file_name}</span>
                          {doc.auto_generated && (
                            <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase tracking-wide" data-testid={`apqp-doc-auto-${i}`}><Sparkles size={9} /> Auto</span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{taskNo != null ? `Task ${taskNo} · ` : ""}{doc.task_name} · {doc.deliverable_name}{doc.document_no ? ` · ${doc.document_no}` : ""}</div>
                      </div>
                      {doc.url && (
                        <>
                          <button onClick={() => viewDocFile(doc)} className="text-slate-500 hover:text-orange-600 p-1" title="View"><Eye size={15} /></button>
                          <a href={doc.url} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="Download"><Download size={15} /></a>
                        </>
                      )}
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      <button onClick={() => navigate("/apqp")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="apqp-back">
        <ArrowLeft size={15} /> Back to APQP Projects
      </button>

      {/* Project header */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap" data-testid="apqp-detail-title">
              <span>{project.apqp_number}: {project.project_name} <span className="text-slate-400 font-medium">[{project.part_number || "—"}]</span></span>
              {project.is_prototype && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wide" data-testid="apqp-proto-badge">Proto PRJ</span>
              )}
              {project.on_hold && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800" data-testid="apqp-hold-badge">On Hold</span>
              )}
              {canEdit && (
                <button onClick={toggleHold} data-testid="apqp-hold-toggle"
                  className={`text-[11px] font-medium px-2 py-0.5 rounded border ${project.on_hold ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-purple-300 text-purple-700 hover:bg-purple-50"}`}>
                  {project.on_hold ? "Resume" : "Put on Hold"}
                </button>
              )}
            </h1>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Customer: <span className="text-lg font-extrabold text-orange-600">{project.customer_name || "—"}</span></span>
              <span>· Plant: <span className="text-slate-700">{project.plant_name || "—"}</span></span>
              <span className="flex items-center gap-1">· Category<span className="text-red-500">*</span>:
                <select disabled={!canEdit} value={project.category || ""} data-testid="apqp-category-select"
                  onChange={(e) => saveCategory(e.target.value)}
                  className={`border rounded px-1.5 py-0.5 text-xs bg-white disabled:bg-slate-50 ${project.category ? "border-slate-200" : "border-red-400 ring-1 ring-red-300 text-red-600"}`}>
                  <option value="">— Select (required)</option>
                  <option value="2W">2W (2-Wheeler)</option>
                  <option value="4W">4W (4-Wheeler)</option>
                </select>
                {!project.category && <span className="text-[11px] text-red-500 font-medium">required</span>}
              </span>
            </div>
            {/* Team — assign the person for each designation ONCE here (compact grid); task
                boxes show only the designation. */}
            <div className="mt-1.5 flex items-center gap-2 text-xs">
              <span className="text-[10px] uppercase tracking-wider text-slate-400">Team</span>
              <div className="flex items-center gap-4">
                {TEAM_ROWS.filter((r) => r.role === "npd_engineer").map((row) => (
                  <div key={row.role || row.key} className="flex items-center gap-1.5">
                    <span className="font-semibold text-indigo-700 w-24 shrink-0">{row.kind === "user" ? ROLE_LABELS[row.role] : row.label}</span>
                    {canEdit ? (
                      row.kind === "user" ? (
                        <select value={(project.team || {})[row.role] || ""} onChange={(e) => setTeamMember(row.role, e.target.value || null)}
                          className="border border-slate-200 rounded px-1 py-0.5 text-xs bg-white w-36" data-testid={`apqp-team-${row.role}`}>
                          <option value="">—</option>
                          {users.map((u) => <option key={u.user_id} value={u.user_id}>{mr(u.name)}</option>)}
                        </select>
                      ) : (
                        <input value={(project.owners || {})[row.key] || ""} onChange={(e) => setOwnerLocal(row.key, e.target.value)} onBlur={saveOwners}
                          placeholder="Mr. Name" className="border border-slate-200 rounded px-1 py-0.5 text-xs bg-white w-36" data-testid={`apqp-team-${row.key}`} />
                      )
                    ) : (
                      <span className="text-slate-700">{row.kind === "user" ? mr(teamName(row.role)) : ((project.owners || {})[row.key] || "—")}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Won Date</div>
                {canEdit ? (
                  <input type="date" value={project.won_date ? String(project.won_date).slice(0, 10) : ""}
                    onChange={(e) => saveWonDate(e.target.value || null)}
                    title="Changing the Won date re-anchors Start Date and shifts the whole plan"
                    className="border border-slate-200 rounded px-1.5 py-0.5 text-xs text-emerald-700 font-medium bg-white" data-testid="apqp-won-date" />
                ) : (
                  <div className="text-emerald-700 font-medium">{project.won_date ? String(project.won_date).slice(0, 10) : "—"}</div>
                )}
              </div>
              {project.hold_date && (
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Hold Date</div><div className="text-purple-700 font-medium">{String(project.hold_date).slice(0, 10)}</div></div>
              )}
              {project.resume_date && (
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Resume Date</div><div className="text-emerald-700 font-medium">{String(project.resume_date).slice(0, 10)}</div></div>
              )}
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Start Date</div><div className="text-slate-700">{project.start_date || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Completion Date</div><div className="text-slate-700">{project.completion_date || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Total Days</div><div className="text-slate-700">{totalDays != null ? `${totalDays} Days` : "—"}</div></div>
            </div>
            {/* Download + compact milestone list: AAMPL auto-fills from the task; Customer is
                manual (you fill it). Add / remove events. Kept small — same space. */}
            <div className="flex flex-col items-end gap-1 w-full max-w-[560px]" data-testid="apqp-milestone-strip">
              <div className="flex items-center gap-1.5">
                <button onClick={() => navigate(`/apqp/${apqpId}/review`)} data-testid="apqp-review-btn"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-teal-700 text-white rounded hover:bg-teal-800">
                  <ListChecks size={13} /> Review & Approve
                </button>
                <button onClick={openDocsModal} data-testid="apqp-docs-shortcut"
                  title="View all auto-generated documents across tasks 1–58"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">
                  <Paperclip size={13} /> Documents
                </button>
                <button onClick={downloadTimingPlan} data-testid="apqp-download-timing-plan"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">
                  <Download size={13} /> Download Timing Plan (Excel)
                </button>
              </div>
              <div className="w-full">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[9px] uppercase tracking-wider text-slate-400">Milestones <span className="normal-case italic">· AAMPL auto · Customer = +1wk (editable)</span></span>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      {msSaved && <span className="text-[10px] text-emerald-600 font-semibold" data-testid="apqp-ms-saved">✓ Saved</span>}
                      <button onClick={recomputeDates} className="text-[10px] text-slate-600 hover:text-slate-800 inline-flex items-center gap-0.5" title="Recompute all task dates cleanly from Start Date + durations + dependencies" data-testid="apqp-recompute-dates">
                        <RotateCcw size={11} /> Recompute dates
                      </button>
                      <button onClick={addMilestone} className="text-[10px] text-orange-600 hover:text-orange-700 inline-flex items-center gap-0.5" data-testid="apqp-add-milestone">
                        <Plus size={11} /> Add
                      </button>
                      <button onClick={saveMilestones} className="text-[10px] text-white bg-emerald-600 hover:bg-emerald-700 rounded px-2 py-0.5 inline-flex items-center gap-0.5" data-testid="apqp-save-milestones">
                        <Save size={11} /> Save
                      </button>
                    </div>
                  )}
                </div>
                {/* Two-column grid keeps 8 milestones to ~4 rows so the header card stays small. */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {milestones.map((m, i) => {
                    const aampl = m.aampl_manual ? (m.aampl_date || "") : (deriveMilestoneDate(m.event) || m.aampl_date || "");
                    const st = milestoneStatus(m.event);
                    // Customer auto-follows AAMPL + 1 week; a manually-picked date wins.
                    const custVal = (m.customer_manual && m.customer_date) ? m.customer_date : addDays(aampl, 7);
                    return (
                      <div key={i} className="flex items-center gap-1">
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${MS_DOT[st]}`} title={st === "complete" ? "Completed" : st === "progress" ? "In Progress" : "Pending"}></span>
                        <span className={`text-[10px] font-semibold truncate flex-1 ${MS_COLOR[st]}`} title={m.event}>{m.event}</span>
                        {canEdit ? (
                          <input type="date" value={(aampl || "").slice(0, 10)}
                            onChange={(e) => updMilestoneLocal(i, "aampl_date", e.target.value)}
                            onBlur={saveMilestones}
                            title="AAMPL date — fully editable; saved exactly as you set it (won't auto-change)"
                            className={`border border-slate-200 rounded px-1 py-0.5 text-[9px] w-[104px] shrink-0 ${m.aampl_manual ? "bg-white" : "bg-slate-50"}`} data-testid={`apqp-ms-aampl-${i}`} />
                        ) : (
                          <span className="text-[9px] text-slate-500 text-center bg-slate-50 rounded px-0.5 w-14 shrink-0" title="AAMPL (auto from task)">{aampl || "—"}</span>
                        )}
                        {canEdit ? (
                          <input type="date" value={(custVal || "").slice(0, 10)} onChange={(e) => updMilestoneLocal(i, "customer_date", e.target.value)} onBlur={saveMilestones}
                            title="Customer = AAMPL + 1 week (editable) — pick from calendar"
                            className="border border-slate-200 rounded px-1 py-0.5 text-[9px] w-[104px] shrink-0" data-testid={`apqp-ms-cust-${i}`} />
                        ) : (
                          <span className="text-[9px] text-slate-600 text-center w-14 shrink-0">{custVal || "—"}</span>
                        )}
                        {canEdit ? (
                          <button onClick={() => removeMilestone(i)} className="text-red-500 hover:text-red-700 shrink-0" title="Remove"><X size={10} /></button>
                        ) : <span className="shrink-0 w-3"></span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* A3 — Timing-Plan header fields + Download */}
        <div className="flex flex-wrap items-end gap-3 mt-2 pt-2 border-t border-slate-100">
          <div className="max-w-[280px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Dev. Category</div>
            <select disabled={!canEdit} value={project.dev_category || ""} data-testid="apqp-dev-category"
              onChange={(e) => saveDevCategory(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white disabled:bg-slate-50 w-full">
              <option value="">—</option>
              {DEV_CATEGORIES.map(([c, label]) => <option key={c} value={c}>{c} - {label}</option>)}
            </select>
            <div className="mt-1 text-[9px] leading-tight text-slate-400 whitespace-normal">
              {DEV_CATEGORIES.map(([c, label]) => `${c}-${label}`).join(" · ")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">ECN Level</div>
            <input disabled={!canEdit} value={project.ecn_level || ""} data-testid="apqp-ecn-level"
              onChange={(e) => setProject((p) => ({ ...p, ecn_level: e.target.value }))}
              onBlur={(e) => saveProjectField("ecn_level", e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50 w-28" placeholder="—" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">CFT Review</div>
            <input disabled={!canEdit} value={project.cft_review ?? ""} data-testid="apqp-cft-review"
              onChange={(e) => setProject((p) => ({ ...p, cft_review: e.target.value }))}
              onBlur={(e) => saveProjectField("cft_review", e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50 w-52" placeholder="Once in a week every Friday" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Top Mgmt. Review</div>
            <input disabled={!canEdit} value={project.top_mgmt_review ?? ""} data-testid="apqp-top-mgmt-review"
              onChange={(e) => setProject((p) => ({ ...p, top_mgmt_review: e.target.value }))}
              onBlur={(e) => saveProjectField("top_mgmt_review", e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50 w-40" placeholder="Once in a month" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-slate-100 text-xs">
          <span className="text-slate-500">Total <strong className="text-slate-800">{counts.total}</strong></span>
          <span className="text-emerald-600">Completed <strong>{counts.completed}</strong></span>
          <span className="text-amber-600" title="All tasks not yet completed (includes overdue)">In Progress <strong>{counts.pending}</strong></span>
          <span className="text-red-600" title="Not complete and plan end date already passed (a subset of In Progress)">Overdue <strong>{counts.overdue}</strong></span>
          {canEdit && (
            <span className="flex items-center gap-1.5 pl-3 border-l border-slate-200">
              <span className="text-slate-400">Applicability:</span>
              <button onClick={() => setAllApplicability("Yes")} data-testid="apqp-all-applicable"
                className="px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium">All Applicable</button>
              <button onClick={() => setAllApplicability("No")} data-testid="apqp-all-na"
                className="px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:bg-slate-50 font-medium">All N/A</button>
            </span>
          )}
        </div>
      </div>

      {/* Phase-grouped task tables */}
      {phases.map((ph, phIdx) => (
        <div key={ph.phase} className="mb-4" data-testid={`apqp-phase-${ph.phase}`}>
          <div className="text-sm font-semibold text-white bg-slate-700 px-3 py-2 rounded-t">{ph.phase}</div>
          <div className="overflow-x-auto border border-slate-200 rounded-b bg-white">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>
                  <th className="text-left px-3 py-2">Task</th>
                  <th className="text-left px-2 py-2 w-20" title="Ap = Applicable · NA = Not Applicable">Applic.</th>
                  <th className="text-left px-3 py-2 w-44">Responsibility</th>
                  <th className="text-left px-3 py-2 w-36">Plan <span className="text-slate-400 normal-case">(Start / End)</span></th>
                  <th className="text-left px-3 py-2 w-36 bg-emerald-50/60">Actual <span className="text-slate-400 normal-case">(Start / End)</span></th>
                  <th className="text-left px-3 py-2 w-16">Days</th>
                  <th className="text-left px-3 py-2 w-32">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ph.tasks.map((t, idx) => {
                  const sm = STATUS_META[t.status] || STATUS_META.incomplete;
                  // Any "Review of …" task (phase gate or the special-characteristics review)
                  // is a review checkpoint → always green. Milestone tasks take their status colour.
                  const isReviewPhase = /review of /i.test(t.task_name || "");
                  const ms = milestoneByTaskId[t.id];
                  const isNA = (t.applicability || "Yes") === "No";
                  const nameColor = isNA ? "text-slate-400"
                    : isReviewPhase ? "text-emerald-600 font-semibold"
                    : ms ? MS_COLOR[ms.status] + " font-semibold"
                    : "text-slate-800";
                  const dotColor = isReviewPhase ? "bg-emerald-500" : ms ? MS_DOT[ms.status] : "";
                  return (
                    <tr key={t.id} className={`hover:bg-slate-50 ${isReviewPhase ? "bg-emerald-50/40" : ms ? "bg-red-50/20" : ""}`} data-testid={`apqp-task-row-${t.id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{phaseTaskOffset[phIdx] + idx + 1}</td>
                      <td className={`px-3 py-2 ${isNA ? "line-through" : ""} ${nameColor}`}>
                        <div className="w-[220px] whitespace-normal break-words leading-snug flex items-start gap-1.5">
                          {dotColor && <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${dotColor}`} title={isReviewPhase ? "Phase review gate" : `Milestone: ${ms?.event || ""}`} />}
                          <span>{t.task_name}</span>
                        </div>
                      </td>
                      <td className="px-1 py-2">
                        <select disabled={!canEdit} value={t.applicability || "Yes"}
                          onChange={(e) => patchTaskAndSave(t.id, { applicability: e.target.value })}
                          className={`w-full border rounded pl-1.5 pr-0.5 py-1 text-xs font-medium disabled:bg-slate-50 ${(t.applicability || "Yes") === "No" ? "border-slate-300 bg-slate-50 text-slate-500" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}
                          title={(t.applicability || "Yes") === "No" ? "Not Applicable" : "Applicable"}
                          data-testid={`apqp-task-applicability-${t.id}`}>
                          <option value="Yes">Ap</option>
                          <option value="No">NA</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {/* Designation only — the actual person is set once in the Team panel at the top.
                            Primary + optional Secondary (backup) responsibility, both mapped to a person. */}
                        <div className="flex flex-col gap-1">
                          <select disabled={!canEdit} value={t.responsibility_role || ""}
                            onChange={(e) => patchTask(t.id, { responsibility_role: e.target.value || null })}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs bg-white disabled:bg-slate-50" data-testid={`apqp-task-resp-${t.id}`}
                            title={t.responsibility_role ? `Primary: ${(roleLabelMap[t.responsibility_role] || t.responsibility_role)} = ${teamName(t.responsibility_role)}` : "Primary responsibility"}>
                            <option value="">—</option>
                            {respRoleOptions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                          <select disabled={!canEdit} value={t.secondary_responsibility_role || ""}
                            onChange={(e) => patchTask(t.id, { secondary_responsibility_role: e.target.value || null })}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-[11px] bg-white disabled:bg-slate-50 text-slate-500" data-testid={`apqp-task-resp2-${t.id}`}
                            title={t.secondary_responsibility_role ? `Secondary: ${(roleLabelMap[t.secondary_responsibility_role] || t.secondary_responsibility_role)} = ${teamName(t.secondary_responsibility_role)}` : "Secondary (backup) responsibility"}>
                            <option value="">2nd —</option>
                            {respRoleOptions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex flex-col gap-1">
                          <input type="date" disabled={!canEdit} value={t.start_date || ""}
                            onChange={(e) => patchTaskAndSave(t.id, { start_date: e.target.value || null })}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50" title="Plan Start" data-testid={`apqp-task-start-${t.id}`} />
                          <input type="date" disabled={!canEdit} value={t.completion_date || ""}
                            onChange={(e) => patchTaskAndSave(t.id, { completion_date: e.target.value || null })}
                            className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50" title="Plan End" data-testid={`apqp-task-end-${t.id}`} />
                        </div>
                      </td>
                      <td className="px-3 py-1.5 bg-emerald-50/40">
                        <div className="flex flex-col gap-1">
                          <input type="date" disabled={!canEdit} value={t.actual_start_date || ""}
                            onChange={(e) => patchTaskAndSave(t.id, { actual_start_date: e.target.value || null })}
                            className="w-full border border-emerald-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50" title="Actual Start" data-testid={`apqp-task-astart-${t.id}`} />
                          <input type="date" disabled={!canEdit} value={t.actual_completion_date || ""}
                            onChange={(e) => patchTaskAndSave(t.id, { actual_completion_date: e.target.value || null })}
                            className="w-full border border-emerald-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50" title="Actual End" data-testid={`apqp-task-aend-${t.id}`} />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {canEdit ? (
                          <input type="number" min="0" placeholder="—" value={actualOffsets[t.id] ?? ""}
                            onChange={(e) => applyActualOffset(t.id, e.target.value)}
                            title="Enter days → Actual dates auto-fill = Plan dates + N days (still editable)"
                            className="w-14 border border-slate-200 rounded px-1 py-1 text-xs text-center font-mono" data-testid={`apqp-task-days-${t.id}`} />
                        ) : (
                          <span className="font-mono text-slate-500">{t.completion_days != null ? t.completion_days : "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => {
                            // Approval-checkpoint tasks open the Review & Approve page
                            // (approve/reject, no document upload) instead of the popup.
                            //  • "Review of phase …"  → whole-phase review
                            //  • "Review of special Characteristics …" → the deliverables it signs off
                            const nm = t.task_name || "";
                            if (/review of phase/i.test(nm)) {
                              navigate(`/apqp/${apqpId}/review?phase=${encodeURIComponent(t.phase || "")}`);
                            } else if (/review of special charac/i.test(nm)) {
                              navigate(`/apqp/${apqpId}/review?checkpoint=${encodeURIComponent(t.id)}`);
                            } else {
                              setPopupTaskId(t.id);
                            }
                          }}
                          data-testid={`apqp-task-status-${t.id}`}
                          className={`w-full border rounded px-2 py-1 text-xs font-semibold ${sm.cls} hover:brightness-95`}>
                          {sm.label}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Save / Cancel */}
      {canEdit && (
        <div className="sticky bottom-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 py-3 pr-24 flex items-center justify-end gap-2 mt-4">
          <span className="text-xs mr-auto">
            {dirty.size > 0
              ? <span className="text-amber-600">{dirty.size} unsaved change(s)</span>
              : excelSynced
              ? <span className="text-emerald-600">✓ Saved · Timing Plan Excel updated</span>
              : <span className="text-slate-400">All changes saved</span>}
          </span>
          <button onClick={load} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded" data-testid="apqp-cancel-btn">
            <RotateCcw size={14} /> Cancel
          </button>
          <button onClick={saveAll} disabled={saving || dirty.size === 0} data-testid="apqp-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      {popupTask && (
        <TaskStatusPopup
          apqpId={apqpId}
          task={popupTask}
          canEdit={canEdit}
          onClose={() => setPopupTaskId(null)}
          onLocalPatch={(patch) => patchTask(popupTask.id, patch)}
          onTaskRefresh={load}
        />
      )}
    </div>
  );
}

// Open an auto-generated form Excel inline via the Microsoft Office online viewer.
async function openFormView(urlPath) {
  const win = window.open("", "_blank");
  try {
    const r = await api.get(urlPath);
    const url = r.data?.url;
    if (!url) throw new Error("No view URL");
    const target = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
    if (win) win.location = target; else window.open(target, "_blank");
  } catch (e) {
    if (win) win.close();
    alert(e.response?.data?.detail || e.message);
  }
}

// Editable Cross Functional Team form (ENGG/F/01) for the "CFT and Awareness Session"
// task — auto-generates the company Excel and satisfies the mandatory deliverable.
function CftFormSection({ apqpId, task, canEdit, onSaved }) {
  const [cft, setCft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/cft`)
      .then((r) => { if (alive) setCft(r.data.cft); })
      .catch(() => { if (alive) setCft(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (field, val) => setCft((c) => ({ ...c, [field]: val }));
  const setMember = (idx, field, val) =>
    setCft((c) => ({ ...c, members: c.members.map((m, i) => (i === idx ? { ...m, [field]: val } : m)) }));
  const addMember = () => setCft((c) => ({ ...c, members: [...(c.members || []), { name: "", dept: "" }] }));
  const removeMember = (idx) => setCft((c) => ({ ...c, members: c.members.filter((_, i) => i !== idx) }));
  const setRev = (idx, field, val) => setCft((c) => ({ ...c, revisions: (c.revisions || []).map((rv, i) => (i === idx ? { ...rv, [field]: val } : rv)) }));
  const addRev = () => setCft((c) => ({ ...c, revisions: [...(c.revisions || []), { rev_no: "", rev_detail: "", rev_date: "" }] }));
  const removeRev = (idx) => setCft((c) => ({ ...c, revisions: (c.revisions || []).filter((_, i) => i !== idx) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/cft`, cft);
      setCft(r.data.cft);
      setSavedAt(true);
      setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/cft.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Cross Functional Team.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!window.confirm("Delete the Cross Functional Team form and its generated Excel? The standard team will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/cft`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/cft`);
      setCft(r2.data.cft);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Cross Functional Team…</div>;
  if (!cft) return null;

  const field = "w-full border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-cft-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <Users size={14} /> Cross Functional Team <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG/F/01</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/cft/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-cft-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-cft-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-cft-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-cft-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div>
          <div className={lbl}>Team Leader</div>
          <input className={field} disabled={!canEdit} value={cft.team_leader || ""}
            onChange={(e) => set("team_leader", e.target.value)} data-testid="apqp-cft-leader" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Team Members</div>
            {canEdit && (
              <button onClick={addMember} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline" data-testid="apqp-cft-add">
                <Plus size={11} /> Add member
              </button>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="text-left w-8 pb-1">#</th>
                <th className="text-left pb-1">Name</th>
                <th className="text-left pb-1">Department</th>
                {canEdit && <th className="w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {(cft.members || []).map((m, i) => (
                <tr key={i} data-testid={`apqp-cft-member-${i}`}>
                  <td className="py-0.5 text-slate-400">{i + 1}</td>
                  <td className="py-0.5 pr-1">
                    <input className={field} disabled={!canEdit} value={m.name || ""}
                      onChange={(e) => setMember(i, "name", e.target.value)} />
                  </td>
                  <td className="py-0.5 pr-1">
                    <input className={field} disabled={!canEdit} value={m.dept || ""}
                      onChange={(e) => setMember(i, "dept", e.target.value)} />
                  </td>
                  {canEdit && (
                    <td className="py-0.5 text-center">
                      <button onClick={() => removeMember(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button>
                    </td>
                  )}
                </tr>
              ))}
              {(cft.members || []).length === 0 && (
                <tr><td colSpan={canEdit ? 4 : 3} className="py-2 text-center text-slate-400 italic">No members. Click "Add member".</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className={lbl}>Team Meeting Frequency</div>
            <input className={field} disabled={!canEdit} value={cft.team_meeting_frequency || ""}
              onChange={(e) => set("team_meeting_frequency", e.target.value)} />
          </div>
          <div>
            <div className={lbl}>Top Management Meeting Frequency</div>
            <input className={field} disabled={!canEdit} value={cft.top_mgmt_meeting_frequency || ""}
              onChange={(e) => set("top_mgmt_meeting_frequency", e.target.value)} />
          </div>
          <div>
            <div className={lbl}>Any supplier to be included in team</div>
            <input className={field} disabled={!canEdit} value={cft.supplier_included || ""}
              onChange={(e) => set("supplier_included", e.target.value)} />
          </div>
          <div>
            <div className={lbl}>Any customer representative to be included</div>
            <input className={field} disabled={!canEdit} value={cft.customer_rep_included || ""}
              onChange={(e) => set("customer_rep_included", e.target.value)} />
          </div>
          <div className="col-span-2">
            <div className={lbl}>Frequency of interaction with Customer Rep / Supplier (if any)</div>
            <input className={field} disabled={!canEdit} value={cft.interaction_frequency || ""}
              onChange={(e) => set("interaction_frequency", e.target.value)} />
          </div>
          <div>
            <div className={lbl}>Approved by</div>
            <input className={field} disabled={!canEdit} value={cft.approved_by || ""}
              onChange={(e) => set("approved_by", e.target.value)} />
          </div>
          <div>
            <div className={lbl}>Date</div>
            <input type="date" className={field} disabled={!canEdit} value={cft.date || ""}
              onChange={(e) => set("date", e.target.value)} />
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Revision History</div>
            {canEdit && <button onClick={addRev} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add revision</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                <tr><th className="w-6"></th><th className="text-left pb-1 px-0.5 w-24">Rev No.</th><th className="text-left pb-1 px-0.5 min-w-[240px]">Rev. Detail</th><th className="text-left pb-1 px-0.5 w-40">Rev Date</th>{canEdit && <th className="w-6"></th>}</tr>
              </thead>
              <tbody>
                {(cft.revisions || []).map((rv, i) => (
                  <tr key={i} className="align-top">
                    <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={rv.rev_no || ""} onChange={(e) => setRev(i, "rev_no", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={rv.rev_detail || ""} onChange={(e) => setRev(i, "rev_detail", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input type="date" className={`${field} min-w-[140px]`} disabled={!canEdit} value={rv.rev_date || ""} onChange={(e) => setRev(i, "rev_date", e.target.value)} /></td>
                    {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRev(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable Drawing Review Sheet (ENGG/F/02), Phase 1.
const DRS_COLS = [
  ["description", "Description", "min-w-[110px]"], ["specification", "Specification", "min-w-[110px]"],
  ["tol_min", "Tol Min", "w-20"], ["tol_max", "Tol Max", "w-20"],
  ["clarity", "Clarity (Y/N)", "w-16"], ["capability", "Capability (Y/N)", "w-16"],
  ["method", "Method / Instrument", "min-w-[120px]"], ["feasibility", "Feasibility (Y/N)", "w-16"],
  ["remarks", "Remarks", "min-w-[130px]"],
];
function DrawingReviewFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/drawing-review`)
      .then((r) => { if (alive) setD(r.data.drawing_review); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { description: "", specification: "", tol_min: "", tol_max: "", clarity: "Y", capability: "Y", method: "", feasibility: "Y", remarks: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));
  const moveRow = (from, to) => setD((c) => {
    const rows = [...(c.rows || [])];
    if (to < 0 || to >= rows.length) return c;
    const [m] = rows.splice(from, 1); rows.splice(to, 0, m);
    return { ...c, rows };
  });

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/drawing-review`, d);
      setD(r.data.drawing_review);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/drawing-review.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Drawing Review Sheet.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Drawing Review Sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/drawing-review`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/drawing-review`);
      setD(r2.data.drawing_review);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Drawing Review Sheet…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-drs-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Drawing Review Sheet <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG/F/02</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/drawing-review/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-drs-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-drs-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-drs-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-drs-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Project Name</div><input className={fld} disabled={!canEdit} value={d.project_name || ""} onChange={(e) => set("project_name", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input type="date" className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Customer Name</div><input className={fld} disabled={!canEdit} value={d.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><div className={lbl}>End Customer</div><input className={fld} disabled={!canEdit} value={d.end_customer || ""} onChange={(e) => set("end_customer", e.target.value)} /></div>
          <div><div className={lbl}>Contact Name</div><input className={fld} disabled={!canEdit} value={d.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div><div className={lbl}>Contact No.</div><input className={fld} disabled={!canEdit} value={d.contact_no || ""} onChange={(e) => set("contact_no", e.target.value)} /></div>
          <div><div className={lbl}>Drawing No.</div><input className={fld} disabled={!canEdit} value={d.drawing_no || ""} onChange={(e) => set("drawing_no", e.target.value)} /></div>
          <div><div className={lbl}>Revision Status</div><input className={fld} disabled={!canEdit} value={d.revision_status || ""} onChange={(e) => set("revision_status", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Project Manager</div><input className={fld} disabled={!canEdit} value={d.project_manager || ""} onChange={(e) => set("project_manager", e.target.value)} /></div>
          <div><div className={lbl}>PM Contact No.</div><input className={fld} disabled={!canEdit} value={d.pm_contact_no || ""} onChange={(e) => set("pm_contact_no", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Supplier Details</div><input className={fld} disabled={!canEdit} value={d.supplier_details || ""} onChange={(e) => set("supplier_details", e.target.value)} /></div>
          <div><div className={lbl}>Material</div><input className={fld} disabled={!canEdit} value={d.material || ""} onChange={(e) => set("material", e.target.value)} /></div>
          <div><div className={lbl}>Surface</div><input className={fld} disabled={!canEdit} value={d.surface || ""} onChange={(e) => set("surface", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-1 col-span-2">
            <div><div className={lbl}>SC</div><input className={fld} disabled={!canEdit} value={d.sc || ""} onChange={(e) => set("sc", e.target.value)} /></div>
            <div><div className={lbl}>CC</div><input className={fld} disabled={!canEdit} value={d.cc || ""} onChange={(e) => set("cc", e.target.value)} /></div>
            <div><div className={lbl}>QC</div><input className={fld} disabled={!canEdit} value={d.qc || ""} onChange={(e) => set("qc", e.target.value)} /></div>
          </div>
          <div className="col-span-2"><div className={lbl}>Special Requirement</div><input className={fld} disabled={!canEdit} value={d.special_requirement || ""} onChange={(e) => set("special_requirement", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Standard (IS/DIN)</div><input className={fld} disabled={!canEdit} value={d.standard || ""} onChange={(e) => set("standard", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Dimensions / Review rows</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>{canEdit && <th className="w-10"></th>}<th className="w-6"></th>{DRS_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  {canEdit && (
                    <td className="pt-1 pr-1">
                      <div className="flex items-center gap-0.5">
                        <GripVertical size={12} className="text-slate-300 cursor-grab" title="Drag to reorder" />
                        <div className="flex flex-col">
                          <button onClick={() => moveRow(i, i - 1)} disabled={i === 0} className="text-slate-400 hover:text-orange-600 disabled:opacity-20 leading-none" title="Move up"><ChevronUp size={12} /></button>
                          <button onClick={() => moveRow(i, i + 1)} disabled={i === (d.rows || []).length - 1} className="text-slate-400 hover:text-orange-600 disabled:opacity-20 leading-none" title="Move down"><ChevronDown size={12} /></button>
                        </div>
                      </div>
                    </td>
                  )}
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  {DRS_COLS.map(([k, , w]) => (
                    <td key={k} className="px-0.5 py-0.5"><input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} /></td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable Team Feasibility Commitment form (ENGG/F/05) for the "Team Feasibility
// commitment" task — auto-generates the company Excel and satisfies the deliverable.
const FEAS_CONCLUSIONS = [
  { code: "feasible_no_revision", label: "Feasible", desc: "Product can be produced as specified with no revisions." },
  { code: "feasible_changes", label: "Feasible", desc: "Changes recommended (see attached)." },
  { code: "not_feasible", label: "Not Feasible", desc: "Design revision required to produce product within the specified requirements (see attached)." },
];

function FeasibilityFormSection({ apqpId, task, canEdit, onSaved }) {
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/feasibility`)
      .then((r) => { if (alive) setF(r.data.feasibility); })
      .catch(() => { if (alive) setF(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setF((c) => ({ ...c, [k]: v }));
  const setConsider = (i, ans) => setF((c) => ({ ...c, considerations: c.considerations.map((x, j) => (j === i ? { ...x, answer: ans } : x)) }));
  const setSign = (i, k, v) => setF((c) => ({ ...c, signoff: c.signoff.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addSign = () => setF((c) => ({ ...c, signoff: [...(c.signoff || []), { name: "", title: "", date: "" }] }));
  const removeSign = (i) => setF((c) => ({ ...c, signoff: c.signoff.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/feasibility`, f);
      setF(r.data.feasibility);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/feasibility.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Team Feasibility Commitment.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Team Feasibility form and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/feasibility`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/feasibility`);
      setF(r2.data.feasibility);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Team Feasibility form…</div>;
  if (!f) return null;

  const field = "w-full border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const YN = ({ active, on, children }) => (
    <button type="button" disabled={!canEdit} onClick={on}
      className={`px-2 py-0.5 text-[11px] rounded border ${active ? (children === "YES" ? "bg-emerald-500 border-emerald-500 text-white" : "bg-red-500 border-red-500 text-white") : "border-slate-300 text-slate-500 hover:bg-slate-50"} disabled:opacity-60`}>
      {children}
    </button>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-feasibility-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Team Feasibility Commitment <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG/F/05</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/feasibility/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-feas-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-feas-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-feas-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-feas-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Customer</div><input className={field} disabled={!canEdit} value={f.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Part Name / Description</div><input className={field} disabled={!canEdit} value={f.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part Number</div><input className={field} disabled={!canEdit} value={f.part_number || ""} onChange={(e) => set("part_number", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={f.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Revision Level</div><input className={field} disabled={!canEdit} value={f.revision_level || ""} onChange={(e) => set("revision_level", e.target.value)} /></div>
        </div>

        <div>
          <div className={lbl}>Feasibility Considerations</div>
          <div className="border border-slate-200 rounded divide-y divide-slate-100">
            {(f.considerations || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1" data-testid={`apqp-feas-consider-${i}`}>
                <div className="flex gap-1 shrink-0">
                  <YN active={(c.answer || "").toUpperCase() === "YES"} on={() => setConsider(i, "YES")}>YES</YN>
                  <YN active={(c.answer || "").toUpperCase() === "NO"} on={() => setConsider(i, "NO")}>NO</YN>
                </div>
                <div className="text-xs text-slate-700">{c.text}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className={lbl}>Conclusion</div>
          <div className="space-y-1">
            {FEAS_CONCLUSIONS.map((o) => (
              <label key={o.code} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="radio" name={`feas-concl-${task.id}`} disabled={!canEdit} className="mt-0.5"
                  checked={f.conclusion === o.code} onChange={() => set("conclusion", o.code)} />
                <span><span className="font-semibold">{o.label}</span> — {o.desc}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Sign-Off (Team Member / Title / Date)</div>
            {canEdit && <button onClick={addSign} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add</button>}
          </div>
          <div className="space-y-1">
            {(f.signoff || []).map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_100px_auto] gap-1 items-center" data-testid={`apqp-feas-sign-${i}`}>
                <input className={field} disabled={!canEdit} placeholder="Name" value={s.name || ""} onChange={(e) => setSign(i, "name", e.target.value)} />
                <input className={field} disabled={!canEdit} placeholder="Title" value={s.title || ""} onChange={(e) => setSign(i, "title", e.target.value)} />
                <input className={field} disabled={!canEdit} placeholder="Date" value={s.date || ""} onChange={(e) => setSign(i, "date", e.target.value)} />
                {canEdit && <button onClick={() => removeSign(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className={lbl}>Comments</div>
          <textarea className={field} rows={2} disabled={!canEdit} value={f.comments || ""} onChange={(e) => set("comments", e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// Editable Risk Assessment Sheet form (ENGG/F/06) for the "Risk Assessment"
// deliverable — auto-generates the company Excel and satisfies the mandatory deliverable.
function RiskAssessmentFormSection({ apqpId, task, canEdit, onSaved }) {
  const [ra, setRa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment`)
      .then((r) => { if (alive) setRa(r.data.risk_assessment); })
      .catch(() => { if (alive) setRa(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setRa((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setRa((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setMember = (i, v) => setRa((c) => ({ ...c, cft_members: c.cft_members.map((x, j) => (j === i ? { ...x, name: v } : x)) }));
  const addMember = () => setRa((c) => ({ ...c, cft_members: [...(c.cft_members || []), { name: "" }] }));
  const removeMember = (i) => setRa((c) => ({ ...c, cft_members: c.cft_members.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment`, ra);
      setRa(r.data.risk_assessment);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Risk Assessment Sheet.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Risk Assessment Sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment`);
      setRa(r2.data.risk_assessment);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Risk Assessment sheet…</div>;
  if (!ra) return null;

  const field = "w-full border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const LEVELS = [["RL", "bg-emerald-500 border-emerald-500"], ["RM", "bg-amber-500 border-amber-500"], ["RH", "bg-red-500 border-red-500"]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-ra-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Risk Assessment Sheet <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG/F/06</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/risk-assessment/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-ra-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-ra-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-ra-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-ra-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Customer Name</div><input className={field} disabled={!canEdit} value={ra.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><div className={lbl}>Product name &amp; no.</div><input className={field} disabled={!canEdit} value={ra.product_name || ""} onChange={(e) => set("product_name", e.target.value)} /></div>
          <div><div className={lbl}>Customer Drg. No.</div><input className={field} disabled={!canEdit} value={ra.customer_drg_no || ""} onChange={(e) => set("customer_drg_no", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={ra.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
        </div>

        <div>
          <div className={lbl}>Risk Analysis</div>
          <div className="border border-slate-200 rounded divide-y divide-slate-100">
            {(ra.rows || []).map((row, i) => (
              <div key={i} className="px-2 py-1.5" data-testid={`apqp-ra-row-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="text-xs font-semibold text-slate-500 w-4 pt-0.5">{row.sn}</div>
                  <div className="text-xs font-medium text-slate-700 flex-1">{row.analysis}</div>
                  <div className="flex gap-1 shrink-0">
                    {LEVELS.map(([lv, color]) => (
                      <button key={lv} type="button" disabled={!canEdit} onClick={() => setRow(i, "level", lv)}
                        className={`px-1.5 py-0.5 text-[10px] rounded border ${(row.level || "").toUpperCase() === lv ? `${color} text-white` : "border-slate-300 text-slate-500 hover:bg-slate-50"} disabled:opacity-60`}>
                        {lv}
                      </button>
                    ))}
                  </div>
                </div>
                {row.criteria && <div className="text-[10px] text-slate-400 mt-0.5 ml-6 whitespace-pre-line">{row.criteria}</div>}
                <div className="ml-6 mt-1">
                  <input className={field} disabled={!canEdit} placeholder="Action plan with target date and responsibility"
                    value={row.action_plan || ""} onChange={(e) => setRow(i, "action_plan", e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className={lbl}>Signature CFT Members</div>
              {canEdit && <button onClick={addMember} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add</button>}
            </div>
            <div className="space-y-1">
              {(ra.cft_members || []).map((m, i) => (
                <div key={i} className="flex items-center gap-1" data-testid={`apqp-ra-member-${i}`}>
                  <span className="text-[10px] text-slate-400 w-4">{i + 1}</span>
                  <input className={field} disabled={!canEdit} value={m.name || ""} onChange={(e) => setMember(i, e.target.value)} />
                  {canEdit && <button onClick={() => removeMember(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className={lbl}>Comments</div>
            <div className="space-y-1 pt-1">
              {[["viable", "Viable"], ["not_viable", "Not Viable"], ["viable_with_change", "Viable with change"]].map(([code, label]) => (
                <label key={code} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input type="radio" name={`ra-comments-${task.id}`} disabled={!canEdit} checked={ra.comments === code} onChange={() => set("comments", code)} />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable Resource Planning & Budget form (Engg-F-46) for the "Risk Assessment &
// Resource Planning" task — auto-generates the company Excel, satisfies the deliverables.
function ResourcePlanningFormSection({ apqpId, task, canEdit, onSaved }) {
  const [rp, setRp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/resource-planning`)
      .then((r) => { if (alive) setRp(r.data.resource_planning); })
      .catch(() => { if (alive) setRp(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setRp((c) => ({ ...c, [k]: v }));
  const setItem = (i, k, v) => setRp((c) => ({ ...c, items: c.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/resource-planning`, rp);
      setRp(r.data.resource_planning);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/resource-planning.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Resource Planning & Budget.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Resource Planning form and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/resource-planning`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/resource-planning`);
      setRp(r2.data.resource_planning);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Resource Planning form…</div>;
  if (!rp) return null;

  const field = "w-full border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const Toggle = ({ active, on, color, children }) => (
    <button type="button" disabled={!canEdit} onClick={on}
      className={`px-1.5 py-0.5 text-[10px] rounded border ${active ? `${color} text-white` : "border-slate-300 text-slate-500 hover:bg-slate-50"} disabled:opacity-60`}>
      {children}
    </button>
  );

  const items = rp.items || [];
  const rows = [];
  let lastCat = null;
  items.forEach((it, i) => {
    if (it.category !== lastCat) {
      rows.push(<tr key={`cat-${i}`}><td colSpan={6} className="pt-2 pb-0.5 text-xs font-bold text-slate-700">{it.category}</td></tr>);
      lastCat = it.category;
    }
    rows.push(
      <tr key={i} className="align-top" data-testid={`apqp-rp-item-${i}`}>
        <td className="py-0.5 pr-1 text-xs text-slate-700 w-[38%]">{it.checkpoint}</td>
        <td className="py-0.5 pr-1">
          <div className="flex gap-1">
            <Toggle active={(it.required || "").toLowerCase() === "yes"} on={() => setItem(i, "required", "Yes")} color="bg-emerald-500 border-emerald-500">Yes</Toggle>
            <Toggle active={(it.required || "").toLowerCase() === "no"} on={() => setItem(i, "required", "No")} color="bg-slate-500 border-slate-500">No</Toggle>
          </div>
        </td>
        <td className="py-0.5 pr-1">
          <div className="flex gap-1">
            <Toggle active={(it.risk || "").toLowerCase() === "low"} on={() => setItem(i, "risk", "low")} color="bg-emerald-500 border-emerald-500">low</Toggle>
            <Toggle active={(it.risk || "").toLowerCase() === "high"} on={() => setItem(i, "risk", "high")} color="bg-red-500 border-red-500">high</Toggle>
          </div>
        </td>
        <td className="py-0.5 pr-1"><input className={field} disabled={!canEdit} placeholder="Remarks" value={it.remarks || ""} onChange={(e) => setItem(i, "remarks", e.target.value)} /></td>
        <td className="py-0.5 pr-1 w-16"><input className={field} disabled={!canEdit} placeholder="Budget" value={it.budget || ""} onChange={(e) => setItem(i, "budget", e.target.value)} /></td>
        <td className="py-0.5 w-16"><input className={field} disabled={!canEdit} placeholder="Qty" value={it.quantity || ""} onChange={(e) => setItem(i, "quantity", e.target.value)} /></td>
      </tr>
    );
  });

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-rp-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Resource Planning &amp; Budget <span className="text-[10px] font-normal text-slate-400 font-mono">Engg-F-46</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/resource-planning/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-rp-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-rp-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-rp-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-rp-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Part Name</div><input className={field} disabled={!canEdit} value={rp.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={field} disabled={!canEdit} value={rp.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Part No</div><input className={field} disabled={!canEdit} value={rp.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Model</div><input className={field} disabled={!canEdit} value={rp.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={rp.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
          <div>
            <div className={lbl}>Type</div>
            <div className="flex items-center gap-3 text-xs text-slate-700 pt-1">
              <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`rp-type-${task.id}`} disabled={!canEdit} checked={(rp.type || "new_product") === "new_product"} onChange={() => set("type", "new_product")} /> New Product</label>
              <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`rp-type-${task.id}`} disabled={!canEdit} checked={rp.type === "product_change"} onChange={() => set("type", "product_change")} /> Product Change</label>
            </div>
          </div>
        </div>

        <div>
          <div className={lbl}>Risk Assessment Check Points</div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="text-left pb-1">Check point</th>
                <th className="text-left pb-1 w-24">Required</th>
                <th className="text-left pb-1 w-24">Risk</th>
                <th className="text-left pb-1">Remarks</th>
                <th className="text-left pb-1 w-16">Budget</th>
                <th className="text-left pb-1 w-16">Qty</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Reusable editors for the (large) Specification Review Sheet form.
const _SR_FIELD = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
const _SR_LBL = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

function SrStrList({ label, items, onChange, canEdit, placeholder }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className={_SR_LBL}>{label}</div>
        {canEdit && <button onClick={() => onChange([...(items || []), ""])} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add</button>}
      </div>
      <div className="space-y-1">
        {(items || []).map((v, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400 w-4">{i + 1}</span>
            <input className={_SR_FIELD} disabled={!canEdit} value={v || ""} placeholder={placeholder}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
            {canEdit && <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700"><X size={12} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SrRowTable({ label, columns, items, onChange, canEdit, blank }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className={_SR_LBL}>{label}</div>
        {canEdit && <button onClick={() => onChange([...(items || []), { ...blank }])} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="text-left pb-1 w-6">#</th>
              {columns.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}
              {canEdit && <th className="w-6"></th>}
            </tr>
          </thead>
          <tbody>
            {(items || []).map((row, i) => (
              <tr key={i} className="align-top">
                <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                {columns.map(([k, , w]) => (
                  <td key={k} className="px-0.5 py-0.5">
                    <input className={`${_SR_FIELD} ${w || ""}`} disabled={!canEdit} value={row[k] || ""}
                      onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />
                  </td>
                ))}
                {canEdit && <td className="text-center pt-1.5"><button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpecReviewFormSection({ apqpId, task, canEdit, onSaved }) {
  const [sr, setSr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/spec-review`)
      .then((r) => { if (alive) setSr(r.data.spec_review); })
      .catch(() => { if (alive) setSr(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setSr((c) => ({ ...c, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/spec-review`, sr);
      setSr(r.data.spec_review);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spec-review.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Specification Review Sheet.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Specification Review Sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/spec-review`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spec-review`);
      setSr(r2.data.spec_review);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Specification Review Sheet…</div>;
  if (!sr) return null;

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-sr-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Specification Review Sheet
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/spec-review/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-sr-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-sr-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-sr-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-sr-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-4">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={_SR_LBL}>Part No.</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Part Name</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Rev. No</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.rev_no || ""} onChange={(e) => set("rev_no", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Date</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Supplier Name</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Supplier Location</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.supplier_location || ""} onChange={(e) => set("supplier_location", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Meeting Date</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.meeting_date || ""} onChange={(e) => set("meeting_date", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Location of Meeting</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.meeting_location || ""} onChange={(e) => set("meeting_location", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Related Standards</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.related_standards || ""} onChange={(e) => set("related_standards", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Availability Status</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.availability_status || ""} onChange={(e) => set("availability_status", e.target.value)} /></div>
          <div className="col-span-2"><div className={_SR_LBL}>Remarks</div><input className={_SR_FIELD} disabled={!canEdit} value={sr.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <SrStrList label="Present Supplier (Dept.)" items={sr.present_supplier} onChange={(v) => set("present_supplier", v)} canEdit={canEdit} placeholder="Name" />
          <SrStrList label="Present Advik (Dept.)" items={sr.present_advik} onChange={(v) => set("present_advik", v)} canEdit={canEdit} placeholder="Name" />
        </div>

        <SrRowTable label="1. Critical dimensions for fitment & function" canEdit={canEdit}
          columns={[["dimension", "Dimension", "min-w-[100px]"], ["control_spec", "Control Specification", "min-w-[120px]"], ["remarks", "Remarks", "min-w-[120px]"]]}
          items={sr.critical_dims} onChange={(v) => set("critical_dims", v)} blank={{ dimension: "", control_spec: "", remarks: "" }} />

        <SrRowTable label="3. Advik Specific Requirement (tests)" canEdit={canEdit}
          columns={[["test_name", "Test name", "min-w-[90px]"], ["control_spec", "Control Specification", "min-w-[140px]"], ["remarks", "Remarks", "min-w-[120px]"]]}
          items={sr.tests} onChange={(v) => set("tests", v)} blank={{ test_name: "", control_spec: "", remarks: "" }} />

        <SrRowTable label="4. Raw Material Source / Sub Supplier" canEdit={canEdit}
          columns={[["part_name", "Part Name", "w-24"], ["rm_spec", "RM Spec/grade", "w-24"], ["rm_source", "RM Source", "min-w-[100px]"], ["local_imported", "Local/Imp.", "w-20"], ["remarks", "Remarks", "min-w-[100px]"]]}
          items={sr.rm_sources} onChange={(v) => set("rm_sources", v)} blank={{ part_name: "", rm_spec: "", rm_source: "", local_imported: "", remarks: "" }} />

        <SrStrList label="Operation Description" items={sr.operations} onChange={(v) => set("operations", v)} canEdit={canEdit} placeholder="Operation" />

        <SrRowTable label="2. Development plan (die/mould/design/testing/QA)" canEdit={canEdit}
          columns={[["activity", "Activity Name", "min-w-[110px]"], ["source", "Source", "w-24"], ["responsibility", "Responsibility", "w-28"], ["trg_date", "Trg date", "w-24"], ["status", "Status", "w-20"]]}
          items={sr.development_plan} onChange={(v) => set("development_plan", v)} blank={{ activity: "", source: "", responsibility: "", trg_date: "", status: "" }} />

        <SrRowTable label="6. Fixture / Checking Aids Details" canEdit={canEdit}
          columns={[["name", "Name", "min-w-[110px]"], ["make", "Make", "w-20"], ["source", "Source", "w-24"], ["target_date", "Target date", "w-24"], ["action_plan", "Action plan", "min-w-[90px]"]]}
          items={sr.fixtures} onChange={(v) => set("fixtures", v)} blank={{ name: "", make: "", source: "", target_date: "", action_plan: "" }} />

        <SrStrList label="7. Documents to be submitted (with Samples)" items={sr.documents} onChange={(v) => set("documents", v)} canEdit={canEdit} placeholder="Document name" />

        <SrRowTable label="8. Supplier Capability (filled by Advik)" canEdit={canEdit}
          columns={[["point", "Point to verify", "min-w-[220px]"], ["status", "Status", "w-16"], ["trg_date", "Trg date", "w-24"]]}
          items={sr.capability} onChange={(v) => set("capability", v)} blank={{ point: "", status: "", trg_date: "" }} />
      </div>
    </div>
  );
}

// Editable Process Qualification / Validation Sheet (ENGG-F-43), Phase 4.
const PV_TRIALS = ["T0", "T1", "OTOP", "Pilot", "T5", "T6", "T7", "T8", "T9", "T10"];
function ProcessValidationFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/process-validation`)
      .then((r) => { if (alive) setD(r.data.process_validation); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setObs = (i, oi, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => { if (j !== i) return x; const o = [...(x.observations || [])]; while (o.length < 10) o.push(""); o[oi] = v; return { ...x, observations: o }; }) }));
  const setQty = (oi, v) => setD((c) => { const q = [...(c.qty_produced || [])]; while (q.length < 10) q.push(""); q[oi] = v; return { ...c, qty_produced: q }; });
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { parameter: "", checking_method: "", observations: Array(10).fill("") }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/process-validation`, d);
      setD(r.data.process_validation);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/process-validation.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Process Validation.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Process Validation sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/process-validation`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/process-validation`);
      setD(r2.data.process_validation);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Process Validation…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pv-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Process Qualification / Validation <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-43</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/process-validation/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pv-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pv-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pv-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pv-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Model</div><input className={fld} disabled={!canEdit} value={d.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Date &amp; Time</div><input className={fld} disabled={!canEdit} value={d.date_time || ""} onChange={(e) => set("date_time", e.target.value)} /></div>
          <div><div className={lbl}>Process Name / No.</div><input className={fld} disabled={!canEdit} value={d.process_name || ""} onChange={(e) => set("process_name", e.target.value)} /></div>
          <div><div className={lbl}>Machine Name / No.</div><input className={fld} disabled={!canEdit} value={d.machine || ""} onChange={(e) => set("machine", e.target.value)} /></div>
          <div><div className={lbl}>Operator</div><input className={fld} disabled={!canEdit} value={d.operator || ""} onChange={(e) => set("operator", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Parameters &amp; Setting Observations (10 trials)</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="w-6"></th>
                <th className="text-left pb-1 px-0.5 min-w-[140px]">Parameter</th>
                <th className="text-left pb-1 px-0.5 min-w-[110px]">Checking Method</th>
                {PV_TRIALS.map((t) => <th key={t} className="text-center pb-1 px-0.5 w-16">{t}</th>)}
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[140px]`} disabled={!canEdit} value={row.parameter || ""} onChange={(e) => setRow(i, "parameter", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[110px]`} disabled={!canEdit} value={row.checking_method || ""} onChange={(e) => setRow(i, "checking_method", e.target.value)} /></td>
                  {PV_TRIALS.map((_, oi) => (
                    <td key={oi} className="px-0.5 py-0.5"><input className={`${fld} w-16`} disabled={!canEdit} value={(row.observations || [])[oi] || ""} onChange={(e) => setObs(i, oi, e.target.value)} /></td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
              <tr className="align-top bg-slate-50">
                <td></td>
                <td colSpan={2} className="px-0.5 py-1 text-[11px] font-semibold text-slate-600">Trial-wise Qty. Produced</td>
                {PV_TRIALS.map((_, oi) => (
                  <td key={oi} className="px-0.5 py-0.5"><input className={`${fld} w-16`} disabled={!canEdit} value={(d.qty_produced || [])[oi] || ""} onChange={(e) => setQty(oi, e.target.value)} /></td>
                ))}
                {canEdit && <td></td>}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Result</div><input className={fld} disabled={!canEdit} value={d.result || ""} onChange={(e) => set("result", e.target.value)} /></div>
          <div><div className={lbl}>Remarks / Comments</div><input className={fld} disabled={!canEdit} value={d.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></div>
          <div><div className={lbl}>Process Validated By</div><input className={fld} disabled={!canEdit} value={d.validated_by || ""} onChange={(e) => set("validated_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable APQP Summary and Sign Off (ENGG-F-24), Phase 5.
const SS_STATUS = [["required", "Required"], ["not_required", "Not Required"], ["", "—"]];
function SummarySignoffFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff`)
      .then((r) => { if (alive) setD(r.data.summary_signoff); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setItem = (grp, i, k, v) => setD((c) => ({ ...c, [grp]: c[grp].map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addItem = (grp) => setD((c) => ({ ...c, [grp]: [...(c[grp] || []), { item: "", status: "required", target: "", actual: "", remarks: "" }] }));
  const removeItem = (grp, i) => setD((c) => ({ ...c, [grp]: c[grp].filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff`, d);
      setD(r.data.summary_signoff);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "APQP Summary and Sign Off.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the APQP Summary Sign-Off and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff`);
      setD(r2.data.summary_signoff);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading APQP Summary Sign-Off…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  const ItemTable = ({ grp, title }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-semibold text-slate-600">{title}</div>
        {canEdit && <button onClick={() => addItem(grp)} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add item</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-400">
            <tr><th className="w-6"></th><th className="text-left pb-1 px-0.5 min-w-[240px]">Item</th><th className="text-left pb-1 px-0.5 w-28">Status</th><th className="text-left pb-1 px-0.5 w-20">Target</th><th className="text-left pb-1 px-0.5 w-20">Actual</th><th className="text-left pb-1 px-0.5 min-w-[120px]">Remarks</th>{canEdit && <th className="w-6"></th>}</tr>
          </thead>
          <tbody>
            {(d[grp] || []).map((it, i) => (
              <tr key={i} className="align-top">
                <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={it.item || ""} onChange={(e) => setItem(grp, i, "item", e.target.value)} /></td>
                <td className="px-0.5 py-0.5">
                  <select className={`${fld} w-28`} disabled={!canEdit} value={it.status || ""} onChange={(e) => setItem(grp, i, "status", e.target.value)}>
                    {SS_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="px-0.5 py-0.5"><input className={`${fld} w-20`} disabled={!canEdit} value={it.target || ""} onChange={(e) => setItem(grp, i, "target", e.target.value)} /></td>
                <td className="px-0.5 py-0.5"><input className={`${fld} w-20`} disabled={!canEdit} value={it.actual || ""} onChange={(e) => setItem(grp, i, "actual", e.target.value)} /></td>
                <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={it.remarks || ""} onChange={(e) => setItem(grp, i, "remarks", e.target.value)} /></td>
                {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeItem(grp, i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-ss-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> APQP Summary and Sign Off <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-24</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/summary-signoff/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-ss-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-ss-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-ss-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-ss-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part Number</div><input className={fld} disabled={!canEdit} value={d.part_number || ""} onChange={(e) => set("part_number", e.target.value)} /></div>
          <div><div className={lbl}>IPC Start Date</div><input className={fld} disabled={!canEdit} value={d.ipc_start_date || ""} onChange={(e) => set("ipc_start_date", e.target.value)} /></div>
          <div><div className={lbl}>IPC Target End Date</div><input className={fld} disabled={!canEdit} value={d.ipc_target_end_date || ""} onChange={(e) => set("ipc_target_end_date", e.target.value)} /></div>
        </div>

        {ItemTable({ grp: "production_items", title: "Item List given to Production" })}
        {ItemTable({ grp: "quality_items", title: "Item List given to Quality" })}
        <div className="text-[10px] text-slate-400">The Mass-Production Transition declaration signature block (Maintenance, NPD/Plant/Quality/Production Head, Purchase, Store &amp; Dispatch, Top Management) is included in the generated Excel for physical sign-off.</div>
      </div>
    </div>
  );
}

// Editable Things Gone Right / Things Gone Wrong (TGR/TGW, Engg-F-55), Phase 5.
const TGR_COLS = [
  ["operation_name", "Operation Name", "min-w-[140px]"], ["tgr", "Things Gone Right", "min-w-[130px]"],
  ["tgw", "Things Gone Wrong", "min-w-[140px]"], ["action_taken", "Action Taken", "min-w-[130px]"],
  ["resp", "Resp.", "w-24"], ["target_date", "Target Date", "w-24"],
  ["review_status", "Review Status", "w-24"], ["remarks", "Remarks", "min-w-[110px]"],
];
function TgrTgwFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw`)
      .then((r) => { if (alive) setD(r.data.tgr_tgw); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { operation_name: "", tgr: "OK", tgw: "", action_taken: "", resp: "", target_date: "", review_status: "", remarks: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw`, d);
      setD(r.data.tgr_tgw);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "TGR TGW.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the TGR/TGW sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw`);
      setD(r2.data.tgr_tgw);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading TGR/TGW…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-tgr-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Things Gone Right / Wrong (TGR/TGW) <span className="text-[10px] font-normal text-slate-400 font-mono">Engg-F-55</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/tgr-tgw/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-tgr-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-tgr-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-tgr-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-tgr-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Core Team</div><input className={fld} disabled={!canEdit} value={d.core_team || ""} onChange={(e) => set("core_team", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Die Setter</div><input className={fld} disabled={!canEdit} value={d.die_setter || ""} onChange={(e) => set("die_setter", e.target.value)} /></div>
          <div><div className={lbl}>Die Fitter</div><input className={fld} disabled={!canEdit} value={d.die_fitter || ""} onChange={(e) => set("die_fitter", e.target.value)} /></div>
          <div><div className={lbl}>Machine No. / Tonnage</div><input className={fld} disabled={!canEdit} value={d.machine_tonnage || ""} onChange={(e) => set("machine_tonnage", e.target.value)} /></div>
          <div><div className={lbl}>Shift</div><input className={fld} disabled={!canEdit} value={d.shift || ""} onChange={(e) => set("shift", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Operations — TGR / TGW</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="w-6"></th>{TGR_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  {TGR_COLS.map(([k, , w]) => (
                    <td key={k} className="px-0.5 py-0.5"><input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} /></td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Process Capability Study (Marelli, Attachment 08) — 2nd SPC format, Phase 4.
function ProcessCapabilityFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/process-capability`)
      .then((r) => { if (alive) setD(r.data.process_capability); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setDim = (i, k, v) => setD((c) => ({ ...c, dimensions: c.dimensions.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setReadingsText = (i, text) => setD((c) => ({ ...c, dimensions: c.dimensions.map((x, j) => (j === i ? { ...x, _readings_text: text } : x)) }));
  const addDim = () => setD((c) => ({ ...c, dimensions: [...(c.dimensions || []), { measuring_equipment: "", nominal: "", upper_limit: "", lower_limit: "", readings: [] }] }));
  const removeDim = (i) => setD((c) => ({ ...c, dimensions: c.dimensions.filter((_, j) => j !== i) }));

  const readingsText = (dim) => (dim._readings_text != null ? dim._readings_text : (dim.readings || []).join(" "));

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...d,
        dimensions: (d.dimensions || []).map((dim) => {
          const txt = readingsText(dim);
          const readings = txt.split(/[\s,]+/).filter((x) => x !== "").map((x) => parseFloat(x)).filter((x) => !isNaN(x));
          const { _readings_text, ...rest } = dim;
          return { ...rest, readings };
        }),
      };
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/process-capability`, payload);
      setD(r.data.process_capability);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/process-capability.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Process Capability Study.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Process Capability Study and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      await api.delete(`/apqp/${apqpId}/tasks/${task.id}/process-capability`);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/process-capability`);
      setD(r2.data.process_capability);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Process Capability Study…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pcap-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Process Capability Study <span className="text-[10px] font-normal text-slate-400 font-mono">Attachment 08</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/process-capability/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pcap-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pcap-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pcap-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pcap-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Supplier Name</div><input className={fld} disabled={!canEdit} value={d.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} /></div>
          <div><div className={lbl}>Supplier Code</div><input className={fld} disabled={!canEdit} value={d.supplier_code || ""} onChange={(e) => set("supplier_code", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Supplier Plant</div><input className={fld} disabled={!canEdit} value={d.supplier_plant || ""} onChange={(e) => set("supplier_plant", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No. &amp; Issue Level</div><input className={fld} disabled={!canEdit} value={d.part_no_issue || ""} onChange={(e) => set("part_no_issue", e.target.value)} /></div>
          <div><div className={lbl}>Production Date / Lot</div><input className={fld} disabled={!canEdit} value={d.production_date_lot || ""} onChange={(e) => set("production_date_lot", e.target.value)} /></div>
          <div><div className={lbl}>Cavity No.</div><input className={fld} disabled={!canEdit} value={d.cavity_no || ""} onChange={(e) => set("cavity_no", e.target.value)} /></div>
          <div><div className={lbl}>Author</div><input className={fld} disabled={!canEdit} value={d.author || ""} onChange={(e) => set("author", e.target.value)} /></div>
          <div><div className={lbl}>Milestone</div><input className={fld} disabled={!canEdit} value={d.milestone || ""} onChange={(e) => set("milestone", e.target.value)} /></div>
          <div><div className={lbl}>Design Note No.</div><input className={fld} disabled={!canEdit} value={d.design_note || ""} onChange={(e) => set("design_note", e.target.value)} /></div>
          <div><div className={lbl}>E-mail</div><input className={fld} disabled={!canEdit} value={d.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Dimensions (Cp/Cpk computed automatically per dimension)</div>
          {canEdit && <button onClick={addDim} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add dimension</button>}
        </div>
        <div className="space-y-2">
          {(d.dimensions || []).map((dim, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-2 bg-slate-50/40">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-semibold text-slate-600">Dimension {i + 1}</div>
                {canEdit && d.dimensions.length > 1 && <button onClick={() => removeDim(i)} className="inline-flex items-center gap-1 text-[11px] text-red-500 hover:underline"><Trash2 size={11} /> Remove</button>}
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div><div className={lbl}>Measuring Equipment</div><input className={fld} disabled={!canEdit} value={dim.measuring_equipment || ""} onChange={(e) => setDim(i, "measuring_equipment", e.target.value)} /></div>
                <div><div className={lbl}>Dimension (Nominal)</div><input className={fld} disabled={!canEdit} value={dim.nominal || ""} onChange={(e) => setDim(i, "nominal", e.target.value)} /></div>
                <div><div className={lbl}>Upper Limit (tol)</div><input className={fld} disabled={!canEdit} value={dim.upper_limit || ""} onChange={(e) => setDim(i, "upper_limit", e.target.value)} /></div>
                <div><div className={lbl}>Lower Limit (tol)</div><input className={fld} disabled={!canEdit} value={dim.lower_limit || ""} onChange={(e) => setDim(i, "lower_limit", e.target.value)} /></div>
              </div>
              <div className="mt-1">
                <div className={lbl}>Readings ({(readingsText(dim).split(/[\s,]+/).filter((x) => x !== "").length)} values — space or comma separated)</div>
                <textarea rows={2} className={fld} disabled={!canEdit} value={readingsText(dim)} onChange={(e) => setReadingsText(i, e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Name</div><input className={fld} disabled={!canEdit} value={d.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><div className={lbl}>Position</div><input className={fld} disabled={!canEdit} value={d.position || ""} onChange={(e) => set("position", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable SPC (Pp & Ppk) capability study, Phase 4.
function SpcPpkFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk`)
      .then((r) => { if (alive) setD(r.data.spc_ppk); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setReading = (ri, ci, v) => setD((c) => ({ ...c, readings: c.readings.map((row, r) => r === ri ? row.map((x, k) => (k === ci ? v : x)) : row) }));
  // Resize the data-collection grid to (rows readings × cols samples), keeping existing values.
  const resizeGrid = (rows, cols) => {
    const R = Math.max(1, Math.min(200, parseInt(rows, 10) || 1));
    const C = Math.max(1, Math.min(200, parseInt(cols, 10) || 1));
    setD((c) => {
      const cur = c.readings || [];
      const next = Array.from({ length: R }, (_, r) => Array.from({ length: C }, (_, k) => (cur[r] && cur[r][k] != null ? cur[r][k] : "")));
      return { ...c, readings: next };
    });
  };

  async function save() {
    setSaving(true);
    try {
      const payload = { ...d, usl: parseFloat(d.usl), lsl: parseFloat(d.lsl), nominal: parseFloat(d.nominal),
        readings: (d.readings || []).map((row) => row.map((v) => (v === "" || v == null ? null : parseFloat(v)))) };
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk`, payload);
      setD(r.data.spc_ppk);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "SPC (Pp & Ppk).xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the SPC (Pp & Ppk) study and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk`);
      setD(r2.data.spc_ppk);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading SPC (Pp & Ppk)…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const nCols = (d.readings?.[0] || []).length || 10;

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-spcppk-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> SPC Study — Pp &amp; Ppk
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/spc-ppk/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-spcppk-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-spcppk-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-spcppk-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-spcppk-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Instrument</div><input className={fld} disabled={!canEdit} value={d.instrument || ""} onChange={(e) => set("instrument", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Specification</div><input className={fld} disabled={!canEdit} value={d.specific || ""} onChange={(e) => set("specific", e.target.value)} /></div>
          <div><div className={lbl}>Operation</div><input className={fld} disabled={!canEdit} value={d.operation || ""} onChange={(e) => set("operation", e.target.value)} /></div>
          <div><div className={lbl}>Least Count</div><input className={fld} disabled={!canEdit} value={d.l_count || ""} onChange={(e) => set("l_count", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>USL</div><input className={fld} disabled={!canEdit} value={d.usl ?? ""} onChange={(e) => set("usl", e.target.value)} /></div>
          <div><div className={lbl}>LSL</div><input className={fld} disabled={!canEdit} value={d.lsl ?? ""} onChange={(e) => set("lsl", e.target.value)} /></div>
          <div><div className={lbl}>Nominal (Design Centre)</div><input className={fld} disabled={!canEdit} value={d.nominal ?? ""} onChange={(e) => set("nominal", e.target.value)} /></div>
          <div><div className={lbl}>No. of classes</div><input type="number" className={fld} disabled={!canEdit} value={d.classes ?? 5} onChange={(e) => set("classes", parseInt(e.target.value, 10) || 5)} /></div>
        </div>

        <div className="text-[10px] text-slate-400">Enter the {(d.readings || []).length} × {nCols} readings (rows = subgroup readings, columns = 10 samples). Xbar/Rbar, control limits, histogram, X̄ &amp; R charts, Pp, Ppk and the conclusion are computed automatically in the Excel.</div>

        <div className="flex flex-wrap items-center gap-2">
          <div className={lbl}>Data collection ({(d.readings || []).length} readings × {nCols} samples)</div>
          {canEdit && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-auto" title="Resize the grid — e.g. 19 readings × 100 samples">
              <span className="text-slate-400">Grid:</span>
              <input type="number" min="1" max="200" value={(d.readings || []).length}
                onChange={(e) => resizeGrid(e.target.value, nCols)}
                className="w-14 border border-slate-300 rounded px-1 py-0.5 text-center" data-testid="spcppk-rows" />
              <span className="text-slate-400">readings ×</span>
              <input type="number" min="1" max="200" value={nCols}
                onChange={(e) => resizeGrid((d.readings || []).length, e.target.value)}
                className="w-14 border border-slate-300 rounded px-1 py-0.5 text-center" data-testid="spcppk-cols" />
              <span className="text-slate-400">samples</span>
            </div>
          )}
        </div>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="text-left pb-1 px-0.5 w-12">Sr.No</th>{Array.from({ length: nCols }).map((_, c) => <th key={c} className="text-center pb-1 px-0.5 w-16">{c + 1}</th>)}</tr>
            </thead>
            <tbody>
              {(d.readings || []).map((row, ri) => (
                <tr key={ri}>
                  <td className="text-slate-500 pr-1 pt-1.5 font-medium">{ri + 1}</td>
                  {row.map((v, ci) => (
                    <td key={ci} className="px-0.5 py-0.5"><input className={`${fld} w-16`} disabled={!canEdit} value={v ?? ""} onChange={(e) => setReading(ri, ci, e.target.value)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Reviewed By</div><input className={fld} disabled={!canEdit} value={d.reviewed_by || ""} onChange={(e) => set("reviewed_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable PPAP Index Sheet, Phase 4.
const PPAP_STATUS = [["yes", "Yes"], ["no", "No"], ["na", "N/A"]];
function PpapFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/ppap`)
      .then((r) => { if (alive) setD(r.data.ppap); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    // Pull every document uploaded/generated across all the project's tasks.
    api.get(`/apqp/${apqpId}/documents`)
      .then((r) => { if (alive) setDocs(r.data.documents || []); })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setDocsLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const viewDoc = (doc) => {
    if (!doc.url) return;
    const target = doc.is_office ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.url)}` : doc.url;
    window.open(target, "_blank", "noopener,noreferrer");
  };
  const downloadAllDocs = () => {
    window.open(`${api.defaults.baseURL}/apqp/${apqpId}/documents.zip`, "_blank");
  };

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setEl = (i, k, v) => setD((c) => ({ ...c, elements: c.elements.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addEl = () => setD((c) => ({ ...c, elements: [...(c.elements || []), { element: "", document: "", status: "yes", remarks: "" }] }));
  const removeEl = (i) => setD((c) => ({ ...c, elements: c.elements.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/ppap`, d);
      setD(r.data.ppap);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/ppap.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "PPAP Index.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the PPAP Index and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/ppap`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/ppap`);
      setD(r2.data.ppap);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading PPAP Index…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-ppap-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> PPAP — Index Sheet
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/ppap/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-ppap-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-ppap-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-ppap-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-ppap-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Supplier</div><input className={fld} disabled={!canEdit} value={d.supplier || ""} onChange={(e) => set("supplier", e.target.value)} /></div>
          <div><div className={lbl}>Submission Level</div>
            <select className={fld} disabled={!canEdit} value={d.submission_level || "3"} onChange={(e) => set("submission_level", e.target.value)}>
              {["1", "2", "3", "4", "5"].map((n) => <option key={n} value={n}>Level {n}</option>)}
            </select>
          </div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.org_date || ""} onChange={(e) => set("org_date", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Reason for Submission</div><input className={fld} disabled={!canEdit} value={d.submission_reason || ""} onChange={(e) => set("submission_reason", e.target.value)} /></div>
          <div><div className={lbl}>PSW Disposition</div>
            <select className={fld} disabled={!canEdit} value={d.psw_disposition || ""} onChange={(e) => set("psw_disposition", e.target.value)}>
              <option value="">—</option><option value="approved">Approved</option><option value="interim">Interim Approval</option><option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>PPAP Elements</div>
          {canEdit && <button onClick={addEl} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add element</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="w-6"></th><th className="text-left pb-1 px-0.5 min-w-[220px]">Element</th><th className="text-left pb-1 px-0.5 min-w-[160px]">Document / Format</th><th className="text-left pb-1 px-0.5 w-20">Status</th><th className="text-left pb-1 px-0.5 min-w-[160px]">Remarks</th>{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.elements || []).map((el, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={el.element || ""} onChange={(e) => setEl(i, "element", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={el.document || ""} onChange={(e) => setEl(i, "document", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5">
                    <select className={`${fld} w-20`} disabled={!canEdit} value={el.status || ""} onChange={(e) => setEl(i, "status", e.target.value)}>
                      <option value="">—</option>{PPAP_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={el.remarks || ""} onChange={(e) => setEl(i, "remarks", e.target.value)} /></td>
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeEl(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ---- All project documents (auto-collected from every task) ---- */}
        <div className="border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="text-xs font-semibold text-slate-700">
              PPAP Package — All Documents <span className="text-slate-400 font-normal">({docs.length})</span>
            </div>
            <button onClick={downloadAllDocs} disabled={docs.length === 0}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-800 text-white rounded hover:bg-slate-900 disabled:opacity-40" data-testid="apqp-ppap-zip">
              <Download size={12} /> Download All (ZIP)
            </button>
          </div>
          {docsLoading ? (
            <div className="p-4 text-center text-xs text-slate-400">Loading documents…</div>
          ) : docs.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400">No documents uploaded/generated in the tasks yet. Upload or save the auto-forms in the other tasks and they’ll appear here automatically.</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {docs.map((doc, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`apqp-ppap-doc-${i}`}>
                  <Paperclip size={13} className="text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-slate-800" title={doc.file_name}>{doc.file_name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{doc.task_name} · {doc.deliverable_name}{doc.document_no ? ` · ${doc.document_no}` : ""}</div>
                  </div>
                  {doc.url && (
                    <>
                      <button onClick={() => viewDoc(doc)} className="text-slate-500 hover:text-orange-600 p-1" title="View"><Eye size={14} /></button>
                      <a href={doc.url} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="Download"><Download size={14} /></a>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Part Submission Warrant (PSW / AIAG), Phase 4.
function PswFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/psw`)
      .then((r) => { if (alive) setD(r.data.psw); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/psw`, d);
      setD(r.data.psw);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/psw.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Part Submission Warrant.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the PSW and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/psw`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/psw`);
      setD(r2.data.psw);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Part Submission Warrant…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const F = ({ k, label, area, cls }) => (
    <div className={cls}>
      <div className={lbl}>{label}</div>
      {area
        ? <textarea rows={area} className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />
        : <input className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />}
    </div>
  );
  const Sel = ({ k, label, opts, cls }) => (
    <div className={cls}>
      <div className={lbl}>{label}</div>
      <select className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)}>
        <option value="">—</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
  const Chk = ({ k, label }) => (
    <label className="flex items-center gap-1.5 text-xs text-slate-700">
      <input type="checkbox" disabled={!canEdit} checked={!!d[k]} onChange={(e) => set(k, e.target.checked)} className="accent-orange-600" /> {label}
    </label>
  );
  const ST = ({ children }) => <div className="text-[11px] font-semibold text-slate-600 border-b border-slate-100 pb-1 pt-1">{children}</div>;
  const YNNA = [["yes", "Yes"], ["no", "No"], ["na", "n/a"]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-psw-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Part Submission Warrant <span className="text-[10px] font-normal text-slate-400 font-mono">PSW</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/psw/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-psw-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-psw-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-psw-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-psw-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {ST({ children: "Part Information" })}
        <div className="grid grid-cols-4 gap-2">
          {F({ k: "part_name", label: "Part Name" })}
          {F({ k: "cust_part_number", label: "Cust. Part Number" })}
          {F({ k: "drawing_no", label: "Shown on Drawing No." })}
          {F({ k: "org_part_number", label: "Org. Part Number" })}
          {F({ k: "eng_change_level", label: "Engineering Change Level" })}
          {F({ k: "eng_change_dated", label: "Dated" })}
          {F({ k: "additional_eng_changes", label: "Additional Engineering Changes" })}
          {F({ k: "additional_dated", label: "Dated" })}
          {Sel({ k: "safety_govt", label: "Safety/Govt Regulation", opts: [["yes", "Yes"], ["no", "No"]] })}
          {F({ k: "purchase_order_no", label: "Purchase Order No." })}
          {F({ k: "weight", label: "Weight (gm)" })}
          {F({ k: "checking_aid_no", label: "Checking Aid No." })}
          {F({ k: "checking_aid_ecl", label: "Checking Aid Eng. Change Level" })}
          {F({ k: "checking_aid_dated", label: "Dated" })}
        </div>

        {ST({ children: "Organization Manufacturing / Customer Submittal" })}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            {F({ k: "org_name", label: "Organization Name & Supplier/Vendor Code" })}
            {F({ k: "org_street", label: "Street Address" })}
            <div className="grid grid-cols-4 gap-1">
              {F({ k: "org_city", label: "City" })}{F({ k: "org_region", label: "Region" })}
              {F({ k: "org_postal", label: "Postal" })}{F({ k: "org_country", label: "Country" })}
            </div>
          </div>
          <div className="space-y-2">
            {F({ k: "cust_name", label: "Customer Name" })}
            {F({ k: "cust_address", label: "Customer Address", area: 2 })}
            <div className="grid grid-cols-2 gap-1">
              {F({ k: "cust_name_division", label: "Customer Name/Division" })}
              {F({ k: "buyer_code", label: "Buyer/Buyer Code" })}
            </div>
            {F({ k: "application", label: "Application" })}
          </div>
        </div>

        {ST({ children: "Materials Reporting" })}
        <div className="grid grid-cols-3 gap-2">
          {Sel({ k: "substances_reported", label: "Substances of Concern reported?", opts: YNNA })}
          {F({ k: "imds_submitted", label: "Submitted by IMDS / customer format" })}
          {Sel({ k: "polymeric_iso", label: "Polymeric parts ISO marked?", opts: YNNA })}
        </div>

        {ST({ children: "Reason for Submission (check at least one)" })}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {Chk({ k: "reason_initial", label: "Initial Submission" })}
          {Chk({ k: "reason_optional_material", label: "Change to Optional Construction or Material" })}
          {Chk({ k: "reason_eng_change", label: "Engineering Change(s)" })}
          {Chk({ k: "reason_supplier_source", label: "Supplier or Material Source Change" })}
          {Chk({ k: "reason_tooling", label: "Tooling: Transfer / Replacement / Refurbishment / additional" })}
          {Chk({ k: "reason_part_processing", label: "Change in Part Processing" })}
          {Chk({ k: "reason_correction", label: "Correction of Discrepancy" })}
          {Chk({ k: "reason_additional_location", label: "Parts Produced at Additional Location" })}
          {Chk({ k: "reason_tooling_inactive", label: "Tooling Inactive > 1 year" })}
          {Chk({ k: "reason_other", label: "Other - please specify" })}
        </div>
        {F({ k: "reason_other_specify", label: "Other (specify)" })}

        {ST({ children: "Requested Submission Level" })}
        {Sel({ k: "submission_level", label: "Level (check one)", opts: [["1", "Level 1 — Warrant only"], ["2", "Level 2 — Warrant + limited data"], ["3", "Level 3 — Warrant + complete supporting data"], ["4", "Level 4 — as defined by customer"], ["5", "Level 5 — reviewed at manufacturing location"]] })}

        {ST({ children: "Submission Results" })}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {Chk({ k: "res_dimensional", label: "Dimensional measurements" })}
          {Chk({ k: "res_material_functional", label: "Material and functional tests" })}
          {Chk({ k: "res_appearance", label: "Appearance criteria" })}
          {Chk({ k: "res_statistical", label: "Statistical process package" })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Sel({ k: "meet_requirements", label: "Results meet all requirements?", opts: [["yes", "Yes"], ["no", "No"]] })}
          {F({ k: "mold_cavity_process", label: "Mold / Cavity / Production Process" })}
        </div>

        {ST({ children: "Declaration" })}
        <div className="grid grid-cols-4 gap-2">
          {F({ k: "production_rate_qty", label: "Production rate (qty)" })}
          {F({ k: "production_rate_hours", label: "per (hours)" })}
          {Sel({ k: "tool_tagged", label: "Customer tools tagged & numbered?", opts: YNNA })}
          {F({ k: "org_date", label: "Date" })}
          {F({ k: "explanation_comments", label: "Explanation / Comments", area: 2, cls: "col-span-4" })}
          {F({ k: "print_name", label: "Print Name" })}
          {F({ k: "phone_no", label: "Phone No." })}
          {F({ k: "fax_no", label: "Fax No." })}
          {F({ k: "title", label: "Title" })}
          {F({ k: "email", label: "E-mail", cls: "col-span-2" })}
        </div>

        {ST({ children: "For Customer Use Only" })}
        <div className="grid grid-cols-4 gap-2">
          {Sel({ k: "disposition", label: "Part Warrant Disposition", opts: [["approved", "Approved"], ["rejected", "Rejected"], ["other", "Other"]] })}
          {F({ k: "customer_print_name", label: "Customer Print Name" })}
          {F({ k: "customer_date", label: "Date" })}
          {F({ k: "customer_tracking", label: "Customer Tracking Number" })}
        </div>
      </div>
    </div>
  );
}

// Editable Pilot Lot — Setup Verification Report (QA-F-64), Phase 4.
const SVR_COLS = [
  ["parameter", "Parameter", "min-w-[110px]"], ["specification", "Specification", "min-w-[130px]"],
  ["inspection_method", "Insp. Method", "min-w-[110px]"], ["foa_1", "FOA 1", "w-14"], ["foa_2", "FOA 2", "w-14"],
  ["foa_judgement", "FOA Judg.", "w-16"], ["loa_1", "LOA 1", "w-14"], ["loa_2", "LOA 2", "w-14"], ["loa_judgement", "LOA Judg.", "w-16"],
];
function PilotLotFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot`)
      .then((r) => { if (alive) setD(r.data.pilot_lot); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setSetup = (si, k, v) => setD((c) => ({ ...c, setups: c.setups.map((s, j) => (j === si ? { ...s, [k]: v } : s)) }));
  const setParam = (si, grp, pi, k, v) => setD((c) => ({ ...c, setups: c.setups.map((s, j) => j === si ? { ...s, [grp]: s[grp].map((p, m) => m === pi ? { ...p, [k]: v } : p) } : s) }));
  const addParam = (si, grp) => setD((c) => ({ ...c, setups: c.setups.map((s, j) => j === si ? { ...s, [grp]: [...(s[grp] || []), { parameter: "", specification: "", inspection_method: "", foa_1: "", foa_2: "", foa_judgement: "", loa_1: "", loa_2: "", loa_judgement: "" }] } : s) }));
  const removeParam = (si, grp, pi) => setD((c) => ({ ...c, setups: c.setups.map((s, j) => j === si ? { ...s, [grp]: s[grp].filter((_, m) => m !== pi) } : s) }));
  const addSetup = () => setD((c) => ({ ...c, setups: [...(c.setups || []), { operation: "", machine_no: "", operator: "", setup_type: "normal", prepared_by: "", approved_by: "", product_params: [], process_params: [] }] }));
  const removeSetup = (si) => setD((c) => ({ ...c, setups: c.setups.filter((_, j) => j !== si) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot`, d);
      setD(r.data.pilot_lot);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Pilot Lot - Setup Verification.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Pilot Lot setup reports and the generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot`);
      setD(r2.data.pilot_lot);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Pilot Lot setup reports…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  const ParamTable = ({ si, grp, title }) => {
    const s = d.setups[si];
    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className={lbl}>{title}</div>
          {canEdit && <button onClick={() => addParam(si, grp)} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="w-6"></th>{SVR_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(s[grp] || []).map((p, pi) => (
                <tr key={pi} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{pi + 1}</td>
                  {SVR_COLS.map(([k, , w]) => (
                    <td key={k} className="px-0.5 py-0.5"><input className={`${fld} ${w}`} disabled={!canEdit} value={p[k] || ""} onChange={(e) => setParam(si, grp, pi, k, e.target.value)} /></td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeParam(si, grp, pi)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pilot-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Pilot Lot — Setup Verification <span className="text-[10px] font-normal text-slate-400 font-mono">QA-F-64</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/pilot-lot/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pilot-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pilot-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pilot-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pilot-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Material Grade</div><input className={fld} disabled={!canEdit} value={d.material_grade || ""} onChange={(e) => set("material_grade", e.target.value)} /></div>
          <div><div className={lbl}>Setup Rejection Qty</div><input className={fld} disabled={!canEdit} value={d.setup_reject_qty || ""} onChange={(e) => set("setup_reject_qty", e.target.value)} /></div>
          <div><div className={lbl}>Date of FOA</div><input className={fld} disabled={!canEdit} value={d.date_foa || ""} onChange={(e) => set("date_foa", e.target.value)} /></div>
          <div><div className={lbl}>Time of FOA</div><input className={fld} disabled={!canEdit} value={d.time_foa || ""} onChange={(e) => set("time_foa", e.target.value)} /></div>
          <div><div className={lbl}>Time of LOA</div><input className={fld} disabled={!canEdit} value={d.time_loa || ""} onChange={(e) => set("time_loa", e.target.value)} /></div>
        </div>
        <div className="text-[10px] text-slate-400">Each setup below becomes its own sheet in the Excel. FOA = First-Off Approval, LOA = Last-Off Approval.</div>

        {(d.setups || []).map((s, si) => (
          <div key={si} className="border border-slate-200 rounded-lg p-2 space-y-2 bg-slate-50/40">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold text-slate-600">Setup {si + 1}{s.operation ? ` — ${s.operation}` : ""}</div>
              {canEdit && d.setups.length > 1 && <button onClick={() => removeSetup(si)} className="inline-flex items-center gap-1 text-[11px] text-red-500 hover:underline"><Trash2 size={11} /> Remove setup</button>}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><div className={lbl}>Operation</div><input className={fld} disabled={!canEdit} value={s.operation || ""} onChange={(e) => setSetup(si, "operation", e.target.value)} /></div>
              <div><div className={lbl}>Machine No.</div><input className={fld} disabled={!canEdit} value={s.machine_no || ""} onChange={(e) => setSetup(si, "machine_no", e.target.value)} /></div>
              <div><div className={lbl}>Operator</div><input className={fld} disabled={!canEdit} value={s.operator || ""} onChange={(e) => setSetup(si, "operator", e.target.value)} /></div>
              <div><div className={lbl}>Type of Set-up</div>
                <select className={fld} disabled={!canEdit} value={s.setup_type || "normal"} onChange={(e) => setSetup(si, "setup_type", e.target.value)}>
                  <option value="normal">Normal</option><option value="4m">4M change</option><option value="isc">ISC</option><option value="pilot_lot">Pilot lot</option>
                </select>
              </div>
            </div>
            {ParamTable({ si, grp: "product_params", title: "Product Parameter" })}
            {ParamTable({ si, grp: "process_params", title: "Process Parameter" })}
            <div className="grid grid-cols-2 gap-2">
              <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={s.prepared_by || ""} onChange={(e) => setSetup(si, "prepared_by", e.target.value)} /></div>
              <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={s.approved_by || ""} onChange={(e) => setSetup(si, "approved_by", e.target.value)} /></div>
            </div>
          </div>
        ))}
        {canEdit && <button onClick={addSetup} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add setup (new sheet)</button>}
      </div>
    </div>
  );
}

// Editable Daily Production Report (PROD-F-02), Phase 4.
const PR_COLS = [
  ["machine_no", "Machine No.", "min-w-[110px]"], ["product_name", "Product Name", "min-w-[130px]"],
  ["process", "Process", "min-w-[120px]"], ["operator", "Operator", "min-w-[100px]"],
  ["prod_hr", "Prod/Hr", "w-16"], ["start", "Start", "w-16"], ["end", "End", "w-16"], ["work_hrs", "Work Hrs", "w-16"],
  ["plan", "Plan", "w-16"], ["actual", "Actual", "w-16"], ["tool_loss_hr", "Tool&Loss Hr", "w-16"],
  ["reason", "Reason", "min-w-[140px]"], ["opt_loss_hr", "Opt Loss Hr", "w-16"], ["opt_oth_loss_hr", "Opt Oth Loss", "w-16"], ["shift", "Shift", "w-16"],
];
function ProductionReportFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/production-report`)
      .then((r) => { if (alive) setD(r.data.production_report); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { kind: "item", machine_no: "", product_name: "", process: "", operator: "", prod_hr: "", start: "", end: "", work_hrs: "", plan: "", actual: "", tool_loss_hr: "", reason: "", opt_loss_hr: "", opt_oth_loss_hr: "", shift: "Day", npd_pilot: false }] }));
  const addSection = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { kind: "section", label: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/production-report`, d);
      setD(r.data.production_report);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/production-report.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Production Report.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Production Report and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/production-report`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/production-report`);
      setD(r2.data.production_report);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Production Report…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-prodrep-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Daily Production Report <span className="text-[10px] font-normal text-slate-400 font-mono">PROD-F-02</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/production-report/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-prodrep-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-prodrep-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-prodrep-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-prodrep-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
          <div><div className={lbl}>Note</div><input className={fld} disabled={!canEdit} value={d.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <div className="text-[10px] text-slate-400">PLAN (= Prod/Hr × Work Hrs), PROD %, and the day totals are computed automatically in the Excel. Tick "NPD" to highlight a pilot-lot row yellow.</div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Production rows</div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>
              <button onClick={addSection} className="inline-flex items-center gap-1 text-[11px] text-sky-600 hover:underline"><Plus size={11} /> Add floor band</button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-100 rounded">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400 sticky top-0 bg-white z-10">
              <tr>
                <th className="w-6"></th>
                <th className="text-center pb-1 px-0.5 w-10">NPD</th>
                {PR_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                row.kind === "section" ? (
                  <tr key={i} className="bg-sky-50">
                    <td></td>
                    <td colSpan={1 + PR_COLS.length} className="px-0.5 py-1">
                      <input className={`${fld} bg-sky-100 font-semibold`} disabled={!canEdit} placeholder="Floor band (e.g. 2ND FLOOR)" value={row.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} />
                    </td>
                    {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ) : (
                  <tr key={i} className={`align-top ${row.npd_pilot ? "bg-yellow-50" : ""}`}>
                    <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                    <td className="text-center pt-1.5"><input type="checkbox" disabled={!canEdit} checked={!!row.npd_pilot} onChange={(e) => setRow(i, "npd_pilot", e.target.checked)} /></td>
                    {PR_COLS.map(([k, , w]) => (
                      <td key={k} className="px-0.5 py-0.5"><input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} /></td>
                    ))}
                    {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable MSA Study — Gauge R&R (GR&R), Phase 4.
function MsaStudyFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-study`)
      .then((r) => { if (alive) setD(r.data.msa_study); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setMeas = (ap, tr, pt, v) => setD((c) => {
    const m = { ...(c.measurements || {}) };
    const rows = (m[ap] || []).map((row) => [...row]);
    while (rows.length <= tr) rows.push([]);
    while (rows[tr].length <= pt) rows[tr].push("");
    rows[tr][pt] = v;
    m[ap] = rows;
    return { ...c, measurements: m };
  });

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/msa-study`, d);
      setD(r.data.msa_study);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-study.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "MSA Study (GR&R).xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the MSA Study and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/msa-study`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-study`);
      setD(r2.data.msa_study);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading MSA Study…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const nParts = parseInt(d.n_parts || 10, 10) || 10;
  const nTrials = parseInt(d.n_trials || 3, 10) || 3;
  const appraisers = [["A", d.appraiser_a], ["B", d.appraiser_b], ["C", d.appraiser_c]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-msastudy-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> MSA Study — Gauge R&amp;R <span className="text-[10px] font-normal text-slate-400 font-mono">GR&amp;R</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/msa-study/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-msastudy-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-msastudy-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-msastudy-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-msastudy-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Gauge Name</div><input className={fld} disabled={!canEdit} value={d.gauge_name || ""} onChange={(e) => set("gauge_name", e.target.value)} /></div>
          <div><div className={lbl}>Gauge No.</div><input className={fld} disabled={!canEdit} value={d.gauge_no || ""} onChange={(e) => set("gauge_no", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Specification</div><input className={fld} disabled={!canEdit} value={d.specification || ""} onChange={(e) => set("specification", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Study Conducted By</div><input className={fld} disabled={!canEdit} value={d.conducted_by || ""} onChange={(e) => set("conducted_by", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-1">
            <div><div className={lbl}>Parts</div><input type="number" className={fld} disabled={!canEdit} value={d.n_parts || 10} onChange={(e) => set("n_parts", parseInt(e.target.value, 10) || 1)} /></div>
            <div><div className={lbl}>Trials</div><input type="number" className={fld} disabled={!canEdit} value={d.n_trials || 3} onChange={(e) => set("n_trials", parseInt(e.target.value, 10) || 1)} /></div>
          </div>
          <div><div className={lbl}>Appraiser A</div><input className={fld} disabled={!canEdit} value={d.appraiser_a || ""} onChange={(e) => set("appraiser_a", e.target.value)} /></div>
          <div><div className={lbl}>Appraiser B</div><input className={fld} disabled={!canEdit} value={d.appraiser_b || ""} onChange={(e) => set("appraiser_b", e.target.value)} /></div>
          <div><div className={lbl}>Appraiser C</div><input className={fld} disabled={!canEdit} value={d.appraiser_c || ""} onChange={(e) => set("appraiser_c", e.target.value)} /></div>
        </div>

        <div className="text-[10px] text-slate-400">Enter the raw readings. Averages, ranges, EV/AV/GR&R/PV/TV, %variation, NDC, the conclusion and the Run/Range charts are all computed automatically in the generated Excel.</div>

        {appraisers.map(([ap, name]) => (
          <div key={ap}>
            <div className={lbl}>Appraiser {ap}{name ? ` — ${name}` : ""}</div>
            <div className="overflow-x-auto">
              <table className="text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                  <tr><th className="text-left pb-1 px-0.5 w-12">Trial</th>{Array.from({ length: nParts }).map((_, p) => <th key={p} className="text-center pb-1 px-0.5 w-16">P{p + 1}</th>)}</tr>
                </thead>
                <tbody>
                  {Array.from({ length: nTrials }).map((_, tr) => (
                    <tr key={tr}>
                      <td className="text-slate-500 pr-1 pt-1.5 font-medium">{tr + 1}</td>
                      {Array.from({ length: nParts }).map((_, p) => (
                        <td key={p} className="px-0.5 py-0.5">
                          <input className={`${fld} w-16`} disabled={!canEdit} value={((d.measurements?.[ap] || [])[tr] || [])[p] ?? ""} onChange={(e) => setMeas(ap, tr, p, e.target.value)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable MSA Plan (QA-F-49), Phase 3.
const MSA_MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const MSA_STATUS = [["", "—"], ["plan", "○ Plan"], ["completed", "● Done"], ["shift", "⊗ Shift"]];
function MsaPlanFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-plan`)
      .then((r) => { if (alive) setD(r.data.msa_plan); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setMonth = (i, mi, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => { if (j !== i) return x; const a = [...(x.months || [])]; while (a.length < 12) a.push(""); a[mi] = v; return { ...x, months: a }; }) }));
  const addItem = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { kind: "item", equipment: "", identification_no: "", make: "", range: "", location: "", msa_not_required: false, months: Array(12).fill(""), remarks: "" }] }));
  const addSection = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { kind: "section", label: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/msa-plan`, d);
      setD(r.data.msa_plan);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-plan.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "MSA Plan.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the MSA Plan and its generated Excel? The standard instrument register will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/msa-plan`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/msa-plan`);
      setD(r2.data.msa_plan);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading MSA Plan…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const yy = (parseInt(d.fy_start_year || "2023", 10) || 2023) % 100;
  const yy2 = (yy + 1) % 100;
  const monthLabels = MSA_MONTHS.map((m, i) => `${m}-${String(i < 9 ? yy : yy2).padStart(2, "0")}`);
  const items = (d.rows || []).filter((r) => r.kind === "item").length;

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-msa-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> MSA Plan (Instruments) <span className="text-[10px] font-normal text-slate-400 font-mono">QA-F-49</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/msa-plan/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-msa-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-msa-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-msa-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-msa-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><div className={lbl}>Title</div><input className={fld} disabled={!canEdit} value={d.title || ""} onChange={(e) => set("title", e.target.value)} /></div>
          <div><div className={lbl}>FY start year</div><input className={fld} disabled={!canEdit} value={d.fy_start_year || ""} onChange={(e) => set("fy_start_year", e.target.value)} /></div>
          <div><div className={lbl}>Effective From</div><input className={fld} disabled={!canEdit} value={d.effective_from || ""} onChange={(e) => set("effective_from", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Note</div><input className={fld} disabled={!canEdit} value={d.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <div className="text-[10px] text-slate-400">Legend: ○ Plan · ● Completed · ⊗ Shift to next month · tick "MSA N/R" to show "MSA NOT REQUIRED" across the months.</div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Instrument register ({items} instruments)</div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={addItem} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add instrument</button>
              <button onClick={addSection} className="inline-flex items-center gap-1 text-[11px] text-amber-600 hover:underline"><Plus size={11} /> Add section band</button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-100 rounded">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400 sticky top-0 bg-white z-10">
              <tr>
                <th className="w-6"></th>
                <th className="text-left pb-1 px-0.5 min-w-[120px]">Equipment</th>
                <th className="text-left pb-1 px-0.5 min-w-[90px]">Ident. No.</th>
                <th className="text-left pb-1 px-0.5 min-w-[90px]">Make</th>
                <th className="text-left pb-1 px-0.5 min-w-[80px]">Range</th>
                <th className="text-left pb-1 px-0.5 min-w-[90px]">Location</th>
                <th className="text-center pb-1 px-0.5 w-12">MSA N/R</th>
                {monthLabels.map((m) => <th key={m} className="text-center pb-1 px-0.5 w-16">{m}</th>)}
                <th className="text-left pb-1 px-0.5 min-w-[90px]">Remarks</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                row.kind === "section" ? (
                  <tr key={i} className="bg-amber-50">
                    <td className="text-slate-400 pr-1 pt-1.5"></td>
                    <td colSpan={6 + 12} className="px-0.5 py-1">
                      <input className={`${fld} bg-amber-100 font-semibold`} disabled={!canEdit} placeholder="Section band (e.g. MAGNETIC V-BLOCK)" value={row.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} />
                    </td>
                    <td></td>
                    {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ) : (
                  <tr key={i} className="align-top">
                    <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[120px]`} disabled={!canEdit} value={row.equipment || ""} onChange={(e) => setRow(i, "equipment", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[90px]`} disabled={!canEdit} value={row.identification_no || ""} onChange={(e) => setRow(i, "identification_no", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[90px]`} disabled={!canEdit} value={row.make || ""} onChange={(e) => setRow(i, "make", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[80px]`} disabled={!canEdit} value={row.range || ""} onChange={(e) => setRow(i, "range", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[90px]`} disabled={!canEdit} value={row.location || ""} onChange={(e) => setRow(i, "location", e.target.value)} /></td>
                    <td className="text-center pt-1.5"><input type="checkbox" disabled={!canEdit} checked={!!row.msa_not_required} onChange={(e) => setRow(i, "msa_not_required", e.target.checked)} /></td>
                    {Array.from({ length: 12 }).map((_, mi) => (
                      <td key={mi} className="px-0.5 py-0.5">
                        <select className={`${fld} w-16`} disabled={!canEdit || row.msa_not_required} value={(row.months || [])[mi] || ""} onChange={(e) => setMonth(i, mi, e.target.value)}>
                          {MSA_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                    ))}
                    <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[90px]`} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                    {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable SPC Plan (QA-F), Phase 3.
const SPC_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SPC_STATUS = [["", "—"], ["plan", "○ Plan"], ["completed", "● Completed"], ["no_prod", "⊗ No Prod"]];
const SPC_NCHARS = 15;
function SpcPlanFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-plan`)
      .then((r) => { if (alive) setD(r.data.spc_plan); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setChar = (i, ci, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => { if (j !== i) return x; const a = [...(x.special_chars || [])]; while (a.length < SPC_NCHARS) a.push(""); a[ci] = v; return { ...x, special_chars: a }; }) }));
  const setMonth = (i, mi, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => { if (j !== i) return x; const a = [...(x.months || [])]; while (a.length < 12) a.push(""); a[mi] = v; return { ...x, months: a }; }) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { part_name: "", part_no: "", special_chars: Array(SPC_NCHARS).fill(""), months: Array(12).fill(""), remarks: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));
  const setSummary = (field, mi, v) => setD((c) => { const a = [...(c[field] || [])]; while (a.length < 12) a.push(""); a[mi] = v; return { ...c, [field]: a }; });

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/spc-plan`, d);
      setD(r.data.spc_plan);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-plan.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "SPC Plan.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the SPC Plan and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/spc-plan`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/spc-plan`);
      setD(r2.data.spc_plan);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading SPC Plan…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const yy = String(d.year || "2024").slice(-2);
  const monthLabels = SPC_MONTHS.map((m) => `${m}-${yy}`);

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-spc-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> SPC Plan <span className="text-[10px] font-normal text-slate-400 font-mono">QA-F</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/spc-plan/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-spc-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-spc-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-spc-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-spc-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Year</div><input className={fld} disabled={!canEdit} value={d.year || ""} onChange={(e) => set("year", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Effective From</div><input className={fld} disabled={!canEdit} value={d.effective_from || ""} onChange={(e) => set("effective_from", e.target.value)} /></div>
          <div><div className={lbl}>Frequency</div><input className={fld} disabled={!canEdit} value={d.frequency || ""} onChange={(e) => set("frequency", e.target.value)} /></div>
        </div>
        <div className="text-[10px] text-slate-400">Legend: ○ Plan · ● Completed · ⊗ No Production in Month</div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Parts — Special Characteristics (1–15) &amp; Monthly status</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add part</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="w-6"></th>
                <th className="text-left pb-1 px-0.5 min-w-[100px]">Part Name</th>
                <th className="text-left pb-1 px-0.5 min-w-[110px]">Part No.</th>
                {Array.from({ length: SPC_NCHARS }).map((_, i) => <th key={i} className="text-center pb-1 px-0.5 w-16">SC {i + 1}</th>)}
                {monthLabels.map((m) => <th key={m} className="text-center pb-1 px-0.5 w-20">{m}</th>)}
                <th className="text-left pb-1 px-0.5 min-w-[100px]">Remarks</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[100px]`} disabled={!canEdit} value={row.part_name || ""} onChange={(e) => setRow(i, "part_name", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[110px]`} disabled={!canEdit} value={row.part_no || ""} onChange={(e) => setRow(i, "part_no", e.target.value)} /></td>
                  {Array.from({ length: SPC_NCHARS }).map((_, ci) => (
                    <td key={ci} className="px-0.5 py-0.5"><input className={`${fld} w-16`} disabled={!canEdit} value={(row.special_chars || [])[ci] || ""} onChange={(e) => setChar(i, ci, e.target.value)} /></td>
                  ))}
                  {Array.from({ length: 12 }).map((_, mi) => (
                    <td key={mi} className="px-0.5 py-0.5">
                      <select className={`${fld} w-20`} disabled={!canEdit} value={(row.months || [])[mi] || ""} onChange={(e) => setMonth(i, mi, e.target.value)}>
                        {SPC_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  ))}
                  <td className="px-0.5 py-0.5"><input className={`${fld} min-w-[100px]`} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={lbl}>Monthly summary</div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="text-left pb-1 px-0.5 min-w-[220px]">Row</th>{monthLabels.map((m) => <th key={m} className="text-center pb-1 px-0.5 w-20">{m}</th>)}</tr>
            </thead>
            <tbody>
              {[["monthly_adherence", "Monthly Adherence Status (%)"], ["reason_delayed", "Reason for Delayed / Cancelled"], ["sign_incharge", "Sign. of In-charge"]].map(([field, label]) => (
                <tr key={field} className="align-top">
                  <td className="px-0.5 py-0.5 text-slate-600 font-medium">{label}</td>
                  {Array.from({ length: 12 }).map((_, mi) => (
                    <td key={mi} className="px-0.5 py-0.5"><input className={`${fld} w-20`} disabled={!canEdit} value={(d[field] || [])[mi] || ""} onChange={(e) => setSummary(field, mi, e.target.value)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={d.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Packaging Specification (PUR_PR_APQP Attachment 09), Phase 3.
function PackagingSpecFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec`)
      .then((r) => { if (alive) setD(r.data.packaging_spec); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec`, d);
      setD(r.data.packaging_spec);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Packaging Specification.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Packaging Specification and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec`);
      setD(r2.data.packaging_spec);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  const [imgUrls, setImgUrls] = useState({});
  async function uploadImg(slot, key, file) {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec/image?slot=${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setD((c) => ({ ...c, [key]: r.data.path }));
      setImgUrls((u) => ({ ...u, [slot]: r.data.url }));
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Packaging Specification…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const F = ({ k, label, area, cls }) => (
    <div className={cls}>
      <div className={lbl}>{label}</div>
      {area
        ? <textarea rows={area} className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />
        : <input className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />}
    </div>
  );
  const Sel = ({ k, label, opts, cls }) => (
    <div className={cls}>
      <div className={lbl}>{label}</div>
      <select className={fld} disabled={!canEdit} value={d[k] || ""} onChange={(e) => set(k, e.target.value)}>
        <option value="">—</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
  const SectionTitle = ({ children }) => <div className="text-[11px] font-semibold text-slate-600 border-b border-slate-100 pb-1 pt-1">{children}</div>;
  const YNNA = [["yes", "Yes"], ["no", "No"], ["na", "NA"]];

  const packs = d.packs || [];
  const setPack = (i, k, v) => setD((c) => ({ ...c, packs: (c.packs || []).map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const styles = d.pack_styles || [];
  const setStyle = (i, k, v) => setD((c) => ({ ...c, pack_styles: (c.pack_styles || []).map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const SPEC_KEYS = [["L", "L"], ["W", "W"], ["H", "H"], ["ERQ", "ERQ"], ["TR", "TR"], ["TQ", "TQ"], ["ECQ", "ECQ"], ["TC", "TC"], ["TQ2", "TQ"], ["others", "Others"]];
  const IMG_SLOTS = [["part_3d", "img_part_3d", "Part 3D Image"], ["pack_1", "img_pack_1", "Packing (1) Photo"], ["pack_2", "img_pack_2", "Packing (2) Photo"], ["pack_3", "img_pack_3", "Packing (3) Photo"], ["pack_4", "img_pack_4", "Packing (4) Photo"]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pkg-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Packaging Specification Data Sheet & Sign-off <span className="text-[10px] font-normal text-slate-400 font-mono">CA/PP-59/013</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/packaging-spec/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pkg-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pkg-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pkg-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pkg-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {SectionTitle({ children: "Identity" })}
        <div className="grid grid-cols-4 gap-2">
          {F({ k: "part_name", label: "Part Name" })}
          {F({ k: "part_no", label: "Part No." })}
          {F({ k: "supplier_name", label: "Supplier Name" })}
          {F({ k: "supplier_code", label: "Supplier Code" })}
          {F({ k: "supplier_address", label: "Supplier Address", cls: "col-span-2" })}
          {F({ k: "po_no", label: "P.O.No." })}
          {F({ k: "mode_of_transport", label: "Mode Of Transp." })}
          {F({ k: "date", label: "Date" })}
          {F({ k: "uom", label: "UOM (Nos/Kg/Mtr)" })}
          {F({ k: "supplier_contact", label: "Supplier Contact" })}
          {F({ k: "email_id", label: "E-Mail ID" })}
        </div>

        {SectionTitle({ children: "Images (Part 3D + Packing photos) — placed in the Excel spaces" })}
        <div className="grid grid-cols-5 gap-2">
          {IMG_SLOTS.map(([slot, key, label]) => (
            <div key={slot} className="border border-slate-200 rounded p-2 flex flex-col items-center gap-1">
              <div className="text-[10px] font-medium text-slate-500 text-center">{label}</div>
              <div className="w-full h-20 bg-slate-50 border border-dashed border-slate-200 rounded flex items-center justify-center overflow-hidden">
                {imgUrls[slot] ? <img src={imgUrls[slot]} alt={label} className="max-h-full max-w-full object-contain" />
                  : d[key] ? <span className="text-[10px] text-emerald-600">✓ Uploaded</span>
                    : <span className="text-[10px] text-slate-300">No image</span>}
              </div>
              {canEdit && (
                <label className="text-[10px] text-orange-600 hover:underline cursor-pointer inline-flex items-center gap-1">
                  <Upload size={10} /> {d[key] ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImg(slot, key, e.target.files[0])} />
                </label>
              )}
            </div>
          ))}
        </div>

        {SectionTitle({ children: "Oil & Protection" })}
        <div className="grid grid-cols-4 gap-2">
          {F({ k: "oil_specification", label: "1) Oil Specification" })}
          {Sel({ k: "oil_application", label: "2) Oil Application", opts: [["dry", "Dry"], ["wet", "Wet"]] })}
          {Sel({ k: "barcoding", label: "3) Barcoding For Packing", opts: [["yes", "Yes"], ["no", "No"]] })}
          {F({ k: "shelf_life", label: "4) Shelf Life Applicable (date)" })}
          {Sel({ k: "rust_protection", label: "5) Rust Protection", opts: YNNA })}
          {Sel({ k: "moisture_protection", label: "6) Moisture Protection", opts: YNNA })}
          {Sel({ k: "rain_protection", label: "7) Rain Protection", opts: YNNA })}
        </div>

        {SectionTitle({ children: "Pack Style" })}
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="text-left pb-1 px-1 min-w-[120px]">Pack Style</th><th className="text-left pb-1 px-1 w-24">Yes/No</th><th className="text-left pb-1 px-1 min-w-[140px]">Colour (mention)</th></tr>
            </thead>
            <tbody>
              {styles.map((s, i) => (
                <tr key={i}>
                  <td className="px-1 py-0.5 text-slate-700">{s.style}</td>
                  <td className="px-1 py-0.5">
                    <select className={`${fld} w-24`} disabled={!canEdit} value={s.yes_no || ""} onChange={(e) => setStyle(i, "yes_no", e.target.value)}>
                      <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                    </select>
                  </td>
                  <td className="px-1 py-0.5"><input className={fld} disabled={!canEdit} value={s.colour || ""} onChange={(e) => setStyle(i, "colour", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {SectionTitle({ children: "Packing Specs (1–4)  ·  L/W/H/ERQ/TR/TQ/ECQ/TC/TQ/Others" })}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {packs.slice(0, 4).map((p, i) => (
            <div key={i} className="border border-slate-200 rounded p-2">
              <div className="text-[11px] font-semibold text-slate-600 mb-1">Packing Specs ({i + 1})</div>
              <div className="grid grid-cols-2 gap-1">
                {SPEC_KEYS.map(([k, label]) => (
                  <div key={k}>
                    <div className="text-[9px] uppercase text-slate-400">{label}</div>
                    <input className={fld} disabled={!canEdit} value={p[k] || ""} onChange={(e) => setPack(i, k, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {SectionTitle({ children: "Packing Description" })}
        <div className="grid grid-cols-2 gap-2">
          {F({ k: "individual_part_weight", label: "1) Individual Part Weight (Kg)" })}
          {F({ k: "protective_cap", label: "4) Protective Cap if Any" })}
          {F({ k: "empty_packing_weight", label: "2) Empty Packing Material Weight (Kg)" })}
          {F({ k: "total_final_weight", label: "5) Total Final Packaging Weight (Kg)" })}
          {F({ k: "total_qty_final", label: "3) Total Quantity in Final Packaging (Nos.)" })}
          {F({ k: "identification_leveling", label: "6) Identification/Leveling on Final Pkg" })}
        </div>

        {SectionTitle({ children: "Comments" })}
        <div className="grid grid-cols-2 gap-2">
          {F({ k: "supplier_comments", label: "Supplier Comments", area: 3 })}
          {F({ k: "pvna_comments", label: "PVNA Comments", area: 3 })}
        </div>

        {SectionTitle({ children: "Supplier Packaging Sign-off" })}
        <div className="grid grid-cols-2 gap-2">
          {F({ k: "sps_name", label: "Name" })}
          {F({ k: "sps_designation", label: "Designation" })}
        </div>

        {SectionTitle({ children: "For Customer Use Only" })}
        <div className="grid grid-cols-4 gap-2">
          {F({ k: "cust_design_name", label: "Design & Dev — Name" })}
          {F({ k: "cust_design_date", label: "Design & Dev — Sign/Date" })}
          {F({ k: "cust_vendor_name", label: "Vendor Dev — Name" })}
          {F({ k: "cust_vendor_date", label: "Vendor Dev — Sign/Date" })}
          {F({ k: "cust_purchase_name", label: "Purchase — Name" })}
          {F({ k: "cust_purchase_date", label: "Purchase — Sign/Date" })}
          {F({ k: "cust_store_name", label: "Store — Name" })}
          {F({ k: "cust_store_date", label: "Store — Sign/Date" })}
        </div>
      </div>
    </div>
  );
}

// Editable Sample Inspection Report (QA-F-11), Phase 3.
const INSP_REPORT_TYPES = [["regular", "Regular"], ["trial", "Trial"], ["pilot", "Pilot Lot"], ["ppap", "PPAP lot"], ["isc", "ISC Parts"]];
const INSP_COLS = [
  ["parameter", "Inspection Parameter", "min-w-[150px]"],
  ["spec", "Specification", "w-24"],
  ["tol", "Tolerance", "w-20"],
  ["method", "Inspection Method", "w-24"],
];
function InspectionReportFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/inspection-report`)
      .then((r) => { if (alive) setD(r.data.inspection_report); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setObs = (i, oi, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => { if (j !== i) return x; const obs = [...(x.obs || ["", "", "", "", ""])]; while (obs.length < 5) obs.push(""); obs[oi] = v; return { ...x, obs }; }) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { parameter: "", spec: "", tol: "", method: "", obs: ["", "", "", "", ""], judgement: "", remarks: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));
  const setRev = (i, k, v) => setD((c) => ({ ...c, revisions: (c.revisions || []).map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRev = () => setD((c) => ({ ...c, revisions: [...(c.revisions || []), { rev_no: "", rev_date: "", detail: "", revised_by: "", approved_by: "" }] }));
  const removeRev = (i) => setD((c) => ({ ...c, revisions: (c.revisions || []).filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/inspection-report`, d);
      setD(r.data.inspection_report);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/inspection-report.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Sample Inspection Report.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Inspection Report and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/inspection-report`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/inspection-report`);
      setD(r2.data.inspection_report);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Inspection Report…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-insp-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Sample Inspection Report <span className="text-[10px] font-normal text-slate-400 font-mono">QA-F-11</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/inspection-report/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-insp-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-insp-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-insp-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-insp-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Model</div><input className={fld} disabled={!canEdit} value={d.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
          <div><div className={lbl}>Dwg. Rev. No./Date</div><input className={fld} disabled={!canEdit} value={d.dwg_rev || ""} onChange={(e) => set("dwg_rev", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={fld} disabled={!canEdit} value={d.report_date || ""} onChange={(e) => set("report_date", e.target.value)} /></div>
          <div><div className={lbl}>Sample Size</div><input className={fld} disabled={!canEdit} value={d.sample_size || ""} onChange={(e) => set("sample_size", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-1">
            <div><div className={lbl}>Total dim</div><input className={fld} disabled={!canEdit} value={d.total_dim || ""} onChange={(e) => set("total_dim", e.target.value)} /></div>
            <div><div className={lbl}>check dim</div><input className={fld} disabled={!canEdit} value={d.check_dim || ""} onChange={(e) => set("check_dim", e.target.value)} /></div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className={lbl}>Type of Report</div>
            <select className={fld} disabled={!canEdit} value={d.report_type || "trial"} onChange={(e) => set("report_type", e.target.value)}>
              {INSP_REPORT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {d.report_type === "trial" && (
            <div><div className={lbl}>Trial (specify)</div><input className={`${fld} w-24`} disabled={!canEdit} value={d.trial_spec || ""} onChange={(e) => set("trial_spec", e.target.value)} /></div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Inspection Parameters &amp; Observations</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="w-6"></th>
                {INSP_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}
                {[1, 2, 3, 4, 5].map((n) => <th key={n} className="text-center pb-1 px-0.5 w-16">Obs {n}</th>)}
                <th className="text-center pb-1 px-0.5 w-14">o / x</th>
                <th className="text-left pb-1 px-0.5 min-w-[110px]">Remarks</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  {INSP_COLS.map(([k, , w]) => (
                    <td key={k} className="px-0.5 py-0.5">
                      <input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                    </td>
                  ))}
                  {[0, 1, 2, 3, 4].map((oi) => (
                    <td key={oi} className="px-0.5 py-0.5">
                      <input className={`${fld} w-16`} disabled={!canEdit} value={(row.obs || [])[oi] || ""} onChange={(e) => setObs(i, oi, e.target.value)} />
                    </td>
                  ))}
                  <td className="px-0.5 py-0.5">
                    <select className={`${fld} w-16 text-center`} disabled={!canEdit}
                      value={(() => { const v = (row.judgement || "").toUpperCase(); return v === "OK" || v === "O" ? "OK" : (v === "NG" || v === "X" ? "NG" : ""); })()}
                      onChange={(e) => setRow(i, "judgement", e.target.value)}>
                      <option value="">—</option>
                      <option value="OK">OK</option>
                      <option value="NG">NG</option>
                    </select>
                  </td>
                  <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div><div className={lbl}>Remarks</div><input className={fld} disabled={!canEdit} value={d.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className={lbl}>Disposition</div>
            <select className={fld} disabled={!canEdit} value={d.disposition || ""} onChange={(e) => set("disposition", e.target.value)}>
              <option value="">—</option>
              <option value="accepted">Accepted</option>
              <option value="deviation">Accepted under deviation</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Inspected by (QA/NPD Er.)</div><input className={fld} disabled={!canEdit} value={d.inspected_by || ""} onChange={(e) => set("inspected_by", e.target.value)} /></div>
          <div><div className={lbl}>Reviewed By (Deptt. Manager)</div><input className={fld} disabled={!canEdit} value={d.reviewed_by || ""} onChange={(e) => set("reviewed_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved by (HOD-QA)</div><input className={fld} disabled={!canEdit} value={d.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-2">
          <div className={lbl}>Revision History <span className="text-slate-300 normal-case">(separate sheet)</span></div>
          {canEdit && <button onClick={addRev} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add revision</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="text-left pb-1 px-0.5 w-16">Rev. No.</th><th className="text-left pb-1 px-0.5 w-24">Rev. Date</th><th className="text-left pb-1 px-0.5 min-w-[200px]">Revision Details</th><th className="text-left pb-1 px-0.5 w-28">Revised By</th><th className="text-left pb-1 px-0.5 w-28">Approved By</th>{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.revisions || []).map((rev, i) => (
                <tr key={i} className="align-top">
                  {["rev_no", "rev_date", "detail", "revised_by", "approved_by"].map((k) => (
                    <td key={k} className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={rev[k] || ""} onChange={(e) => setRev(i, k, e.target.value)} /></td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRev(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable Trial Validation Record (ENGG-F-46), Phase 3.
function TrialValidationFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/trial-validation`)
      .then((r) => { if (alive) setD(r.data.trial_validation); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/trial-validation`, d);
      setD(r.data.trial_validation);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/trial-validation.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Trial Validation Record.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Trial Validation Record and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/trial-validation`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/trial-validation`);
      setD(r2.data.trial_validation);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Trial Validation Record…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-tv-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Trial Validation Record <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-46</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/trial-validation/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-tv-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-tv-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-tv-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-tv-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Model</div><input className={fld} disabled={!canEdit} value={d.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
          <div><div className={lbl}>Supplier Name</div><input className={fld} disabled={!canEdit} value={d.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} /></div>
          <div><div className={lbl}>Date &amp; Time</div><input className={fld} disabled={!canEdit} value={d.date_time || ""} onChange={(e) => set("date_time", e.target.value)} /></div>
          <div><div className={lbl}>Trial Qty</div><input className={fld} disabled={!canEdit} value={d.trial_qty || ""} onChange={(e) => set("trial_qty", e.target.value)} /></div>
          <div className="col-span-3"><div className={lbl}>Trial Purpose</div><input className={fld} disabled={!canEdit} value={d.trial_purpose || ""} onChange={(e) => set("trial_purpose", e.target.value)} /></div>
        </div>

        <div><div className={lbl}>Trial Summary</div><textarea rows={3} className={fld} disabled={!canEdit} value={d.trial_summary || ""} onChange={(e) => set("trial_summary", e.target.value)} /></div>
        <div><div className={lbl}>Trial Result</div><textarea rows={3} className={fld} disabled={!canEdit} value={d.trial_result || ""} onChange={(e) => set("trial_result", e.target.value)} /></div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Result Judgement (OK/NG)</div><input className={fld} disabled={!canEdit} value={d.result_judgement || ""} onChange={(e) => set("result_judgement", e.target.value)} /></div>
          <div><div className={lbl}>Remarks / Comments</div><input className={fld} disabled={!canEdit} value={d.remarks || ""} onChange={(e) => set("remarks", e.target.value)} /></div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Production Incharge</div><input className={fld} disabled={!canEdit} value={d.production_incharge || ""} onChange={(e) => set("production_incharge", e.target.value)} /></div>
          <div><div className={lbl}>Quality Incharge</div><input className={fld} disabled={!canEdit} value={d.quality_incharge || ""} onChange={(e) => set("quality_incharge", e.target.value)} /></div>
          <div><div className={lbl}>Operation Head</div><input className={fld} disabled={!canEdit} value={d.operation_head || ""} onChange={(e) => set("operation_head", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Initial Production Parts Logbook (Engg-F-28), Phase 3.
const IPP_COLS = [
  ["dev_category", "Dev. Category", "w-24"],
  ["trial_stage", "Trial Stage", "w-16"],
  ["total_qty", "Total Qty", "w-16"],
  ["ok", "OK", "w-12"],
  ["ng", "NG", "w-12"],
  ["ip_tag_date", "IP Tag / Prod. date", "w-32", "date"],
  ["batch_code", "Batch Code", "w-20"],
  ["dispatch", "Dispatch", "w-20"],
  ["dispatch_date", "Date", "w-32", "date"],
  ["balance", "Balance", "w-16"],
  ["scrap_qty", "Scrap qty.", "w-16"],
  ["scrap_reason", "Reason for scrap", "min-w-[130px]"],
  ["customer_feedback", "Feedback from customer", "w-20", "select"],
  ["remarks", "Remarks", "min-w-[130px]"],
];
function IppLogbookFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook`)
      .then((r) => { if (alive) setD(r.data.ipp_logbook); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { dev_category: "NEW", trial_stage: "", total_qty: "", ok: "", ng: "", ip_tag_date: "", batch_code: "", dispatch: "", dispatch_date: "", balance: "", scrap_qty: "", scrap_reason: "", customer_feedback: "", remarks: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook`, d);
      setD(r.data.ipp_logbook);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Initial Production Parts Logbook.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Logbook and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook`);
      setD(r2.data.ipp_logbook);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Logbook…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-ipp-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Initial Production Parts Logbook <span className="text-[10px] font-normal text-slate-400 font-mono">Engg-F-28</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/ipp-logbook/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-ipp-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-ipp-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-ipp-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-ipp-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={d.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={d.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Project</div><input className={fld} disabled={!canEdit} value={d.project || ""} onChange={(e) => set("project", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>Trial Records</div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="w-6"></th>{IPP_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  {IPP_COLS.map(([k, , w, type]) => (
                    <td key={k} className="px-0.5 py-0.5">
                      {type === "select" ? (
                        <select className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)}>
                          <option value="">—</option>
                          <option value="OK">OK</option>
                          <option value="NG">NG</option>
                        </select>
                      ) : (
                        <input type={type === "date" ? "date" : "text"} className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                      )}
                    </td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-slate-400">Part Name / Part No. / Customer above are applied to every row in the generated Excel.</div>
      </div>
    </div>
  );
}

// Editable Operation Standard (OPI) form (ENGG-F-18), Phase 3.
const OS_COLS = [["parameter", "Parameter", "min-w-[120px]"], ["cls", "Class", "w-14"], ["specification", "Specification", "min-w-[160px]"], ["inspection_method", "Inspection Method", "min-w-[120px]"], ["frequency", "Frequency", "min-w-[180px]", true]];
function OperationStandardFormSection({ apqpId, task, canEdit, onSaved }) {
  const [os, setOs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/operation-standard`)
      .then((r) => { if (alive) setOs(r.data.operation_standard); })
      .catch(() => { if (alive) setOs(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setOs((c) => ({ ...c, [k]: v }));
  const setRow = (group, i, k, v) => setOs((c) => ({ ...c, [group]: c[group].map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = (group) => setOs((c) => ({ ...c, [group]: [...(c[group] || []), { parameter: "", cls: "", specification: "", inspection_method: "", frequency: "" }] }));
  const removeRow = (group, i) => setOs((c) => ({ ...c, [group]: c[group].filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/operation-standard`, os);
      setOs(r.data.operation_standard);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/operation-standard.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Operation Standard.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Operation Standard and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/operation-standard`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/operation-standard`);
      setOs(r2.data.operation_standard);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Operation Standard…</div>;
  if (!os) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  const ParamTable = ({ title, group }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className={lbl}>{title}</div>
        {canEdit && <button onClick={() => addRow(group)} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-slate-400">
            <tr><th className="w-6"></th>{OS_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
          </thead>
          <tbody>
            {(os[group] || []).map((row, i) => (
              <tr key={i} className="align-top">
                <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                {OS_COLS.map(([k, , w, isArea]) => (
                  <td key={k} className="px-0.5 py-0.5">
                    {isArea
                      ? <textarea rows={3} className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(group, i, k, e.target.value)} />
                      : <input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(group, i, k, e.target.value)} />}
                  </td>
                ))}
                {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(group, i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-os-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Operation Standard (OPI) <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-18</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/operation-standard/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-os-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-os-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-os-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-os-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Part Name</div><input className={fld} disabled={!canEdit} value={os.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={fld} disabled={!canEdit} value={os.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={os.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Required M/C</div><input className={fld} disabled={!canEdit} value={os.required_mc || ""} onChange={(e) => set("required_mc", e.target.value)} /></div>
          <div><div className={lbl}>Operation Name</div><input className={fld} disabled={!canEdit} value={os.operation_name || ""} onChange={(e) => set("operation_name", e.target.value)} /></div>
          <div><div className={lbl}>Operation / Die No.</div><input className={fld} disabled={!canEdit} value={os.operation_die_no || ""} onChange={(e) => set("operation_die_no", e.target.value)} /></div>
        </div>
        <div className="text-[10px] text-slate-400">The operation sketch (part drawing) is embedded automatically in the generated Excel.</div>

        {ParamTable({ title: "Product Parameter", group: "product_params" })}
        {ParamTable({ title: "Process Parameter", group: "process_params" })}

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={fld} disabled={!canEdit} value={os.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={fld} disabled={!canEdit} value={os.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Control Plan (CP) form (ENGG-F-17), Phase 3.
const CP_COLS = [
  ["process_number", "Part/Proc No", "w-16", false], ["process_name", "Process Name", "min-w-[110px]", false],
  ["machine", "Machine/Tools", "min-w-[100px]", false], ["char_no", "No.", "w-14", false],
  ["product", "Product", "min-w-[100px]", false], ["process", "Process", "min-w-[100px]", false],
  ["spl_class", "Spl", "w-10", false], ["spec_tolerance", "Spec/Tolerance", "min-w-[110px]", false],
  ["eval_technique", "Eval Technique", "min-w-[100px]", false], ["error_proofing", "Err Proof", "w-16", false],
  ["sample_size", "Sample Size", "min-w-[90px]", false], ["sample_freq", "Freq", "min-w-[90px]", false],
  ["recording", "Rec Y/N", "w-14", false], ["controls", "Current Controls", "min-w-[140px]", true],
  ["responsibility", "Responsibility", "min-w-[100px]", false], ["reaction_plan", "Reaction Plan", "min-w-[120px]", true],
  ["corrective_action", "Corrective Action", "min-w-[120px]", true],
];
const CP_BLANK = Object.fromEntries(CP_COLS.map(([k]) => [k, ""]));
function ControlPlanFormSection({ apqpId, task, canEdit, onSaved }) {
  const [cp, setCp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/control-plan`)
      .then((r) => { if (alive) setCp(r.data.control_plan); })
      .catch(() => { if (alive) setCp(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setCp((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setCp((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setCp((c) => ({ ...c, rows: [...(c.rows || []), { ...CP_BLANK }] }));
  const removeRow = (i) => setCp((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/control-plan`, cp);
      setCp(r.data.control_plan);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/control-plan.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Control Plan.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Control Plan sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/control-plan`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/control-plan`);
      setCp(r2.data.control_plan);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Control Plan…</div>;
  if (!cp) return null;

  const fld = "w-full border border-slate-200 rounded px-1 py-0.5 text-[10px] disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const HF = [["customer", "Customer"], ["part_no", "Part No"], ["part_name", "Part Name"], ["dwg_rev", "Dwg. Rev/Model"], ["cp_no", "CP No"], ["key_contact", "Key Contact"], ["core_team", "Core Team"], ["supplier_approval", "Supplier/Plant Approval"], ["rev_no_date", "Rev No/Date"], ["key_date", "Key Date"], ["org_date", "Org Date"], ["prepared_by", "Prepared By"], ["approved_by", "Approved By"]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-cp-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Control Plan <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-17</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/control-plan/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-cp-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-cp-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-cp-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-cp-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div>
            <div className={lbl}>Type</div>
            <select className={fld.replace("text-[10px]", "text-xs")} disabled={!canEdit} value={cp.type || "pre_launch"} onChange={(e) => set("type", e.target.value)}>
              <option value="prototype">Prototype</option>
              <option value="pre_launch">Pre-Launch</option>
              <option value="production">Production</option>
              <option value="safe_launch">Safe Launch</option>
            </select>
          </div>
          {HF.map(([k, label]) => (
            <div key={k}><div className={lbl}>{label}</div><input className={fld.replace("text-[10px]", "text-xs")} disabled={!canEdit} value={cp[k] || ""} onChange={(e) => set(k, e.target.value)} /></div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Control Plan rows <span className="text-slate-300 normal-case">(scroll →)</span></div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="text-[10px]">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  {CP_COLS.map(([k, label]) => <th key={k} className="px-1 py-1 text-left">{label}</th>)}
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(cp.rows || []).map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top" data-testid={`apqp-cp-row-${i}`}>
                    {CP_COLS.map(([k, , w, isArea]) => (
                      <td key={k} className="px-0.5 py-0.5">
                        {isArea
                          ? <textarea rows={2} className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                          : <input className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />}
                      </td>
                    ))}
                    {canEdit && <td className="text-center"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable Process FMEA (PFMEA) form (ENGG-F-16), Phase 3. RPN = Sev × Occ × Det (auto).
const _pfmeaRpn = (a, b, c) => {
  const n = [a, b, c].map((v) => Number(v));
  if (n.some((x) => !Number.isFinite(x) || String(x).trim() === "")) return "";
  if ([a, b, c].some((v) => v === "" || v == null)) return "";
  return n[0] * n[1] * n[2];
};
function PfmeaFormSection({ apqpId, task, canEdit, onSaved }) {
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/pfmea`)
      .then((r) => { if (alive) setPm(r.data.pfmea); })
      .catch(() => { if (alive) setPm(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setPm((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setPm((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const blankRow = { process_no: "", process: "", requirement: "", failure_mode: "", effect: "", severity: "", special_char: "", cause: "", occurrence: "", prevention: "", detection_control: "", detection: "", recommended: "", responsibility: "", action_taken: "", ar_severity: "", ar_occurrence: "", ar_detection: "" };
  const addRow = () => setPm((c) => ({ ...c, rows: [...(c.rows || []), { ...blankRow }] }));
  const removeRow = (i) => setPm((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/pfmea`, pm);
      setPm(r.data.pfmea);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pfmea.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Process FMEA.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the PFMEA sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/pfmea`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pfmea`);
      setPm(r2.data.pfmea);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading PFMEA…</div>;
  if (!pm) return null;

  const fld = "w-full border border-slate-200 rounded px-1 py-0.5 text-[10px] disabled:bg-slate-50";
  const num = "w-9 border border-slate-200 rounded px-0.5 py-0.5 text-[10px] text-center disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const HF = [["customer", "Customer"], ["part_no", "Part No"], ["part_name", "Part Name"], ["dwg_rev", "Dwg. Rev / Model"], ["pfmea_rev", "PFMEA Rev/Date"], ["key_contact", "Key Contact"], ["core_team", "Core Team"], ["supplier_approval", "Supplier/Plant Approval"], ["fmea_no", "PFMEA No."], ["key_date", "Key Date"], ["org_date", "Org Date"], ["prepared_by", "Prepared By"], ["approved_by", "Approved By"]];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pfmea-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Process FMEA <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-16</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/pfmea/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pfmea-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pfmea-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pfmea-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pfmea-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          {HF.map(([k, label]) => (
            <div key={k}><div className={lbl}>{label}</div><input className={fld.replace("text-[10px]", "text-xs")} disabled={!canEdit} value={pm[k] || ""} onChange={(e) => set(k, e.target.value)} /></div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>FMEA rows <span className="text-slate-300 normal-case">(RPN = Sev × Occ × Det, auto · scroll →)</span></div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="text-[10px]">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-1 py-1 w-14 text-left">Process No.</th>
                  <th className="px-1 py-1 min-w-[90px] text-left">Process</th>
                  <th className="px-1 py-1 min-w-[90px] text-left">Requirement</th>
                  <th className="px-1 py-1 min-w-[90px] text-left">Failure Mode</th>
                  <th className="px-1 py-1 min-w-[140px] text-left">Effect(s)</th>
                  <th className="px-1 py-1 w-10">Sev</th>
                  <th className="px-1 py-1 w-8">Sp</th>
                  <th className="px-1 py-1 min-w-[130px] text-left">Cause(s)</th>
                  <th className="px-1 py-1 w-10">Occ</th>
                  <th className="px-1 py-1 min-w-[150px] text-left">Prevention</th>
                  <th className="px-1 py-1 min-w-[130px] text-left">Detection</th>
                  <th className="px-1 py-1 w-10">Det</th>
                  <th className="px-1 py-1 w-10 bg-slate-200">RPN</th>
                  <th className="px-1 py-1 min-w-[110px] text-left">Recommended</th>
                  <th className="px-1 py-1 min-w-[110px] text-left">Responsibility</th>
                  <th className="px-1 py-1 min-w-[100px] text-left">Action Taken</th>
                  <th className="px-1 py-1 w-9">Sev</th>
                  <th className="px-1 py-1 w-9">Occ</th>
                  <th className="px-1 py-1 w-9">Det</th>
                  <th className="px-1 py-1 w-10 bg-slate-200">RPN</th>
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(pm.rows || []).map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top" data-testid={`apqp-pfmea-row-${i}`}>
                    <td className="px-0.5 py-0.5"><input className={`${fld} !w-14`} disabled={!canEdit} value={row.process_no || ""} onChange={(e) => setRow(i, "process_no", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={row.process || ""} onChange={(e) => setRow(i, "process", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={row.requirement || ""} onChange={(e) => setRow(i, "requirement", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={fld} disabled={!canEdit} value={row.failure_mode || ""} onChange={(e) => setRow(i, "failure_mode", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={3} className={fld} disabled={!canEdit} value={row.effect || ""} onChange={(e) => setRow(i, "effect", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.severity || ""} onChange={(e) => setRow(i, "severity", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.special_char || ""} onChange={(e) => setRow(i, "special_char", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={3} className={fld} disabled={!canEdit} value={row.cause || ""} onChange={(e) => setRow(i, "cause", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.occurrence || ""} onChange={(e) => setRow(i, "occurrence", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={3} className={fld} disabled={!canEdit} value={row.prevention || ""} onChange={(e) => setRow(i, "prevention", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={3} className={fld} disabled={!canEdit} value={row.detection_control || ""} onChange={(e) => setRow(i, "detection_control", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.detection || ""} onChange={(e) => setRow(i, "detection", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5 text-center font-semibold bg-slate-50">{_pfmeaRpn(row.severity, row.occurrence, row.detection)}</td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={fld} disabled={!canEdit} value={row.recommended || ""} onChange={(e) => setRow(i, "recommended", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={fld} disabled={!canEdit} value={row.responsibility || ""} onChange={(e) => setRow(i, "responsibility", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={fld} disabled={!canEdit} value={row.action_taken || ""} onChange={(e) => setRow(i, "action_taken", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.ar_severity || ""} onChange={(e) => setRow(i, "ar_severity", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.ar_occurrence || ""} onChange={(e) => setRow(i, "ar_occurrence", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={num} disabled={!canEdit} value={row.ar_detection || ""} onChange={(e) => setRow(i, "ar_detection", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5 text-center font-semibold bg-slate-50">{_pfmeaRpn(row.ar_severity, row.ar_occurrence, row.ar_detection)}</td>
                    {canEdit && <td className="text-center"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable PDCA Summary Sheet (Lesson Learned / Corrective Action).
const PDCA_COLS = [
  ["date_of_analysis", "Date of Analysis", "w-28", "date"],
  ["stage", "Stage", "w-24"],
  ["defect_description", "Defect Description", "min-w-[150px]"],
  ["root_cause", "Root cause", "min-w-[180px]"],
  ["counter_measure", "Counter measure", "min-w-[180px]"],
  ["resp", "Resp.", "w-24"],
  ["target_date", "Target date", "w-28", "date"],
  ["actual_completion", "Actual Completion", "w-28", "date"],
  ["lot1", "LOT1", "w-24"],
  ["lot2", "LOT2", "w-24"],
  ["lot3", "LOT3", "w-24"],
  ["horizontal_deployment", "Horizontal Deployment", "min-w-[130px]"],
];
function PdcaFormSection({ apqpId, task, canEdit, onSaved }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/pdca`)
      .then((r) => { if (alive) setD(r.data.pdca); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setD((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setD((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setD((c) => ({ ...c, rows: [...(c.rows || []), { date_of_analysis: "", stage: "", defect_description: "", root_cause: "", counter_measure: "", resp: "", target_date: "", actual_completion: "", lot1: "", lot2: "", lot3: "", horizontal_deployment: "" }] }));
  const removeRow = (i) => setD((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/pdca`, d);
      setD(r.data.pdca);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pdca.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "PDCA Summary Sheet.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the PDCA Summary Sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/pdca`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pdca`);
      setD(r2.data.pdca);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading PDCA Summary Sheet…</div>;
  if (!d) return null;

  const fld = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pdca-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> PDCA Summary Sheet
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/pdca/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pdca-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pdca-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pdca-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pdca-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Part Name & Number</div><input className={fld} disabled={!canEdit} value={d.part_name_number || ""} onChange={(e) => set("part_name_number", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={fld} disabled={!canEdit} value={d.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between">
          <div className={lbl}>PDCA Rows <span className="text-slate-300 normal-case">(Plan · Do · Check · Act; scroll →)</span></div>
          {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr><th className="w-6"></th>{PDCA_COLS.map(([k, l]) => <th key={k} className="text-left pb-1 px-0.5">{l}</th>)}{canEdit && <th className="w-6"></th>}</tr>
            </thead>
            <tbody>
              {(d.rows || []).map((row, i) => (
                <tr key={i} className="align-top">
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  {PDCA_COLS.map(([k, , w, type]) => (
                    <td key={k} className="px-0.5 py-0.5">
                      {type === "date"
                        ? <input type="date" className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                        : <textarea rows={2} className={`${fld} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />}
                    </td>
                  ))}
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable Past Defect History (Kakotora) form (ENGG-F-20), Phase 2.
function KakotoraFormSection({ apqpId, task, canEdit, onSaved }) {
  const [kk, setKk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/kakotora`)
      .then((r) => { if (alive) setKk(r.data.kakotora); })
      .catch(() => { if (alive) setKk(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setKk((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setKk((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setKk((c) => ({ ...c, rows: [...(c.rows || []), { project: "", part_name: "", complaint: "", customer: "", date: "", category: "External", root_cause: "", lesson_learnt: "", updated_by: "", applicability: "NO", remarks: "" }] }));
  const removeRow = (i) => setKk((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  // Bulk set Applicable for every entry whose complaint matches a keyword (e.g. "Plating").
  const [bulkKeyword, setBulkKeyword] = useState("");
  const [bulkValue, setBulkValue] = useState("YES");
  const bulkMatches = (kk?.rows || []).filter((r) => bulkKeyword.trim() && (r.complaint || "").toLowerCase().includes(bulkKeyword.trim().toLowerCase())).length;
  const applyBulk = () => {
    const kw = bulkKeyword.trim().toLowerCase();
    if (!kw) return;
    setKk((c) => ({ ...c, rows: c.rows.map((r) => ((r.complaint || "").toLowerCase().includes(kw) ? { ...r, applicability: bulkValue } : r)) }));
  };
  // Distinct complaints for the quick-pick datalist.
  const complaintOptions = Array.from(new Set((kk?.rows || []).map((r) => (r.complaint || "").trim()).filter(Boolean)));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/kakotora`, kk);
      setKk(r.data.kakotora);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/kakotora.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Past Defect History (Kakotora).xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Kakotora sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/kakotora`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/kakotora`);
      setKk(r2.data.kakotora);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Kakotora…</div>;
  if (!kk) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const CAT_BG = { external: "bg-red-50", internal: "bg-yellow-50", supplier: "bg-green-50", npd: "bg-sky-50" };

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-kk-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Past Defect History (Kakotora) <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-20</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/kakotora/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-kk-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-kk-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-kk-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-kk-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Updation Frequency</div><input className={field} disabled={!canEdit} value={kk.updation_frequency || ""} onChange={(e) => set("updation_frequency", e.target.value)} /></div>
          <div><div className={lbl}>Updation Responsibility</div><input className={field} disabled={!canEdit} value={kk.updation_responsibility || ""} onChange={(e) => set("updation_responsibility", e.target.value)} /></div>
          <div><div className={lbl}>Part</div><input className={field} disabled={!canEdit} value={kk.part || ""} onChange={(e) => set("part", e.target.value)} /></div>
        </div>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded" data-testid="apqp-kk-bulk">
            <span className="text-[11px] font-semibold text-slate-600">Set Applicable by complaint:</span>
            <input list="kk-complaint-options" value={bulkKeyword} onChange={(e) => setBulkKeyword(e.target.value)}
              placeholder="e.g. Plating, Rust, Burr…" className="border border-slate-300 rounded px-2 py-1 text-xs w-48" data-testid="apqp-kk-bulk-keyword" />
            <datalist id="kk-complaint-options">
              {complaintOptions.map((c, i) => <option key={i} value={c} />)}
            </datalist>
            <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-xs" data-testid="apqp-kk-bulk-value">
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </select>
            <button type="button" onClick={applyBulk} disabled={!bulkKeyword.trim()}
              className="px-2.5 py-1 text-xs rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40" data-testid="apqp-kk-bulk-apply">
              Apply to {bulkMatches} matching
            </button>
            <span className="text-[10px] text-slate-400">Matches every entry whose complaint contains the text. Don’t forget to Save.</span>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Past Defect Entries <span className="text-slate-300 normal-case">(scroll →)</span></div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add entry</button>}
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="text-[10px]">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-1 py-1 w-6">#</th>
                  <th className="px-1 py-1 w-20 text-left">Project</th>
                  <th className="px-1 py-1 min-w-[110px] text-left">Part Name/No</th>
                  <th className="px-1 py-1 min-w-[150px] text-left">Complaint/Incident</th>
                  <th className="px-1 py-1 w-20 text-left">Customer/Supp.</th>
                  <th className="px-1 py-1 min-w-[150px] text-left">Date</th>
                  <th className="px-1 py-1 w-24 text-left">Category</th>
                  <th className="px-1 py-1 min-w-[170px] text-left">Root Cause</th>
                  <th className="px-1 py-1 min-w-[190px] text-left">Lesson Learnt</th>
                  <th className="px-1 py-1 w-20 text-left">Updated By</th>
                  <th className="px-1 py-1 w-20 text-left">Applicable</th>
                  <th className="px-1 py-1 min-w-[80px] text-left">Remarks</th>
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(kk.rows || []).map((row, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top" data-testid={`apqp-kk-row-${i}`}>
                    <td className="px-1 py-1 text-slate-400 text-center">{i + 1}</td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.project || ""} onChange={(e) => setRow(i, "project", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.part_name || ""} onChange={(e) => setRow(i, "part_name", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={field} disabled={!canEdit} value={row.complaint || ""} onChange={(e) => setRow(i, "complaint", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.customer || ""} onChange={(e) => setRow(i, "customer", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input type="date" className={`${field} min-w-[140px]`} disabled={!canEdit} value={row.date || ""} onChange={(e) => setRow(i, "date", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5">
                      <select className={`${field} ${CAT_BG[(row.category || "").toLowerCase()] || ""}`} disabled={!canEdit} value={row.category || ""} onChange={(e) => setRow(i, "category", e.target.value)}>
                        <option value="External">External</option>
                        <option value="Internal">Internal</option>
                        <option value="SUPPLIER">SUPPLIER</option>
                        <option value="NPD">NPD</option>
                      </select>
                    </td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={field} disabled={!canEdit} value={row.root_cause || ""} onChange={(e) => setRow(i, "root_cause", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><textarea rows={2} className={field} disabled={!canEdit} value={row.lesson_learnt || ""} onChange={(e) => setRow(i, "lesson_learnt", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.updated_by || ""} onChange={(e) => setRow(i, "updated_by", e.target.value)} /></td>
                    <td className="px-0.5 py-0.5">
                      <select className={field} disabled={!canEdit} value={row.applicability || ""} onChange={(e) => setRow(i, "applicability", e.target.value)}>
                        <option value="">—</option><option value="YES">YES</option><option value="NO">NO</option>
                      </select>
                    </td>
                    <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                    {canEdit && <td className="text-center"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable New Tooling / Gauge / Test Equipment Requirement form (ENGG-F-03), Phase 2.
function ToolingReqFormSection({ apqpId, task, canEdit, onSaved }) {
  const [tr, setTr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/tooling-req`)
      .then((r) => { if (alive) setTr(r.data.tooling_req); })
      .catch(() => { if (alive) setTr(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setTr((c) => ({ ...c, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/tooling-req`, tr);
      setTr(r.data.tooling_req);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tooling-req.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "New Tooling & Gauge Requirement.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Tooling Requirement sheet and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/tooling-req`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tooling-req`);
      setTr(r2.data.tooling_req);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Tooling Requirement…</div>;
  if (!tr) return null;

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-tr-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> New Tooling / Gauge / Test Equipment <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-03</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/tooling-req/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-tr-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-tr-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-tr-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-tr-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={_SR_LBL}>Part Name</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Part No</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Customer</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={_SR_LBL}>DRG. Rev No / Rev Date</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.drg_rev || ""} onChange={(e) => set("drg_rev", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Date</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Model</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
        </div>

        <SrRowTable label="01 · List of Tools" canEdit={canEdit}
          columns={[["tool_name", "Tool Name", "min-w-[130px]"], ["type", "Type", "w-24"], ["cavity", "Cavity", "w-20"], ["mc_required", "M/c required", "w-24"], ["availability", "Avail.", "w-16"], ["target_date", "Target Date", "w-24"]]}
          items={tr.tools} onChange={(v) => set("tools", v)} blank={{ tool_name: "", type: "Single tool", cavity: "Single", mc_required: "", availability: "-", target_date: "" }} />

        <SrRowTable label="02 · List of Gauges & Testing Equipments" canEdit={canEdit}
          columns={[["name", "Name", "min-w-[130px]"], ["gauge_no", "Gauge/Instrument No.", "w-28"], ["range", "Range", "w-24"], ["lc", "L/C", "w-20"], ["availability", "Avail.", "w-16"], ["target_date", "Target Date", "w-24"]]}
          items={tr.gauges} onChange={(v) => set("gauges", v)} blank={{ name: "", gauge_no: "", range: "", lc: "", availability: "YES", target_date: "" }} />

        <SrRowTable label="03 · Test Requirement (if any)" canEdit={canEdit}
          columns={[["test_name", "Test name", "w-24"], ["specification", "Specification", "min-w-[180px]"], ["external_internal", "External / Internal", "w-28"]]}
          items={tr.tests} onChange={(v) => set("tests", v)} blank={{ test_name: "", specification: "", external_internal: "" }} />

        <div className="grid grid-cols-2 gap-2">
          <div><div className={_SR_LBL}>Prepared By</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={_SR_LBL}>Approved By</div><input className={_SR_FIELD} disabled={!canEdit} value={tr.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Tool/Gauge/Fixture Development Plan form (ENGG-F-14) — Plan/Actual date grid
// across tooling stages, for the Phase-2 "Tool/Gauge/Fixture Development Plan" task.
function ToolDevFormSection({ apqpId, task, canEdit, onSaved }) {
  const [td, setTd] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/tool-dev`)
      .then((r) => { if (alive) { setTd(r.data.tool_dev); setStages(r.data.stages || []); } })
      .catch(() => { if (alive) setTd(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setTd((c) => ({ ...c, [k]: v }));
  const setItem = (i, k, v) => setTd((c) => ({ ...c, items: c.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const setCell = (i, kind, si, v) => setTd((c) => ({
    ...c,
    items: c.items.map((x, j) => {
      if (j !== i) return x;
      const arr = [...(x[kind] || [])];
      while (arr.length < stages.length) arr.push("");
      arr[si] = v;
      return { ...x, [kind]: arr };
    }),
  }));
  const addItem = () => setTd((c) => ({ ...c, items: [...(c.items || []), { description: "", plan: stages.map(() => ""), actual: stages.map(() => ""), remarks: "" }] }));
  const removeItem = (i) => setTd((c) => ({ ...c, items: c.items.filter((_, j) => j !== i) }));

  // Auto-fill the Plan row with dates evenly spread across all stages between From→To.
  const [ranges, setRanges] = useState({}); // { itemIndex: {from, to} }
  const setRange = (i, k, v) => setRanges((m) => ({ ...m, [i]: { ...(m[i] || {}), [k]: v } }));
  const fmtDMY = (dt) => `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  const autoFillPlan = (i, kind) => {
    const rg = ranges[i] || {};
    if (!rg.from || !rg.to || stages.length === 0) { alert("Pick both From and To dates first."); return; }
    const f = new Date(rg.from), t = new Date(rg.to);
    if (isNaN(f) || isNaN(t)) return;
    const n = stages.length;
    const span = t.getTime() - f.getTime();
    const dates = Array.from({ length: n }, (_, k) => fmtDMY(new Date(f.getTime() + (n === 1 ? 0 : Math.round((span * k) / (n - 1))))));
    setTd((c) => ({ ...c, items: c.items.map((x, j) => (j === i ? { ...x, [kind]: dates } : x)) }));
  };

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/tool-dev`, td);
      setTd(r.data.tool_dev);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tool-dev.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Tool Development Plan.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Tool Development Plan and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/tool-dev`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/tool-dev`);
      setTd(r2.data.tool_dev);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Tool Development Plan…</div>;
  if (!td) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const cell = "w-16 border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center disabled:bg-slate-50";

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-td-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Tool Monitoring Sheet <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-14</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/tool-dev/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-td-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-td-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-td-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-td-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Part Name</div><input className={field} disabled={!canEdit} value={td.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No</div><input className={field} disabled={!canEdit} value={td.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Customer</div><input className={field} disabled={!canEdit} value={td.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Rev No</div><input className={field} disabled={!canEdit} value={td.rev_no || ""} onChange={(e) => set("rev_no", e.target.value)} /></div>
          <div><div className={lbl}>Date of Go-Ahead</div><input className={field} disabled={!canEdit} value={td.start_date || ""} placeholder="04-08-2026" onChange={(e) => set("start_date", e.target.value)} /></div>
          <div><div className={lbl}>Customer Event</div><input className={field} disabled={!canEdit} value={td.customer_event || ""} onChange={(e) => set("customer_event", e.target.value)} /></div>
          <div><div className={lbl}>Prepared By</div><input className={field} disabled={!canEdit} value={td.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={field} disabled={!canEdit} value={td.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Tool Monitoring <span className="text-slate-300 normal-case">(Plan = yellow, Actual = green · set a From→To range to auto-fill dates across all stages; scroll →)</span></div>
            {canEdit && <button onClick={addItem} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add item</button>}
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="text-[10px]">
              <thead className="bg-orange-50 text-slate-600">
                <tr>
                  <th className="px-1 py-1 sticky left-0 bg-orange-50 min-w-[140px] text-left">Tool</th>
                  <th className="px-1 py-1 w-14">P/A</th>
                  {stages.map((s, i) => <th key={i} className="px-1 py-1 w-16 font-medium leading-tight">{s}</th>)}
                  <th className="px-1 py-1 min-w-[120px] text-left">Remarks</th>
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(td.items || []).map((it, i) => (
                  <Fragment key={i}>
                    <tr className="border-t border-slate-100" data-testid={`apqp-td-item-${i}`}>
                      <td rowSpan={2} className="px-1 py-1 sticky left-0 bg-white align-top min-w-[170px]">
                        <textarea rows={2} className={field} disabled={!canEdit} value={it.description || ""} onChange={(e) => setItem(i, "description", e.target.value)} />
                        {canEdit && (
                          <div className="mt-1 border border-slate-100 rounded p-1 bg-slate-50/60" title="Fill dates automatically across all stages between From and To">
                            <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Auto-fill dates</div>
                            <div className="flex items-center gap-1">
                              <input type="date" value={(ranges[i] || {}).from || ""} onChange={(e) => setRange(i, "from", e.target.value)} className="border border-slate-200 rounded px-1 py-0.5 text-[9px] w-[86px]" title="From" />
                              <span className="text-[9px] text-slate-400">→</span>
                              <input type="date" value={(ranges[i] || {}).to || ""} onChange={(e) => setRange(i, "to", e.target.value)} className="border border-slate-200 rounded px-1 py-0.5 text-[9px] w-[86px]" title="To" />
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <button type="button" onClick={() => autoFillPlan(i, "plan")} className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-800 hover:bg-yellow-300">Fill Plan</button>
                              <button type="button" onClick={() => autoFillPlan(i, "actual")} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 hover:bg-emerald-300">Fill Actual</button>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-0.5 text-center font-semibold bg-yellow-100">Plan</td>
                      {stages.map((s, si) => (
                        <td key={si} className="px-0.5 py-0.5 bg-yellow-50">
                          <input className={cell} disabled={!canEdit} value={(it.plan || [])[si] || ""} onChange={(e) => setCell(i, "plan", si, e.target.value)} />
                        </td>
                      ))}
                      <td rowSpan={2} className="px-1 py-1 align-top"><textarea rows={2} className={field} disabled={!canEdit} value={it.remarks || ""} onChange={(e) => setItem(i, "remarks", e.target.value)} /></td>
                      {canEdit && <td rowSpan={2} className="text-center align-top"><button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700"><X size={12} /></button></td>}
                    </tr>
                    <tr>
                      <td className="px-1 py-0.5 text-center font-semibold bg-green-100">Actual</td>
                      {stages.map((s, si) => (
                        <td key={si} className="px-0.5 py-0.5 bg-green-50">
                          <input className={cell} disabled={!canEdit} value={(it.actual || [])[si] || ""} onChange={(e) => setCell(i, "actual", si, e.target.value)} />
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable Make or Buy Decision form (Engg-F-49) for the "BOM / Make or Buy" task.
function MakeBuyFormSection({ apqpId, task, canEdit, onSaved }) {
  const [mb, setMb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/make-buy`)
      .then((r) => { if (alive) setMb(r.data.make_buy); })
      .catch(() => { if (alive) setMb(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setMb((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setMb((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setMb((c) => ({ ...c, rows: [...(c.rows || []), { component_name: "", process_description: "", in_house: "", out_source: "", remarks: "" }] }));
  const removeRow = (i) => setMb((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/make-buy`, mb);
      setMb(r.data.make_buy);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/make-buy.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Make or Buy Decision.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Make or Buy Decision and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/make-buy`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/make-buy`);
      setMb(r2.data.make_buy);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Make or Buy Decision…</div>;
  if (!mb) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const YN = ({ value, on }) => (
    <select className={field} disabled={!canEdit} value={value || ""} onChange={(e) => on(e.target.value)}>
      <option value="">—</option>
      <option value="Yes">Yes</option>
      <option value="-">-</option>
    </select>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-mb-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Make or Buy Decision <span className="text-[10px] font-normal text-slate-400 font-mono">Engg-F-49</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/make-buy/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-mb-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-mb-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-mb-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-mb-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Drawing Number</div><input className={field} disabled={!canEdit} value={mb.drawing_number || ""} onChange={(e) => set("drawing_number", e.target.value)} /></div>
          <div><div className={lbl}>Rev. No</div><input className={field} disabled={!canEdit} value={mb.rev_no || ""} onChange={(e) => set("rev_no", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={field} disabled={!canEdit} value={mb.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Customer Name</div><input className={field} disabled={!canEdit} value={mb.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><div className={lbl}>Product</div><input className={field} disabled={!canEdit} value={mb.product || ""} onChange={(e) => set("product", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={mb.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>ECN No</div><input className={field} disabled={!canEdit} value={mb.ecn_no || ""} onChange={(e) => set("ecn_no", e.target.value)} /></div>
          <div><div className={lbl}>ECN Rev No</div><input className={field} disabled={!canEdit} value={mb.ecn_rev_no || ""} onChange={(e) => set("ecn_rev_no", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Components — Make or Buy</div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add component</button>}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="text-left pb-1 w-6">#</th>
                <th className="text-left pb-1">Component Name</th>
                <th className="text-left pb-1">Process Description</th>
                <th className="text-left pb-1 w-20">In-House</th>
                <th className="text-left pb-1 w-20">Out Source</th>
                <th className="text-left pb-1">Remarks</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(mb.rows || []).map((row, i) => (
                <tr key={i} data-testid={`apqp-mb-row-${i}`}>
                  <td className="text-slate-400 pr-1">{i + 1}</td>
                  <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.component_name || ""} onChange={(e) => setRow(i, "component_name", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.process_description || ""} onChange={(e) => setRow(i, "process_description", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><YN value={row.in_house} on={(v) => setRow(i, "in_house", v)} /></td>
                  <td className="px-0.5 py-0.5"><YN value={row.out_source} on={(v) => setRow(i, "out_source", v)} /></td>
                  <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                  {canEdit && <td className="text-center"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable Training Plan (Process Description) form (HR-F-03) for the Training Plan task.
const TP_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function TrainingFormSection({ apqpId, task, canEdit, onSaved }) {
  const [tp, setTp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/training-plan`)
      .then((r) => { if (alive) setTp(r.data.training_plan); })
      .catch(() => { if (alive) setTp(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setTp((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setTp((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setTp((c) => ({ ...c, rows: [...(c.rows || []), { description: "", plan_month: "", actual_month: "", remarks: "" }] }));
  const removeRow = (i) => setTp((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/training-plan`, tp);
      setTp(r.data.training_plan);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/training-plan.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Training Plan (Process Description).xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Training Plan and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/training-plan`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/training-plan`);
      setTp(r2.data.training_plan);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Training Plan…</div>;
  if (!tp) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const MonthSel = ({ value, on, kind }) => (
    <select className={`${field} ${kind === "plan" ? "bg-yellow-50" : "bg-green-50"}`} disabled={!canEdit} value={value || ""} onChange={(e) => on(e.target.value)}>
      <option value="">—</option>
      {TP_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
    </select>
  );

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-tp-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Training Plan <span className="text-[10px] font-normal text-slate-400 font-mono">HR-F-03 · Process Description</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/training-plan/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-tp-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-tp-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-tp-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-tp-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><div className={lbl}>Year</div><input className={field} disabled={!canEdit} value={tp.year || ""} placeholder="2026" onChange={(e) => set("year", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={tp.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Project Name</div><input className={field} disabled={!canEdit} value={tp.project_name || ""} onChange={(e) => set("project_name", e.target.value)} /></div>
          <div><div className={lbl}>Model Name</div><input className={field} disabled={!canEdit} value={tp.model_name || ""} onChange={(e) => set("model_name", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Training Activities <span className="text-slate-300 normal-case">(plan month = yellow, actual = green)</span></div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add activity</button>}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="text-left pb-1 w-6">#</th>
                <th className="text-left pb-1">Process Description</th>
                <th className="text-left pb-1 w-24">Plan Month</th>
                <th className="text-left pb-1 w-24">Actual Month</th>
                <th className="text-left pb-1 w-28">Remarks</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {(tp.rows || []).map((row, i) => (
                <tr key={i} className="align-top" data-testid={`apqp-tp-row-${i}`}>
                  <td className="text-slate-400 pr-1 pt-1.5">{i + 1}</td>
                  <td className="px-0.5 py-0.5"><textarea rows={2} className={field} disabled={!canEdit} value={row.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} /></td>
                  <td className="px-0.5 py-0.5"><MonthSel value={row.plan_month} kind="plan" on={(v) => setRow(i, "plan_month", v)} /></td>
                  <td className="px-0.5 py-0.5"><MonthSel value={row.actual_month} kind="actual" on={(v) => setRow(i, "actual_month", v)} /></td>
                  <td className="px-0.5 py-0.5"><input className={field} disabled={!canEdit} value={row.remarks || ""} onChange={(e) => setRow(i, "remarks", e.target.value)} /></td>
                  {canEdit && <td className="text-center pt-1.5"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared By</div><input className={field} disabled={!canEdit} value={tp.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved By</div><input className={field} disabled={!canEdit} value={tp.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable List of Special Characteristics form (ENGG-F-10) for the LSC task.
function LscFormSection({ apqpId, task, canEdit, onSaved }) {
  const [ls, setLs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/lsc`)
      .then((r) => { if (alive) setLs(r.data.lsc); })
      .catch(() => { if (alive) setLs(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setLs((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) => setLs((c) => ({ ...c, rows: c.rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addRow = () => setLs((c) => ({ ...c, rows: [...(c.rows || []), { characteristic: "", product: "Y", process: "", spec_tolerance: "", marking_symbol: "", pfmea_apn: "", test_equipment: "", msa: "", cpk: "" }] }));
  const removeRow = (i) => setLs((c) => ({ ...c, rows: c.rows.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/lsc`, ls);
      setLs(r.data.lsc);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/lsc.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "List of Special Characteristics.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the List of Special Characteristics and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/lsc`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/lsc`);
      setLs(r2.data.lsc);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Special Characteristics…</div>;
  if (!ls) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const rcol = [
    ["characteristic", "Characteristic", "min-w-[110px]"], ["product", "Prod.", "w-12"], ["process", "Proc.", "w-12"],
    ["spec_tolerance", "Specification Tolerance", "min-w-[110px]"], ["marking_symbol", "Mark", "w-14"],
    ["pfmea_apn", "PFMEA", "w-14"], ["test_equipment", "Test Equip.", "min-w-[90px]"], ["msa", "MSA", "w-12"], ["cpk", "cpk", "w-14"],
  ];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-lsc-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> List of Special Characteristics <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-10</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/lsc/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-lsc-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-lsc-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-lsc-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-lsc-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Part Name</div><input className={field} disabled={!canEdit} value={ls.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={field} disabled={!canEdit} value={ls.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Customer Name</div><input className={field} disabled={!canEdit} value={ls.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><div className={lbl}>DRG. Rev No / Rev Date</div><input className={field} disabled={!canEdit} value={ls.drg_rev || ""} onChange={(e) => set("drg_rev", e.target.value)} /></div>
          <div><div className={lbl}>Date</div><input className={field} disabled={!canEdit} value={ls.date || ""} placeholder="dd.mm.yyyy" onChange={(e) => set("date", e.target.value)} /></div>
          <div><div className={lbl}>Model</div><input className={field} disabled={!canEdit} value={ls.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Special Characteristics</div>
            {canEdit && <button onClick={addRow} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add row</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="text-left pb-1 w-6">#</th>
                  {rcol.map(([k, label]) => <th key={k} className="text-left pb-1 px-0.5">{label}</th>)}
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(ls.rows || []).map((row, i) => (
                  <tr key={i} data-testid={`apqp-lsc-row-${i}`}>
                    <td className="text-slate-400 pr-1">{i + 1}</td>
                    {rcol.map(([k, , w]) => (
                      <td key={k} className="px-0.5 py-0.5">
                        <input className={`${field} ${w}`} disabled={!canEdit} value={row[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                      </td>
                    ))}
                    {canEdit && <td className="text-center"><button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div><div className={lbl}>Remarks (whole list)</div><input className={field} disabled={!canEdit} value={ls.remarks_note || ""} onChange={(e) => set("remarks_note", e.target.value)} /></div>
          <div><div className={lbl}>Prepared by</div><input className={field} disabled={!canEdit} value={ls.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved by</div><input className={field} disabled={!canEdit} value={ls.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Process Flow Chart (PFD) form (ENGG-F-15) for the "Preliminary PFD" task.
const PFD_SYMBOLS = [
  ["operation", "Operation ◯"], ["inspection", "Inspection ■"], ["opn_ins", "Opn.&Ins. ▨"],
  ["decision", "Decision ◆"], ["transportation", "Transport ⇨"], ["storage", "Storage ▽"],
];
function PfdFormSection({ apqpId, task, canEdit, onSaved }) {
  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/pfd`)
      .then((r) => { if (alive) setPf(r.data.pfd); })
      .catch(() => { if (alive) setPf(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const [dragIdx, setDragIdx] = useState(null);
  const set = (k, v) => setPf((c) => ({ ...c, [k]: v }));
  const setStep = (i, k, v) => setPf((c) => ({ ...c, steps: c.steps.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addStep = () => setPf((c) => ({ ...c, steps: [...(c.steps || []), { sn: "", description: "", location: "", incoming_source: "", symbol: "operation", machine: "", desired_outcome: "", process_characteristics: "" }] }));
  const removeStep = (i) => setPf((c) => ({ ...c, steps: c.steps.filter((_, j) => j !== i) }));
  // Drag-to-reorder a step to any position; S.N re-sequences to 10,20,30… after the move.
  const moveStep = (from, to) => setPf((c) => {
    const arr = [...(c.steps || [])];
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return c;
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    const renum = arr.map((s, idx) => ({ ...s, sn: String((idx + 1) * 10) }));
    return { ...c, steps: renum };
  });

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/pfd`, pf);
      setPf(r.data.pfd);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pfd.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Process Flow Chart.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Process Flow Chart and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/pfd`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/pfd`);
      setPf(r2.data.pfd);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Process Flow Chart…</div>;
  if (!pf) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const scol = [
    ["sn", "S.N", "w-12"], ["description", "Description", "min-w-[110px]"], ["location", "Location", "min-w-[90px]"],
    ["incoming_source", "Incoming Source of Variation", "min-w-[150px]"], ["machine", "Machine/Equipment", "min-w-[110px]"],
    ["desired_outcome", "Desired Outcome", "min-w-[130px]"], ["process_characteristics", "Process Characteristics", "min-w-[130px]"],
  ];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-pfd-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Process Flow Chart <span className="text-[10px] font-normal text-slate-400 font-mono">ENGG-F-15</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/pfd/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-pfd-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-pfd-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-pfd-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-pfd-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className={lbl}>Type</div>
            <select className={field} disabled={!canEdit} value={pf.type || "prototype"} onChange={(e) => set("type", e.target.value)}>
              <option value="prototype">Prototype</option>
              <option value="pre_launch">Pre-Launch</option>
              <option value="production">Production</option>
              <option value="safe_launch">Safe Launch</option>
            </select>
          </div>
          <div><div className={lbl}>Customer</div><input className={field} disabled={!canEdit} value={pf.customer || ""} onChange={(e) => set("customer", e.target.value)} /></div>
          <div><div className={lbl}>Part No.</div><input className={field} disabled={!canEdit} value={pf.part_no || ""} onChange={(e) => set("part_no", e.target.value)} /></div>
          <div><div className={lbl}>Part Name</div><input className={field} disabled={!canEdit} value={pf.part_name || ""} onChange={(e) => set("part_name", e.target.value)} /></div>
          <div><div className={lbl}>Dwg. Rev. / Model</div><input className={field} disabled={!canEdit} value={pf.dwg_rev_model || ""} onChange={(e) => set("dwg_rev_model", e.target.value)} /></div>
          <div><div className={lbl}>Key Contact Person</div><input className={field} disabled={!canEdit} value={pf.key_contact || ""} onChange={(e) => set("key_contact", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Core Team</div><input className={field} disabled={!canEdit} value={pf.core_team || ""} onChange={(e) => set("core_team", e.target.value)} /></div>
          <div><div className={lbl}>Supplier Code</div><input className={field} disabled={!canEdit} value={pf.supplier_code || ""} onChange={(e) => set("supplier_code", e.target.value)} /></div>
          <div className="col-span-2"><div className={lbl}>Supplier / Plant Approval</div><input className={field} disabled={!canEdit} value={pf.supplier_plant_approval || ""} onChange={(e) => set("supplier_plant_approval", e.target.value)} /></div>
          <div><div className={lbl}>PFC Number</div><input className={field} disabled={!canEdit} value={pf.pfc_number || ""} onChange={(e) => set("pfc_number", e.target.value)} /></div>
          <div><div className={lbl}>PFC Rev. No. / Date</div><input className={field} disabled={!canEdit} value={pf.pfc_rev_date || ""} onChange={(e) => set("pfc_rev_date", e.target.value)} /></div>
          <div><div className={lbl}>Date (org)</div><input className={field} disabled={!canEdit} value={pf.date_org || ""} onChange={(e) => set("date_org", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>Process Steps</div>
            {canEdit && <button onClick={addStep} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add step</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                <tr>
                  {canEdit && <th className="w-10"></th>}
                  {scol.map(([k, label]) => <th key={k} className="text-left pb-1 px-0.5">{label}</th>)}
                  <th className="text-left pb-1 px-0.5 w-28">Symbol</th>
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(pf.steps || []).map((s, i) => (
                  <tr key={i} className={`align-top ${dragIdx === i ? "bg-orange-50" : ""}`} data-testid={`apqp-pfd-step-${i}`}
                    onDragOver={(e) => { if (dragIdx !== null) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); moveStep(dragIdx, i); setDragIdx(null); }}>
                    {canEdit && (
                      <td className="px-0.5 align-middle">
                        <div className="flex items-center gap-0.5">
                          <span draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => setDragIdx(null)}
                            className="inline-flex cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 select-none" title="Drag to reorder step">
                            <GripVertical size={13} />
                          </span>
                          <div className="flex flex-col leading-none">
                            <button type="button" onClick={() => moveStep(i, i - 1)} disabled={i === 0}
                              className="text-slate-400 hover:text-orange-600 disabled:opacity-20 disabled:hover:text-slate-400" title="Move up"><ChevronUp size={12} /></button>
                            <button type="button" onClick={() => moveStep(i, i + 1)} disabled={i === (pf.steps || []).length - 1}
                              className="text-slate-400 hover:text-orange-600 disabled:opacity-20 disabled:hover:text-slate-400" title="Move down"><ChevronDown size={12} /></button>
                          </div>
                        </div>
                      </td>
                    )}
                    {scol.map(([k, , w]) => (
                      <td key={k} className="px-0.5 py-0.5">
                        <input className={`${field} ${w}`} disabled={!canEdit} value={s[k] || ""} onChange={(e) => setStep(i, k, e.target.value)} />
                      </td>
                    ))}
                    <td className="px-0.5 py-0.5">
                      <select className={field} disabled={!canEdit} value={s.symbol || "operation"} onChange={(e) => setStep(i, "symbol", e.target.value)}>
                        {PFD_SYMBOLS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>
                    </td>
                    {canEdit && <td className="text-center"><button onClick={() => removeStep(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Prepared by</div><input className={field} disabled={!canEdit} value={pf.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved by</div><input className={field} disabled={!canEdit} value={pf.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// Editable Work Load Calculation form (Eagg-F-47) for the "Work Load Management" task.
function WorkloadFormSection({ apqpId, task, canEdit, onSaved }) {
  const [wl, setWl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/apqp/${apqpId}/tasks/${task.id}/workload`)
      .then((r) => { if (alive) setWl(r.data.workload); })
      .catch(() => { if (alive) setWl(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apqpId, task.id]);

  const set = (k, v) => setWl((c) => ({ ...c, [k]: v }));
  const setMember = (i, k, v) => setWl((c) => ({ ...c, members: c.members.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addMember = () => setWl((c) => ({ ...c, members: [...(c.members || []), { name: "", function: "", series_workload: "", simple_projects: "", complex_projects: "", total_projects: "", buffer: "", cumulative: "", spare: "", remarks: "" }] }));
  const removeMember = (i) => setWl((c) => ({ ...c, members: c.members.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/workload`, wl);
      setWl(r.data.workload);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      onSaved && onSaved(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/workload.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Work Load Calculation.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Delete the Work Load Calculation form and its generated Excel? The standard template will be restored.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/workload`);
      onSaved && onSaved(r.data);
      const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/workload`);
      setWl(r2.data.workload);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Work Load form…</div>;
  if (!wl) return null;

  const field = "w-full border border-slate-200 rounded px-1.5 py-1 text-xs disabled:bg-slate-50";
  const lbl = "text-[10px] uppercase tracking-wide text-slate-400 mb-0.5";
  const cols = [
    ["name", "Member", "min-w-[90px]"], ["function", "Function", "w-20"], ["series_workload", "Series", "w-16"],
    ["simple_projects", "Simple", "w-14"], ["complex_projects", "Complex", "w-14"], ["total_projects", "Total", "w-16"],
    ["buffer", "Buffer", "w-16"], ["cumulative", "Cumulative", "w-16"], ["spare", "Spare", "w-16"], ["remarks", "Remarks", "min-w-[80px]"],
  ];

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-wl-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Work Load Calculation <span className="text-[10px] font-normal text-slate-400 font-mono">Eagg-F-47</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Saved · Excel generated</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/workload/view-url`)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-wl-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-wl-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50" data-testid="apqp-wl-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="apqp-wl-save">
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><div className={lbl}>Project Name</div><input className={field} disabled={!canEdit} value={wl.project_name || ""} onChange={(e) => set("project_name", e.target.value)} /></div>
          <div><div className={lbl}>Customer Name</div><input className={field} disabled={!canEdit} value={wl.customer_name || ""} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><div className={lbl}>Prepared by</div><input className={field} disabled={!canEdit} value={wl.prepared_by || ""} onChange={(e) => set("prepared_by", e.target.value)} /></div>
          <div><div className={lbl}>Approved by</div><input className={field} disabled={!canEdit} value={wl.approved_by || ""} onChange={(e) => set("approved_by", e.target.value)} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={lbl}>CFT Project Members — Work Load</div>
            {canEdit && <button onClick={addMember} className="inline-flex items-center gap-1 text-[11px] text-orange-600 hover:underline"><Plus size={11} /> Add member</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="text-left pb-1 w-6">#</th>
                  {cols.map(([k, label]) => <th key={k} className="text-left pb-1 px-0.5">{label}</th>)}
                  {canEdit && <th className="w-6"></th>}
                </tr>
              </thead>
              <tbody>
                {(wl.members || []).map((m, i) => (
                  <tr key={i} data-testid={`apqp-wl-member-${i}`}>
                    <td className="text-slate-400 pr-1">{i + 1}</td>
                    {cols.map(([k, , w]) => (
                      <td key={k} className="px-0.5 py-0.5">
                        <input className={`${field} ${w}`} disabled={!canEdit} value={m[k] || ""} onChange={(e) => setMember(i, k, e.target.value)} />
                      </td>
                    ))}
                    {canEdit && <td className="text-center"><button onClick={() => removeMember(i)} className="text-red-500 hover:text-red-700" title="Remove"><X size={12} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-400 mt-1">The workload legend (function reference %) is included automatically in the generated Excel.</div>
        </div>
      </div>
    </div>
  );
}

// Cost Estimation sheet — auto-generated from the APQP project's linked source costing.
// Not an editable template: it renders the live costing quote. "Edit" opens that costing.
function CostEstimationFormSection({ apqpId, task, canEdit, onSaved }) {
  const [ce, setCe] = useState(null);
  const [attached, setAttached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/cost-estimation`);
      setCe(r.data.cost_estimation); setAttached(r.data.attached);
    } catch { setCe(null); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apqpId, task.id]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/apqp/${apqpId}/tasks/${task.id}/cost-estimation`);
      onSaved && onSaved(r.data);
      setSavedAt(true); setTimeout(() => setSavedAt(false), 2500);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/cost-estimation.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Cost Estimation.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm("Remove the generated Cost Estimation sheet from this deliverable?")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/apqp/${apqpId}/tasks/${task.id}/cost-estimation`);
      onSaved && onSaved(r.data);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="mb-4 text-xs text-slate-400">Loading Cost Estimation…</div>;
  if (!ce) return null;
  const hasSource = ce.has_source;

  return (
    <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="apqp-ce-form">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <ListChecks size={14} /> Cost Estimation <span className="text-[10px] font-normal text-slate-400">(costing sheet)</span>
        </div>
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[11px] text-emerald-600">✓ Generated · attached</span>}
          <button onClick={() => openFormView(`/apqp/${apqpId}/tasks/${task.id}/cost-estimation/view-url`)} disabled={!hasSource}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40" data-testid="apqp-ce-view">
            <Eye size={12} /> View
          </button>
          <button onClick={download} disabled={busy || !hasSource}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40" data-testid="apqp-ce-download">
            <Download size={12} /> Excel
          </button>
          {canEdit && (
            <>
              <button onClick={del} disabled={busy || !attached}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40" data-testid="apqp-ce-delete">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={save} disabled={saving || !hasSource}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-40" data-testid="apqp-ce-save">
                <Save size={12} /> {saving ? "Generating…" : "Generate & Attach"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3">
        {!hasSource ? (
          <div className="text-xs text-amber-600">No linked costing found for this APQP project — the Cost Estimation sheet is generated from the source costing.</div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-600 space-y-0.5">
              <div>Auto-generated from the linked costing:</div>
              <div className="font-mono text-slate-800">{ce.npd_number} · {ce.part_name}</div>
              <div>Grand Total / pc: <span className="font-semibold text-slate-800">₹{Number(ce.grand_total || 0).toFixed(2)}</span></div>
              {!ce.approved_for_export && <div className="text-[11px] text-amber-600">Note: source costing is not yet approved for export.</div>}
            </div>
            {canEdit && (
              <a href={`/npd/${ce.source_npd_id}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="apqp-ce-edit">
                <Eye size={12} /> Edit costing ↗
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskStatusPopup({ apqpId, task, canEdit, onClose, onLocalPatch, onTaskRefresh }) {
  const [tab, setTab] = useState("deliverables");
  const [remarks, setRemarks] = useState(task.remarks || "");
  const [status, setStatus] = useState(task.status || "incomplete");
  const [deliverables, setDeliverables] = useState(task.deliverables || []);
  const [others, setOthers] = useState(task.other_documents || []);
  const [otherBusy, setOtherBusy] = useState(false);
  const [comments, setComments] = useState(task.comments || []);
  const [chat, setChat] = useState(task.chat || []);
  const [msg, setMsg] = useState("");
  const [busyIdx, setBusyIdx] = useState(null);
  const fileRefs = useRef({});
  const isCft = /cft and awareness/i.test(task.task_name || "");
  const isFeasibility = /team feasibility/i.test(task.task_name || "");
  const isDrawingReview = /drawing review/i.test(task.task_name || "");
  const isResourcePlanning = /risk assessment/i.test(task.task_name || "");
  const isCostEstimation = /cost estimation/i.test(task.task_name || "");
  const isWorkload = /work load management/i.test(task.task_name || "");
  // Both "Preliminary PFD" (Phase 1) and "PFD" (Phase 3) use the Process Flow Chart form.
  const isPfd = /preliminary pfd/i.test(task.task_name || "") || (task.task_name || "").trim().toLowerCase() === "pfd";
  const isLsc = /listing of special characteristics/i.test(task.task_name || "");
  const isTraining = /training plan regarding/i.test(task.task_name || "");
  const isMakeBuy = /make or buy/i.test(task.task_name || "");
  const isSpecReview = /review meeting with customer/i.test(task.task_name || "");
  const isToolDev = /development plan/i.test(task.task_name || "") && /tool|gauge|fixture/i.test(task.task_name || "");
  const isToolingReq = /new capital/i.test(task.task_name || "") || (/testing equipment/i.test(task.task_name || "") && /requirement/i.test(task.task_name || ""));
  const isKakotora = /kakotora/i.test(task.task_name || "") || /past defect/i.test(task.task_name || "");
  const isPdca = /lesson learn/i.test(task.task_name || "") || /feedback analysis/i.test(task.task_name || "") || /corrective action/i.test(task.task_name || "") || /\bpdca\b/i.test(task.task_name || "");
  const isPfmea = /pfmea/i.test(task.task_name || "");
  const isControlPlan = /control plan/i.test(task.task_name || "") || /\bcp\b/i.test(task.task_name || "");
  const isOperationStandard = /operation standard/i.test(task.task_name || "") || /operator process instruction/i.test(task.task_name || "");
  const isIppLogbook = /prepare sample/i.test(task.task_name || "") || /production parts logbook/i.test(task.task_name || "");
  const isTrialValidation = /tool validation/i.test(task.task_name || "") || /trial validation/i.test(task.task_name || "");
  const isInspectionReport = /inspection report/i.test(task.task_name || "");
  const isPackagingSpec = /packaging spec/i.test(task.task_name || "");
  const isSpcPlan = /spc plan/i.test(task.task_name || "") || /statistical process control/i.test(task.task_name || "");
  const isMsaPlan = /msa plan/i.test(task.task_name || "") || /measurement system analysis/i.test(task.task_name || "");
  const isMsaStudy = /msa study/i.test(task.task_name || "") || /gauge r\s?&\s?r/i.test(task.task_name || "") || /gr\s?&\s?r/i.test(task.task_name || "");
  const isProductionReport = /production trial run/i.test(task.task_name || "") || /production report/i.test(task.task_name || "") || /pilot lot/i.test(task.task_name || "");
  const isPpap = /\bppap\b/i.test(task.task_name || "");
  const isPsw = /\bpsw\b/i.test(task.task_name || "") || /part submission warrant/i.test(task.task_name || "");
  const isSpcPpk = /ppk/i.test(task.task_name || "");
  const isProcessValidation = /product & process validation/i.test(task.task_name || "") || /process validation/i.test(task.task_name || "") || /process qualification/i.test(task.task_name || "");
  const isTgrTgw = /tgr/i.test(task.task_name || "") || /tgw/i.test(task.task_name || "") || /things gone/i.test(task.task_name || "");
  const isSummarySignoff = /summary sign off/i.test(task.task_name || "") || /summary and sign/i.test(task.task_name || "") || /apqp planning summary/i.test(task.task_name || "");

  // When an auto-form saves/deletes, reflect the new deliverable files + status in the popup.
  function onCftSaved(res) {
    if (res?.deliverables) setDeliverables(res.deliverables);
    if (res?.status) setStatus(res.status);
    onTaskRefresh && onTaskRefresh();
  }

  function setDelivRemark(index, val) {
    setDeliverables((prev) => prev.map((d, i) => (i === index ? { ...d, remarks: val } : d)));
  }

  // Upload files ONE AT A TIME — the backend appends to the deliverable's file list, so
  // concurrent uploads would race and lose files. Sequential keeps every file.
  async function uploadFor(index, files) {
    const list = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
    if (!list.length) return;
    setBusyIdx(index);
    try {
      let last = null;
      let newStatus = null;
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/apqp/${apqpId}/tasks/${task.id}/deliverables/${index}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        last = r.data.deliverable;
        newStatus = r.data.status || newStatus;
      }
      if (last) setDeliverables((prev) => prev.map((d, i) => (i === index ? last : d)));
      // Auto-complete: reflect the flipped status in the popup dropdown immediately.
      if (newStatus) setStatus(newStatus);
      // Refresh the page tables (progress + status column).
      onTaskRefresh && onTaskRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusyIdx(null);
    }
  }

  async function viewFile(index, fileName, f) {
    const name = (fileName || "").toLowerCase();
    const isOffice = /\.(xlsx|xls|docx|doc|pptx|ppt)$/.test(name);
    const q = f != null ? `?f=${f}` : "";
    // Open the tab synchronously so it isn't blocked as a popup, then redirect it.
    const win = window.open("", "_blank");
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/deliverables/${index}/view-url${q}`);
      const url = r.data?.url;
      if (!url) throw new Error("No view URL");
      const target = isOffice
        ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`
        : url;
      if (win) win.location = target; else window.open(target, "_blank");
    } catch (e) {
      if (win) win.close();
      // Fallback: stream the file (older behavior) if presign/view-url is unavailable.
      try {
        const r2 = await api.get(`/apqp/${apqpId}/tasks/${task.id}/deliverables/${index}/file${q}`, { responseType: "blob" });
        window.open(URL.createObjectURL(r2.data), "_blank");
      } catch (e2) {
        alert(e2.response?.data?.detail || e.response?.data?.detail || e.message);
      }
    }
  }

  async function removeFile(index, f) {
    if (!window.confirm("Remove this file?")) return;
    setBusyIdx(index);
    try {
      const q = f != null ? `?f=${f}` : "";
      await api.delete(`/apqp/${apqpId}/tasks/${task.id}/deliverables/${index}/file${q}`);
      // Refetch the task to get the updated files list.
      const r = await api.get(`/apqp/${apqpId}`);
      const fresh = (r.data?.phases || []).flatMap((p) => p.tasks).find((x) => x.id === task.id);
      if (fresh) setDeliverables(fresh.deliverables || []);
      onTaskRefresh && onTaskRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusyIdx(null);
    }
  }

  const [dlAllBusy, setDlAllBusy] = useState(false);
  async function downloadAll() {
    setDlAllBusy(true);
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/deliverables/download-all`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${task.task_name || "task"} - documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.status === 404 ? "No documents uploaded yet." : (e.response?.data?.detail || e.message));
    } finally {
      setDlAllBusy(false);
    }
  }

  async function downloadFile(index, name, f) {
    try {
      const q = f != null ? `?f=${f}` : "";
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/deliverables/${index}/file${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "APQP_Timing_Plan.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  const [regenBusy, setRegenBusy] = useState(false);
  async function regenerateTimingPlan() {
    if (!window.confirm("Rebuild the Timing Plan from the app data? This discards any hand-edited Excel you uploaded.")) return;
    setRegenBusy(true);
    try {
      // Force a fresh rebuild + re-attach; clears any user-edited copy.
      await api.post(`/apqp/${apqpId}/timing-plan/regenerate`);
      onTaskRefresh && onTaskRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setRegenBusy(false);
    }
  }

  async function uploadOther(file) {
    if (!file) return;
    setOtherBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/apqp/${apqpId}/tasks/${task.id}/other-documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOthers(r.data.other_documents || []);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setOtherBusy(false);
    }
  }

  async function viewOther(index) {
    try {
      const r = await api.get(`/apqp/${apqpId}/tasks/${task.id}/other-documents/${index}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  async function sendMessage(kind) {
    if (!msg.trim()) return;
    try {
      const r = await api.post(`/apqp/${apqpId}/tasks/${task.id}/message`, { text: msg.trim(), kind });
      if (r.data.field === "chat") setChat((p) => [...p, r.data.entry]);
      else setComments((p) => [...p, r.data.entry]);
      setMsg("");
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  }

  // Persist per-deliverable remarks to the backend (shared by Save and Apply).
  async function persistDeliverableRemarks() {
    const map = {};
    (deliverables || []).forEach((d, i) => { map[String(i)] = d.remarks || ""; });
    await api.put(`/apqp/${apqpId}/tasks/${task.id}/deliverable-remarks`, { remarks: map });
  }

  const [savingPopup, setSavingPopup] = useState(false);
  // Wide-grid forms (MSA / SPC / monthly plans / big tables) open maximized so every
  // month/stage column is visible without cramping. Still toggleable.
  const _isWideForm = /\bmsa\b|\bspc\b|control plan|pre-?launch cp|pfmea|\bpfd\b|process flow|inspection report|tool.*(development|monitoring)|development plan|production report|pilot lot|process capability|process validation/i.test(task?.task_name || "");
  const [maximized, setMaximized] = useState(_isWideForm);   // stretch popup to near full-screen on demand
  // Save — persist status, task remarks and per-deliverable remarks WITHOUT closing the
  // dialog and WITHOUT requiring all documents to be uploaded.
  async function saveWithoutClosing() {
    setSavingPopup(true);
    try {
      await persistDeliverableRemarks();
      await api.put(`/apqp/${apqpId}/tasks/${task.id}`, { status, remarks });
      onLocalPatch({ status, remarks });
      onTaskRefresh && onTaskRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSavingPopup(false);
    }
  }

  async function applyStatusRemarks() {
    // E — block Completed while any mandatory (non-system) deliverable has no file.
    if (status === "complete") {
      const missing = (deliverables || []).filter(
        (d) => d.mandatory && !d.system_generated && !d.file_path
      );
      if (missing.length) {
        const names = missing
          .map((d) => `${d.name}${d.document_no ? ` (${d.document_no})` : ""}`)
          .join(", ");
        alert(`Cannot mark Completed — mandatory document(s) not attached: ${names}`);
        return;
      }
    }
    try { await persistDeliverableRemarks(); } catch (e) { /* non-fatal */ }
    // Push into the page's dirty draft; page Save persists via task PUT.
    onLocalPatch({ status, remarks });
    onClose();
  }

  const TABS = [
    { key: "deliverables", label: "Deliverables", icon: ListChecks },
    { key: "comment", label: "Comment", icon: MessageSquare },
    { key: "activity", label: "Activity", icon: History },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-3">
      <div className={`bg-white rounded-lg shadow-xl w-full flex flex-col ${maximized ? "max-w-[97vw] h-[95vh] max-h-[95vh]" : "max-w-5xl max-h-[90vh]"}`} data-testid="apqp-status-popup">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800 text-sm">{task.task_name}</h3>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setMaximized((m) => !m)} title={maximized ? "Restore size" : "Stretch / maximize"}
              className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100" data-testid="apqp-popup-maximize">
              {maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
          </div>
        </div>

        {/* Status + remarks bar */}
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <label className="text-xs text-slate-500">Status</label>
          <select disabled={!canEdit} value={status} onChange={(e) => setStatus(e.target.value)}
            className="border border-slate-200 rounded px-2 py-1 text-xs bg-white" data-testid="apqp-popup-status">
            <option value="incomplete">Incomplete</option>
            <option value="complete">Completed</option>
            <option value="reject">Reject</option>
          </select>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t font-semibold ${tab === t.key ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                data-testid={`apqp-popup-tab-${t.key}`}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === "deliverables" && (
            <>
            {isCft && <CftFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isFeasibility && <FeasibilityFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isDrawingReview && <DrawingReviewFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isResourcePlanning && <RiskAssessmentFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isResourcePlanning && <ResourcePlanningFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isCostEstimation && <CostEstimationFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isWorkload && <WorkloadFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPfd && <PfdFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isLsc && <LscFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isTraining && <TrainingFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isMakeBuy && <MakeBuyFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isSpecReview && <SpecReviewFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isToolDev && <ToolDevFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isToolingReq && <ToolingReqFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isKakotora && <KakotoraFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPdca && <PdcaFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPfmea && <PfmeaFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isControlPlan && <ControlPlanFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isOperationStandard && <OperationStandardFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isIppLogbook && <IppLogbookFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isTrialValidation && <TrialValidationFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isInspectionReport && <InspectionReportFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPackagingSpec && <PackagingSpecFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isSpcPlan && <SpcPlanFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isMsaPlan && <MsaPlanFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isMsaStudy && <MsaStudyFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isProductionReport && <PilotLotFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isProductionReport && <ProductionReportFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPpap && <PpapFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isPsw && <PswFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isSpcPpk && <SpcPpkFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isSpcPpk && <ProcessCapabilityFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isProcessValidation && <ProcessValidationFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isTgrTgw && <TgrTgwFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            {isSummarySignoff && <SummarySignoffFormSection apqpId={apqpId} task={task} canEdit={canEdit} onSaved={onCftSaved} />}
            <div className="flex justify-end mb-2">
              <button onClick={downloadAll} disabled={dlAllBusy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid="apqp-download-all">
                <Download size={13} /> {dlAllBusy ? "Preparing…" : "Download All"}
              </button>
            </div>
            <table className="w-full text-sm" data-testid="apqp-deliverables-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-2 py-1.5 w-10">S No</th>
                  <th className="text-left px-2 py-1.5">Document Name</th>
                  <th className="text-left px-2 py-1.5 w-24">Document No.</th>
                  <th className="text-left px-2 py-1.5 w-44">Attach File(s)</th>
                  <th className="text-left px-2 py-1.5 w-20">View</th>
                  <th className="text-left px-2 py-1.5 w-40">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deliverables.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-xs text-slate-400">No deliverables.</td></tr>}
                {deliverables.map((d, i) => {
                  const files = (d.files && d.files.length) ? d.files : (d.file_path ? [{ file_path: d.file_path, file_name: d.file_name }] : []);
                  const multi = !d.system_generated;
                  return (
                  <tr key={i} data-testid={`apqp-deliverable-${i}`} className={d.system_generated ? "bg-emerald-50/40 align-top" : "align-top"}>
                    <td className="px-2 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-2 py-2 text-slate-800">
                      {d.name} {d.mandatory && !d.system_generated && <span className="text-red-500">*</span>}
                      {d.system_generated && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase tracking-wide">Auto</span>}
                    </td>
                    <td className="px-2 py-2 text-xs font-mono text-slate-600" data-testid={`apqp-deliverable-docno-${i}`}>{d.document_no || "—"}</td>
                    <td className="px-2 py-2">
                      {canEdit ? (
                        <>
                          <input type="file" className="hidden" ref={(el) => (fileRefs.current[i] = el)}
                            multiple={multi}
                            onChange={(e) => { uploadFor(i, Array.from(e.target.files || [])); e.target.value = ""; }}
                            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.xlsx,.docx,.doc" data-testid={`apqp-deliverable-file-${i}`} />
                          <button onClick={() => fileRefs.current[i]?.click()} disabled={busyIdx === i}
                            title={d.system_generated ? "Upload a Timing Plan you edited in Excel — it becomes the saved version" : "You can select multiple files"}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50" data-testid={`apqp-deliverable-upload-${i}`}>
                            <Upload size={12} /> {busyIdx === i ? "Uploading…" : (d.system_generated ? "Upload edited" : (files.length ? "Add file(s)" : "Upload"))}
                          </button>
                          {d.system_generated && d.user_edited && (
                            <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide">Edited</span>
                          )}
                          {files.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {files.map((f, fi) => (
                                <div key={fi} className="text-[10px] text-slate-500 flex items-center gap-1">
                                  <Paperclip size={9} /><span className="truncate max-w-[120px]" title={f.file_name}>{f.file_name}</span>
                                  {!d.system_generated && (
                                    <button onClick={() => removeFile(i, d.files ? fi : undefined)} className="text-slate-400 hover:text-red-600" title="Remove"><X size={10} /></button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      {files.length > 0 ? (
                        <div className="space-y-1">
                          {files.map((f, fi) => (
                            <div key={fi} className="flex items-center gap-2">
                              <button onClick={() => viewFile(i, f.file_name, d.files ? fi : undefined)} className="inline-flex items-center gap-1 text-xs text-sky-600 hover:underline" data-testid={`apqp-deliverable-view-${i}`}>
                                <Eye size={12} /> View
                              </button>
                              <button onClick={() => downloadFile(i, f.file_name, d.files ? fi : undefined)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline" data-testid={`apqp-deliverable-download-${i}`}>
                                <Download size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2">
                      {d.system_generated && canEdit ? (
                        <button onClick={regenerateTimingPlan} disabled={regenBusy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50" data-testid={`apqp-deliverable-regenerate-${i}`}>
                          <RotateCcw size={12} /> {regenBusy ? "…" : "Regenerate"}
                        </button>
                      ) : (
                        <input disabled={!canEdit} value={d.remarks || ""} onChange={(e) => setDelivRemark(i, e.target.value)}
                          placeholder="Remarks…" className="w-full border border-slate-200 rounded px-2 py-1 text-xs disabled:bg-slate-50" data-testid={`apqp-deliverable-remarks-${i}`} />
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}

          {tab === "deliverables" && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="apqp-other-docs">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-600">Other Documents <span className="text-slate-400 font-normal">(optional)</span></div>
                {canEdit && (
                  <label className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 cursor-pointer" data-testid="apqp-other-upload">
                    <Upload size={12} /> {otherBusy ? "Uploading…" : "Add File"}
                    <input type="file" className="hidden" disabled={otherBusy}
                      onChange={(e) => { uploadOther(e.target.files?.[0]); e.target.value = ""; }}
                      accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.xlsx,.docx,.doc" />
                  </label>
                )}
              </div>
              {others.length === 0 && <div className="text-xs text-slate-400 italic">No additional documents.</div>}
              <ul className="space-y-1">
                {others.map((o, i) => (
                  <li key={o.id || i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`apqp-other-${i}`}>
                    <span className="flex items-center gap-1.5 text-slate-700"><Paperclip size={11} />{o.file_name}</span>
                    <button onClick={() => viewOther(i)} className="inline-flex items-center gap-1 text-sky-600 hover:underline"><Download size={11} /> View</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(tab === "comment" || tab === "chat") && (
            <div>
              <div className="space-y-2 mb-3 max-h-56 overflow-y-auto">
                {(tab === "comment" ? comments : chat).length === 0 && <div className="text-xs text-slate-400 italic">No {tab === "comment" ? "comments" : "messages"} yet.</div>}
                {(tab === "comment" ? comments : chat).map((c) => (
                  <div key={c.id} className="bg-slate-50 rounded px-3 py-2 text-xs">
                    <div className="text-slate-700">{c.text}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{c.by_name || "User"} · {(c.at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={`Add a ${tab}…`}
                    className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="apqp-message-input" />
                  <button onClick={() => sendMessage(tab === "chat" ? "chat" : "comment")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800" data-testid="apqp-message-send">
                    <Send size={12} /> Post
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-2 text-xs">
              {(task.activity || []).length === 0 && <div className="text-slate-400 italic">No activity.</div>}
              {(task.activity || []).map((a, i) => (
                <div key={i} className="flex gap-2 text-slate-600">
                  <span className="text-slate-400">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
                  <span>{a.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded">Cancel</button>
          {canEdit && (
            <button onClick={saveWithoutClosing} disabled={savingPopup}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm border border-orange-600 text-orange-700 rounded hover:bg-orange-50 disabled:opacity-50" data-testid="apqp-popup-save">
              <Save size={14} /> {savingPopup ? "Saving…" : "Save"}
            </button>
          )}
          {canEdit && (
            <button onClick={applyStatusRemarks} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700" data-testid="apqp-popup-apply">
              <Save size={14} /> Apply
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
